import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Package,
  MapPin,
  User,
  Phone,
  Mail,
  Truck,
  Calculator,
  CreditCard,
  AlertCircle,
  CheckCircle,
  Plus,
  Minus,
  Info,
  AlertTriangle,
  LocateFixed,
  Loader,
  FileText,
  ArrowLeft,
  ArrowRight,
  RefreshCw,
  ExternalLink,
  Gift,
  Search,
  Shield,
  Boxes
} from 'lucide-react';
// import { Button, Card, Input, Badge, Alert, Modal, LocationSearch } from '../components/ui';
import { Button, Card, Input, Badge, Alert, Modal } from '../components/ui';
import MapLocation from '../components/ui/MapLocation';

import { cn } from '../utils/cn';
import { useAuth } from '../contexts/AuthContext';
import { relayAPI, carrierAPI, modepaiementAPI, expeditionAPI, promoCodeAPI, TrajetAPI } from '../services/api';
import { calculateTariff, CITY_ZONE_MAPPING } from '../services/tariffService';
import { COUNTRY_ZONE_MAPPING, getCitiesByCountry } from '../services/VilleCommune';

// import printService from '../services/printService';
import toast from 'react-hot-toast';
import PaymentRedirect from '../components/PaymentRedirect';
import ErrorModal from '../components/ErrorModal';

// 🏙️ Listes des villes et communes
const ABIDJAN_COMMUNES = [
  'Abobo', 'Adjamé', 'Attecoubé', 'Cocody', 'Koumassi', 
  'Marcory', 'Plateau', 'Port-Bouët', 'Treichville', 'Yopougon'
];

// 🏙️ Communes d'Abidjan à exclure des villes interurbaines
const ABIDJAN_COMMUNES_TO_EXCLUDE = [
  'Abobo', 'Adjamé', 'Attecoubé', 'Cocody', 'Koumassi', 
  'Marcory', 'Plateau', 'Port-Bouët', 'Treichville', 'Yopougon'
];

// 🌍 Codes pays pour les numéros de téléphone
const COUNTRY_CODES = [
  { code: '+225', country: 'Côte d\'Ivoire', flag: '🇨🇮' },
  { code: '+226', country: 'Burkina Faso', flag: '🇧🇫' },
  { code: '+223', country: 'Mali', flag: '🇲🇱' },
  { code: '+221', country: 'Sénégal', flag: '🇸🇳' },
  { code: '+229', country: 'Bénin', flag: '🇧🇯' },
  { code: '+228', country: 'Togo', flag: '🇹🇬' },
  { code: '+233', country: 'Ghana', flag: '🇬🇭' },
  { code: '+234', country: 'Nigéria', flag: '🇳🇬' },
  { code: '+237', country: 'Cameroun', flag: '🇨🇲' },
  { code: '+212', country: 'Maroc', flag: '🇲🇦' },
  { code: '+213', country: 'Algérie', flag: '🇩🇿' },
  { code: '+216', country: 'Tunisie', flag: '🇹🇳' },
  { code: '+33', country: 'France', flag: '🇫🇷' },
  { code: '+1', country: 'États-Unis', flag: '🇺🇸' },
  { code: '+44', country: 'Royaume-Uni', flag: '🇬🇧' }
];

// 🏙️ Toutes les villes (incluant les communes d'Abidjan)
const ALL_CITIES_WITH_COMMUNES = Object.keys(CITY_ZONE_MAPPING).sort();

// 🏙️ Villes interurbaines (excluant les communes d'Abidjan)
const ALL_CITIES = ALL_CITIES_WITH_COMMUNES.filter(city => 
  !ABIDJAN_COMMUNES_TO_EXCLUDE.includes(city)
);

// 🌍 Liste des pays pour service sous-régionale
const ALL_COUNTRIES = Object.keys(COUNTRY_ZONE_MAPPING).sort();

// 🏳️ Mapping des pays avec leurs drapeaux
const COUNTRY_FLAGS = {
  'Ghana': '🇬🇭',
  'Burkina Faso': '🇧🇫',
  'Mali': '🇲🇱',
  'Guinée': '🇬🇳',
  'Liberia': '🇱🇷',
  'Togo': '🇹🇬',
  'Bénin': '🇧🇯',
  'Niger': '🇳🇪',
  'Sierra Leone': '🇸🇱',
  'Guinée-Bissau': '🇬🇼',
  'Nigeria': '🇳🇬',
  'Sénégal': '🇸🇳',
  'Gambie': '🇬🇲',
  'Cap-Vert': '🇨🇻',
  'Côte d\'Ivoire': '🇨🇮',
  'Cameroun': '🇨🇲',
  'Gabon': '🇬🇦',
  'Congo': '🇨🇬',
  'République Démocratique du Congo': '🇨🇩',
  'République Centrafricaine': '🇨🇫',
  'Tchad': '🇹🇩',
  'Guinée Équatoriale': '🇬🇶',
  'São Tomé-et-Príncipe': '🇸🇹',
  'Maroc': '🇲🇦',
  'Algérie': '🇩🇿',
  'Tunisie': '🇹🇳',
  'Égypte': '🇪🇬',
  'Libye': '🇱🇾',
  'Mauritanie': '🇲🇷',
  'Soudan': '🇸🇩',
  'Éthiopie': '🇪🇹',
  'Kenya': '🇰🇪',
  'Tanzanie': '🇹🇿',
  'Afrique du Sud': '🇿🇦',
  'Angola': '🇦🇴',
  'Namibie': '🇳🇦'
};

// 🔍 Composant de recherche personnalisé pour les villes/communes
const SearchableSelect = ({ value, onChange, options, placeholder, label, showFlags = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const dropdownRef = React.useRef(null);

  // Fonction pour normaliser les chaînes (insensible à la casse et aux accents)
  const normalizeSearchString = (str) => {
    if (!str) return '';
    return str
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();
  };

  useEffect(() => {
    const normalizedSearch = normalizeSearchString(searchTerm);
    const filtered = options.filter(option =>
      normalizeSearchString(option).includes(normalizedSearch)
    );
    setFilteredOptions(filtered);
  }, [searchTerm, options]);

  // Fermer le dropdown quand on clique à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSelect = (option) => {
    onChange(option);
    setIsOpen(false);
    setSearchTerm('');
  };

  const displayValue = value || placeholder;

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        onClick={() => setIsOpen(!isOpen)}
        className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-ksl-red focus:border-transparent cursor-pointer"
      >
        <div className="flex justify-between items-center">
          <span className={value ? 'text-gray-900 dark:text-gray-100 flex items-center' : 'text-gray-500 dark:text-gray-400'}>
            {value ? (
              <>
                {showFlags && COUNTRY_FLAGS[value] && (
                  <span className="mr-2 text-lg">{COUNTRY_FLAGS[value]}</span>
                )}
                {value}
              </>
            ) : (
              displayValue
            )}
          </span>
          <svg className={`w-4 h-4 transform transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-dark-bg-secondary border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-hidden">
          <div className="p-2 border-b border-gray-200 dark:border-gray-600">
            <input
              type="text"
              placeholder={`Rechercher ${label.toLowerCase()}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-ksl-red"
              autoFocus
            />
          </div>
          <div className="max-h-48 overflow-y-auto">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <div
                  key={option}
                  onClick={() => handleSelect(option)}
                  className="px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer text-gray-900 dark:text-gray-100 flex items-center"
                >
                  {showFlags && COUNTRY_FLAGS[option] && (
                    <span className="mr-2 text-lg">{COUNTRY_FLAGS[option]}</span>
                  )}
                  {option}
                </div>
              ))
            ) : (
              <div className="px-3 py-2 text-gray-500 dark:text-gray-400">
                Aucun résultat trouvé
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Types de véhicules avec spécifications poids et volume exactes
const VEHICLE_TYPES = [
  { id: 1, name: '🏍️ XS (Moto)', code: 'xs_moto', icon: '🏍️', min_weight: 0, max_weight: 2, tariff: 100, description: '🏍️ XS (Moto) - 0-2kg' },
  { id: 2, name: 'S (Tricycle)', code: 's_tricycle', icon: '🛺', min_weight: 2, max_weight: 5, tariff: 90, description: 'S (Tricycle) - 2-5kg' },
  { id: 3, name: 'SM (Petit véhicule utilitaire)', code: 'sm_petit_vehicule', icon: '🚗', min_weight: 5, max_weight: 10, tariff: 80, description: 'SM (Petit véhicule utilitaire) - 5-10kg' },
  { id: 4, name: '🚐 M (Fourgon compact)', code: 'm_fourgon_compact', icon: '🚐', min_weight: 10, max_weight: 30, tariff: 70, description: '🚐 M (Fourgon compact) - 10-30kg' },
  { id: 5, name: '🚐 L (Fourgon moyen)', code: 'l_fourgon_moyen', icon: '🚐', min_weight: 30, max_weight: 100, tariff: 60, description: '🚐 L (Fourgon moyen) - 30-100kg' },
  { id: 6, name: 'XL (Grand fourgon)', code: 'xl_grand_fourgon', icon: '🚛', min_weight: 100, max_weight: 300, tariff: 40, description: 'XL (Grand fourgon) - 100-300kg' },
  { id: 7, name: '2XL (Petit camion)', code: '2xl_petit_camion', icon: '🚚', min_weight: 300, max_weight: 500, tariff: 80, description: '2XL (Petit camion) - 300-500kg' },
  { id: 8, name: '🚛 3XL (Camion léger)', code: '3xl_camion_leger', icon: '🚛', min_weight: 500, max_weight: 1000, tariff: 55, description: '🚛 3XL (Camion léger) - 500-1000kg' },
  { id: 9, name: 'Petit fourgon', code: 'petit_fourgon', icon: '🚐', min_weight: 1000, max_weight: 1500, tariff: 50, description: 'Petit fourgon - 1000-1500kg' },
  { id: 10, name: 'Grand fourgon', code: 'grand_fourgon', icon: '🚛', min_weight: 1500, max_weight: 3000, tariff: 20, description: 'Grand fourgon - 1500-3000kg' },
  { id: 11, name: 'Petit camion', code: 'petit_camion', icon: '🚚', min_weight: 3000, max_weight: 5000, tariff: 40, description: 'Petit camion - 3000-5000kg' },
  { id: 12, name: '🚛 Camion léger', code: 'camion_leger', icon: '🚛', min_weight: 5000, max_weight: 10000, tariff: 35, description: '🚛 Camion léger - 5000-10000kg' },
  { id: 13, name: '🚛 Camion moyen', code: 'camion_moyen', icon: '🚛', min_weight: 10000, max_weight: 20000, tariff: 30, description: '🚛 Camion moyen - 10000-20000kg' },
  { id: 14, name: '🚛 Camion complet', code: 'camion_complet', icon: '🚛', min_weight: 20000, max_weight: 40000, tariff: 25, description: '🚛 Camion complet - 20000-40000kg' }
];

// 🌍 Composant pour la sélection du code pays
const CountryCodeSelect = ({ value, onChange, className = "" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const selectedCountry = COUNTRY_CODES.find(country => country.code === value) || COUNTRY_CODES[0];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSelect = (country) => {
    onChange(country.code);
    setIsOpen(false);
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-l-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
      >
        <span className="text-lg">{selectedCountry.flag}</span>
        <span className="text-sm font-medium">{selectedCountry.code}</span>
        <ArrowLeft className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-90' : '-rotate-90'}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-64 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
          {COUNTRY_CODES.map((country) => (
            <button
              key={country.code}
              type="button"
              onClick={() => handleSelect(country)}
              className={`w-full flex items-center space-x-3 px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors ${
                value === country.code ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300' : 'text-gray-900 dark:text-white'
              }`}
            >
              <span className="text-lg">{country.flag}</span>
              <span className="font-medium">{country.code}</span>
              <span className="text-sm text-gray-600 dark:text-gray-400">{country.country}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const Expedier = () => {
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  
  // 🔝 Référence pour le scroll automatique
  const topElementRef = useRef(null);
  
  // 🔝 Fonction utilitaire pour le scroll vers le haut
  const scrollToTop = () => {
    setTimeout(() => {
      // Méthode 1: Scroll vers l'élément référencé
      if (topElementRef.current) {
        topElementRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      // Méthode 2: Scroll vers le haut de la fenêtre
      window.scrollTo({ top: 0, behavior: 'smooth' });
      // Méthode 3: Alternative pour les navigateurs qui ne supportent pas smooth
      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
    }, 100);
  };
  
  // Récupérer les données d'estimation depuis localStorage
  const getEstimationData = () => {
    try {
      const storedData = localStorage.getItem('ksl_expedition_data');
      if (storedData) {
        const estimationData = JSON.parse(storedData);
        console.log('✅ Données d\'estimation récupérées:', estimationData);
        return estimationData;
      }
      console.log('ℹ️ Aucune donnée d\'estimation trouvée');
      return null;
    } catch (error) {
      console.error('❌ Erreur récupération données d\'estimation:', error);
      return null;
    }
  };
  
  // 🚚 FONCTION UTILITAIRE : Récupérer les données utilisateur depuis localStorage
  const getUserData = React.useCallback(() => {
    try {
      const storedUser = localStorage.getItem('ksl_user');
      if (storedUser) {
        const userData = JSON.parse(storedUser);
        console.log('✅ Données utilisateur récupérées depuis localStorage:', userData);
        return userData;
      }
      console.log('⚠️ Fallback sur user context');
      return user;
    } catch (error) {
      console.error('❌ Erreur récupération données utilisateur:', error);
      return user;
    }
  }, [user]);
  
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [quoteData, setQuoteData] = useState(null);
  const [isCalculatingQuote, setIsCalculatingQuote] = useState(false);

  // États pour les données dynamiques
  const [relayPoints, setRelayPoints] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [isLoadingRelayPoints, setIsLoadingRelayPoints] = useState(false);
  const [isLoadingCarriers, setIsLoadingCarriers] = useState(false);
  const [relayPointsError, setRelayPointsError] = useState(null);
  const [carriersError, setCarriersError] = useState(null);

  // 💳 NOUVEAUX ÉTATS POUR LA GESTION DES MODES DE PAIEMENT
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [isLoadingPaymentMethods, setIsLoadingPaymentMethods] = useState(false);
  const [paymentMethodsError, setPaymentMethodsError] = useState(null);
  const [paymentStep, setPaymentStep] = useState('idle'); // 'idle' | 'initializing' | 'success' | 'error'
  const paymentCheckIntervalRef = React.useRef(null);
  const [paymentPhoneNumber, setPaymentPhoneNumber] = useState('');
  const [paymentCountryCode, setPaymentCountryCode] = useState('+225');
  // Champs invités (utilisateur non authentifié)
  const [guestFirstName, setGuestFirstName] = useState('');
  const [guestLastName, setGuestLastName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  // État local pour gérer la saisie du nom complet sans perte d'espaces
  const [customerFullNameInput, setCustomerFullNameInput] = useState('');
  const [recipientFullNameInput, setRecipientFullNameInput] = useState('');
  const [showPaymentRedirect, setShowPaymentRedirect] = useState(false);
  const [paymentFinalStatus, setPaymentFinalStatus] = useState(null); // 'success' | 'pending' | 'failed'
  const [paymentCreatedExpedition, setPaymentCreatedExpedition] = useState(null);
  const [useMyLocation, setUseMyLocation] = useState(false);
  const [useMyLocationDelivery, setUseMyLocationDelivery] = useState(false);
  const [selectedRelayPoint, setSelectedRelayPoint] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalData, setErrorModalData] = useState({ title: '', message: '', type: 'error' });
  
  // État pour gérer l'onglet actif des types de service
  const [activeServiceTab, setActiveServiceTab] = useState('abidjan'); // 'abidjan' ou 'interurbain'
  
  // Authentification simple
  const isAuthenticated = !!user;
  
  // États pour les formats de colis
  const [selectedPackageFormat, setSelectedPackageFormat] = useState('');
  const [showCustomDimensions, setShowCustomDimensions] = useState(false);
  const [evaError, setEvaError] = useState(null);

  // États pour les codes pays des téléphones
  const [customerPhoneCountryCode, setCustomerPhoneCountryCode] = useState('+225');
  const [recipientPhoneCountryCode, setRecipientPhoneCountryCode] = useState('+225');
  
  const [customerPhoneNumberLocal, setCustomerPhoneNumberLocal] = useState('');
  const [pickupPhoneNumberLocal, setPickupPhoneNumberLocal] = useState('');

  // 🏷️ Liste complète des types de service
  const serviceTypes = [
    { key: 'flash', name: 'Chrono', delais: '2h à 4h', description: 'Livraison ultra-rapide, priorité maximale' },
    { key: 'express', name: 'Express', delais: '24H - 0-1 JOUR', description: 'Livraison rapide, priorité maximale' },
    { key: 'standard', name: 'Standard', delais: '24h à 48h', description: 'Livraison normale, bon rapport qualité/prix' },
    { key: 'economique', name: 'Économique', delais: '48h à 78h', description: 'Livraison économique, délai plus long' },
    { key: 'interurbaine', name: 'Interubaine', delais: '86H - 2-4 JOURS', description: 'Livraison interurbaine, couverture étendue' },
    { key: 'sous_regionale', name: 'Sous-régionale', delais: '3-7 JOURS', description: 'Livraison internationale, 38 pays africains' },
  ];

  // 🔍 États pour le champ select moderne de type de service
  const [isServiceTypeSectionExpanded, setIsServiceTypeSectionExpanded] = useState(false);
  const [searchServiceType, setSearchServiceType] = useState('');

  // 🔍 Filtrage des types de service selon l'onglet actif et la recherche
  const filteredServiceTypes = useMemo(() => {
    // Filtrer selon l'onglet actif
    let servicesToFilter = [];
    if (activeServiceTab === 'abidjan') {
      // Services pour partout à Abidjan : flash, express, standard, économique
      servicesToFilter = serviceTypes.filter(s => ['flash', 'express', 'standard', 'economique'].includes(s.key));
    } else if (activeServiceTab === 'interurbain') {
      // Services interurbains : interurbaine et sous_regionale
      servicesToFilter = serviceTypes.filter(s => ['interurbaine', 'sous_regionale'].includes(s.key));
    }
    
    // Appliquer la recherche si nécessaire
    if (!searchServiceType.trim()) {
      return servicesToFilter;
    }
    const searchTerm = searchServiceType.toLowerCase();
    return servicesToFilter.filter(service => 
      service.name.toLowerCase().includes(searchTerm) ||
      service.description.toLowerCase().includes(searchTerm) ||
      service.delais.toLowerCase().includes(searchTerm)
    );
  }, [searchServiceType, activeServiceTab, serviceTypes]);

  // 🔍 États pour le champ select moderne de type de colis
  const [isPackageTypeSectionExpanded, setIsPackageTypeSectionExpanded] = useState(false);
  const [searchPackageType, setSearchPackageType] = useState('');

  // 🔍 Filtrage des types de colis selon la recherche
  const filteredPackageTypes = useMemo(() => {
    const packageTypesList = [
      { value: 'standard', label: 'Standard', variant: 'default', emoji: '📦' },
      { value: 'froid', label: 'Froid', variant: 'info', emoji: '❄️' },
      { value: 'securise', label: 'Sécurisé', variant: 'error', emoji: '🔒' },
      { value: 'gros_volume', label: 'Gros volume', variant: 'success', emoji: '📏' },
      { value: 'doc_admin', label: 'Doc administratif', variant: 'info', emoji: '📋' }
    ];
    if (!searchPackageType.trim()) {
      return packageTypesList;
    }
    const searchTerm = searchPackageType.toLowerCase();
    return packageTypesList.filter(type => 
      type.label.toLowerCase().includes(searchTerm) ||
      type.value.toLowerCase().includes(searchTerm)
    );
  }, [searchPackageType]);

  // 🔄 Fonction pour séparer le nom complet en prénom et nom
  const splitFullName = (fullName) => {
    if (!fullName) {
      return { first_name: '', last_name: '' };
    }
    // Ne pas utiliser trim() ici pour préserver les espaces en cours de saisie
    const trimmed = fullName.trim();
    if (!trimmed) {
      return { first_name: '', last_name: '' };
    }
    const parts = trimmed.split(/\s+/);
    if (parts.length === 1) {
      return { first_name: parts[0], last_name: '' };
    }
    // Le premier mot est le prénom, le reste est le nom
    const first_name = parts[0];
    const last_name = parts.slice(1).join(' ');
    return { first_name, last_name };
  };

  // 🔄 Fonction pour combiner prénom et nom en nom complet
  const getFullName = () => {
    // Si on a une valeur en cours de saisie dans l'état local, l'utiliser pour préserver les espaces
    if (customerFullNameInput !== '') {
      return customerFullNameInput;
    }
    const first = expeditionData.customer_first_name || '';
    const last = expeditionData.customer_last_name || '';
    // Préserver un espace entre prénom et nom même si l'un est vide
    if (!first && !last) return '';
    if (!first) return last;
    if (!last) return first;
    return `${first} ${last}`;
  };

  // 🔄 Fonction pour combiner prénom et nom du destinataire en nom complet
  const getRecipientFullName = () => {
    // Si on a une valeur en cours de saisie dans l'état local, l'utiliser pour préserver les espaces
    if (recipientFullNameInput !== '') {
      return recipientFullNameInput;
    }
    const first = expeditionData.recipient_first_name || '';
    const last = expeditionData.recipient_last_name || '';
    // Préserver un espace entre prénom et nom même si l'un est vide
    if (!first && !last) return '';
    if (!first) return last;
    if (!last) return first;
    return `${first} ${last}`;
  };

  // 🔄 Fonction pour séparer le nom complet du destinataire en prénom et nom
  const splitRecipientFullName = (fullName) => {
    if (!fullName) {
      return { first_name: '', last_name: '' };
    }
    // Ne pas utiliser trim() ici pour préserver les espaces en cours de saisie
    const trimmed = fullName.trim();
    if (!trimmed) {
      return { first_name: '', last_name: '' };
    }
    const parts = trimmed.split(/\s+/);
    if (parts.length === 1) {
      return { first_name: parts[0], last_name: '' };
    }
    // Le premier mot est le prénom, le reste est le nom
    const first_name = parts[0];
    const last_name = parts.slice(1).join(' ');
    return { first_name, last_name };
  };
  
  // 🔒 Refs pour éviter les boucles infinies lors de l'initialisation des codes pays
  const hasInitializedCustomerCode = React.useRef(false);
  const hasInitializedRecipientCode = React.useRef(false);

  
  // États pour la sélection commune/ville
  const [selectedCommune] = useState('');
  const [selectedDepartureCity] = useState('');

  // 🎁 États pour la gestion des codes promo
  const [promoCode, setPromoCode] = useState('');
  const [promoCodeData, setPromoCodeData] = useState(null);
  const [isValidatingPromo, setIsValidatingPromo] = useState(false);
  const [promoError, setPromoError] = useState(null);
  const [originalPrice, setOriginalPrice] = useState(0);
  const [finalPrice, setFinalPrice] = useState(0);
  const [discountAmount, setDiscountAmount] = useState(0);

  // Formats de colis standardisés avec calculs corrects
  const packageFormats = [
    // 🏍️ MOTO
    {
      id: 'xs',
      name: '🏍️ XS (Moto)',
      category: 'moto',
      weightMin: 0,
      weightMax: 2,
      weightFixed: 1, // (2-0)/2 + 0 = 1
      volumeMin: 0,
      volumeMax: 10000,
      volumeFixed: 5000, // (10000-0)/2 + 0 = 5000
      dimensions: { length: 20, width: 20, height: 25 },
      examples: 'Documents, accessoires, petits appareils',
      description: 'Volume: ≤10 000 cm³ - Poids: 0-2 kg'
    },
    {
      id: 's',
      name: '🏍️ S (Moto)',
      category: 'moto',
      weightMin: 2,
      weightMax: 5,
      weightFixed: 3.5, // (5-2)/2 + 2 = 3.5
      volumeMin: 0,
      volumeMax: 20000,
      volumeFixed: 10000, // (20000-0)/2 + 0 = 10000
      dimensions: { length: 40, width: 25, height: 20 },
      examples: 'Vêtements, petite électronique',
      description: 'Volume: ≤20 000 cm³ - Poids: 2-5 kg'
    },
    {
      id: 'sm',
      name: '🏍️ SM (Moto)',
      category: 'moto',
      weightMin: 5,
      weightMax: 10,
      weightFixed: 7.5, // (10-5)/2 + 5 = 7.5
      volumeMin: 0,
      volumeMax: 27000,
      volumeFixed: 13500, // (27000-0)/2 + 0 = 13500
      dimensions: { length: 30, width: 30, height: 30 },
      examples: 'Livres, chaussures, articles de sport',
      description: 'Volume: ≤27 000 cm³ - Poids: 5-10 kg'
    },
    {
      id: 'm',
      name: '🏍️ M (Moto)',
      category: 'moto',
      weightMin: 10,
      weightMax: 30,
      weightFixed: 20, // (30-10)/2 + 10 = 20
      volumeMin: 0,
      volumeMax: 80000,
      volumeFixed: 40000, // (80000-0)/2 + 0 = 40000
      dimensions: { length: 40, width: 40, height: 50 },
      examples: 'Électroménager, équipements',
      description: 'Volume: ≤80 000 cm³ - Poids: 10-30 kg'
    },
    {
      id: 'moto_hors_gabarit',
      name: '🏍️ Taille hors Gabarit (Moto)',
      category: 'moto',
      isCustom: true, // Marqueur pour la saisie manuelle
      weightMin: 0,
      weightMax: 0,
      weightFixed: 0,
      volumeMin: 0,
      volumeMax: 0,
      volumeFixed: 0,
      dimensions: { length: 0, width: 0, height: 0 },
      examples: 'Dimensions personnalisées',
      description: 'Saisie manuelle requise'
    },
    
    // 🚐 FOURGON OU VOITURE
    {
      id: 'l',
      name: '🚐 L (Petit fourgon ou Voiture)',
      category: 'fourgon',
      weightMin: 30,
      weightMax: 100,
      weightFixed: 65, // (100-30)/2 + 30 = 65
      volumeMin: 0,
      volumeMax: 300000,
      volumeFixed: 150000, // (300000-0)/2 + 0 = 150000
      dimensions: { length: 60, width: 50, height: 100 },
      examples: 'Meubles plats, équipements moyens',
      description: 'Volume: ≤300 000 cm³ - Poids: 30-100 kg'
    },
    {
      id: 'xl',
      name: '🚐 XL (Petit Fourgon)',
      category: 'fourgon',
      weightMin: 100,
      weightMax: 300,
      weightFixed: 200, // (300-100)/2 + 100 = 200
      volumeMin: 0,
      volumeMax: 800000,
      volumeFixed: 400000, // (800000-0)/2 + 0 = 400000
      dimensions: { length: 100, width: 80, height: 100 },
      examples: 'Meubles, électroménager, équipements',
      description: 'Volume: ≤800 000 cm³ - Poids: 100-300 kg'
    },
    {
      id: '2xl',
      name: '🚐 2XL (Petit Fourgon)',
      category: 'fourgon',
      weightMin: 300,
      weightMax: 500,
      weightFixed: 400, // (500-300)/2 + 300 = 400
      volumeMin: 0,
      volumeMax: 960000,
      volumeFixed: 480000, // (960000-0)/2 + 0 = 480000
      dimensions: { length: 120, width: 80, height: 100 },
      examples: 'Meubles volumineux, équipements lourds',
      description: 'Volume: ≤960 000 cm³ - Poids: 300-500 kg'
    },
    {
      id: '3xl',
      name: '🚐 3XL (Moyen Fourgon)',
      category: 'fourgon',
      weightMin: 500,
      weightMax: 1000,
      weightFixed: 750, // (1000-500)/2 + 500 = 750
      volumeMin: 0,
      volumeMax: 1920000,
      volumeFixed: 960000, // (1920000-0)/2 + 0 = 960000
      dimensions: { length: 120, width: 80, height: 200 },
      examples: 'Gros meubles, équipements industriels',
      description: 'Volume: ≤1 920 000 cm³ - Poids: 500-1000 kg'
    },
    {
      id: 'grand_fourgon',
      name: '🚐 Grand fourgon',
      category: 'fourgon',
      weightMin: 1000,
      weightMax: 1500,
      weightFixed: 1250, // (1500-1000)/2 + 1000 = 1250
      volumeMin: 0,
      volumeMax: 2880000,
      volumeFixed: 1440000, // (2880000-0)/2 + 0 = 1440000
      dimensions: { length: 240, width: 120, height: 100 },
      examples: 'Très gros meubles, équipements volumineux',
      description: 'Volume: ≤2 880 000 cm³ - Poids: 1000-1500 kg'
    },
    {
      id: 'fourgon_hors_gabarit',
      name: '🚐 Taille hors Gabarit (Fourgon)',
      category: 'fourgon',
      isCustom: true, // Marqueur pour la saisie manuelle
      weightMin: 0,
      weightMax: 0,
      weightFixed: 0,
      volumeMin: 0,
      volumeMax: 0,
      volumeFixed: 0,
      dimensions: { length: 0, width: 0, height: 0 },
      examples: 'Dimensions personnalisées',
      description: 'Saisie manuelle requise'
    },
    
    // 🚛 CAMION
    {
      id: 'petit_camion',
      name: '🚛 Petit Camion',
      category: 'camion',
      weightMin: 1500,
      weightMax: 3000,
      weightFixed: 2250, // (3000-1500)/2 + 1500 = 2250
      volumeMin: 0,
      volumeMax: 4800000,
      volumeFixed: 2400000, // (4800000-0)/2 + 0 = 2400000
      dimensions: { length: 300, width: 160, height: 100 },
      examples: 'Équipements industriels, marchandises lourdes',
      description: 'Volume: ≤4 800 000 cm³ - Poids: 1500-3000 kg'
    },
    {
      id: 'camion_leger',
      name: '🚛 Camion léger',
      category: 'camion',
      weightMin: 3000,
      weightMax: 5000,
      weightFixed: 4000, // (5000-3000)/2 + 3000 = 4000
      volumeMin: 0,
      volumeMax: 9600000,
      volumeFixed: 4800000, // (9600000-0)/2 + 0 = 4800000
      dimensions: { length: 300, width: 200, height: 160 },
      examples: 'Machines, équipements lourds',
      description: 'Volume: ≤9 600 000 cm³ - Poids: 3000-5000 kg'
    },
    {
      id: 'camion_moyen',
      name: '🚛 Camion moyen',
      category: 'camion',
      weightMin: 5000,
      weightMax: 10000,
      weightFixed: 7500, // (10000-5000)/2 + 5000 = 7500
      volumeMin: 0,
      volumeMax: 30000000,
      volumeFixed: 15000000, // (30000000-0)/2 + 0 = 15000000
      dimensions: { length: 600, width: 250, height: 200 },
      examples: 'Machines industrielles, équipements très lourds',
      description: 'Volume: ≤30 000 000 cm³ - Poids: 5000-10000 kg'
    },
    {
      id: 'camion_grand',
      name: '🚛 Camion grand',
      category: 'camion',
      weightMin: 10000,
      weightMax: 20000,
      weightFixed: 15000, // (20000-10000)/2 + 10000 = 15000
      volumeMin: 0,
      volumeMax: 60000000,
      volumeFixed: 30000000, // (60000000-0)/2 + 0 = 30000000
      dimensions: { length: 1200, width: 250, height: 200 },
      examples: 'Machines très lourdes, équipements industriels',
      description: 'Volume: ≤60 000 000 cm³ - Poids: 10000-20000 kg'
    },
    {
      id: 'camion_remorque',
      name: '🚛 Camion REMORQUE',
      category: 'camion',
      weightMin: 20000,
      weightMax: 40000,
      weightFixed: 30000, // (40000-20000)/2 + 20000 = 30000
      volumeMin: 0,
      volumeMax: 90000000,
      volumeFixed: 45000000, // (90000000-0)/2 + 0 = 45000000
      dimensions: { length: 1360, width: 250, height: 260 },
      examples: 'Machines très lourdes, équipements industriels',
      description: 'Volume: ≤90 000 000 cm³ - Poids: 20000-40000 kg'
    },
    {
      id: 'camion_hors_gabarit',
      name: '🚛 Taille hors Gabarit (Camion)',
      category: 'camion',
      isCustom: true, // Marqueur pour la saisie manuelle
      weightMin: 0,
      weightMax: 0,
      weightFixed: 0,
      volumeMin: 0,
      volumeMax: 0,
      volumeFixed: 0,
      dimensions: { length: 0, width: 0, height: 0 },
      examples: 'Dimensions personnalisées',
      description: 'Saisie manuelle requise'
    }
  ];

  // Anciens formats de colis (COMMENTÉS - À SUPPRIMER PLUS TARD)
  /*
  const oldPackageFormats = [
    {
      id: 'xs',
      name: 'XS',
      volume: 10000, // cm³
      weight: 2, // kg
      dimensions: { length: 25, width: 20, height: 20 },
      examples: 'Documents, petits accessoires, Sacs',
      description: '0 – 2 kg ≤ 10 000 cm³ (20 x 20 x 25 cm)'
    },
    {
      id: 's',
      name: 'S',
      volume: 20000, // cm³
      weight: 5, // kg
      dimensions: { length: 40, width: 25, height: 20 },
      examples: 'Vêtements, livres',
      description: '2 – 5 kg ≤ 20 000 cm³ (40 x 25 x 20 cm)'
    },
    {
      id: 'sm',
      name: 'SM',
      volume: 27000, // cm³
      weight: 10, // kg
      dimensions: { length: 30, width: 30, height: 30 },
      examples: 'Vêtements, Bagages',
      description: '5 – 10 kg ≤ 27 000 cm³ (30 x 30 x 30 cm)'
    },
    {
      id: 'm',
      name: 'M',
      volume: 80000, // cm³
      weight: 30, // kg
      dimensions: { length: 50, width: 40, height: 40 },
      examples: 'Électronique, petit électroménager',
      description: '10 – 30 kg ≤ 80 000 cm³ (40 x 40 x 50 cm)'
    },
    {
      id: 'l',
      name: 'L',
      volume: 300000, // cm³
      weight: 100, // kg
      dimensions: { length: 100, width: 50, height: 60 },
      examples: 'Gros appareils ménagers portables',
      description: '30 – 100 kg ≤ 300 000 cm³ (60 x 50 x 100 cm)'
    },
    {
      id: 'xl',
      name: 'XL',
      volume: 800000, // cm³
      weight: 300, // kg
      dimensions: { length: 100, width: 80, height: 100 },
      examples: 'Petits meubles, équipements lourds, Gros appareils',
      description: '100 – 300 kg ≤ 800 000 cm³ (100 x 80 x 100 cm)'
    },
    {
      id: '2xl',
      name: '2XL',
      volume: 960000, // cm³
      weight: 500, // kg
      dimensions: { length: 100, width: 80, height: 120 },
      examples: 'Lot de Produits alimentaires, cartons regroupés',
      description: '300 – 500 kg ≤ 960 000 cm³ (120 x 80 x 100 cm)'
    },
    {
      id: '3xl',
      name: '3XL',
      volume: 1920000, // cm³
      weight: 1000, // kg
      dimensions: { length: 200, width: 80, height: 120 },
      examples: 'Lot de Boissons, charges moyennes',
      description: '500 – 1 000 kg ≤ 1 920 000 cm³ (120 x 80 x 200 cm)'
    },
    {
      id: 'petit_fourgon',
      name: '🚐 Petit Fourgon',
      volume: 2880000, // cm³
      weight: 1500, // kg
      dimensions: { length: 100, width: 120, height: 240 },
      examples: 'Lot de Produits divers (standard transport)',
      description: '1 000 – 1 500 kg ≤ 2 880 000 cm³ (240 x 120 x 100 cm)'
    },
    {
      id: 'grand_fourgon',
      name: '🚐 Grand Fourgon',
      volume: 4800000, // cm³
      weight: 3000, // kg
      dimensions: { length: 100, width: 160, height: 300 },
      examples: 'Matériaux, pièces métalliques',
      description: '1 500 – 3 000 kg ≤ 4 800 000 cm³ (300 x 160 x 100 cm)'
    },
    {
      id: 'petit_camion',
      name: '🚛 Petit Camion',
      volume: 9600000, // cm³
      weight: 5000, // kg
      dimensions: { length: 160, width: 200, height: 300 },
      examples: 'Lots de materiaux',
      description: '3 000 – 5 000 kg ≤ 9 600 000 cm³ (300 x 200 x 160 cm)'
    },
    {
      id: 'camion_leger',
      name: '🚛 Camion léger',
      volume: 30000000, // cm³
      weight: 10000, // kg
      dimensions: { length: 200, width: 250, height: 600 },
      examples: 'Matériaux, lots de palettes',
      description: '5 000 – 10 000 kg ≤ 30 000 000 cm³ (600 x 250 x 200 cm)'
    },
    {
      id: 'camion_moyen',
      name: '🚛 Camion moyen',
      volume: 60000000, // cm³
      weight: 20000, // kg
      dimensions: { length: 200, width: 250, height: 1200 },
      examples: 'Gros lots industriels, palettes',
      description: '10 000 – 20 000 kg ≤ 60 000 000 cm³ (1 200 x 250 x 200 cm)'
    },
    {
      id: 'camion_complet',
      name: '🚛 Camion complet (FTL)',
      volume: 90000000, // cm³
      weight: 40000, // kg
      dimensions: { length: 260, width: 250, height: 1360 },
      examples: 'Chargement complet – palettes',
      description: '20 000 – 40 000 kg ≤ 90 000 000 cm³ (1 360 x 250 x 260 cm)'
    },
    {
      id: 'personnalise',
      name: 'Format personnalisé',
      volume: 0, // cm³
      weight: 0, // kg
      dimensions: { length: 0, width: 0, height: 0 },
      examples: 'Format personnalisé - saisie manuelle requise',
      description: 'Format personnalisé - saisie manuelle requise'
    }
  ];
  */

  // 🚚 Mapping Format de colis → Code véhicule pour la tarification
  const PACKAGE_FORMAT_TO_VEHICLE_CODE = {
    // 🏍️ MOTO
    'xs': 'xs_moto',
    's': 's_tricycle',
    'sm': 'sm_petit_vehicule',
    'm': 'm_fourgon_compact',
    'moto_hors_gabarit': 'camion_complet', // Fallback pour hors gabarit moto
    
    // 🚐 FOURGON OU VOITURE
    'l': 'l_fourgon_moyen',
    'xl': 'xl_grand_fourgon',
    '2xl': '2xl_petit_camion',
    '3xl': '3xl_camion_leger',
    'grand_fourgon': 'grand_fourgon',
    'fourgon_hors_gabarit': 'camion_complet', // Fallback pour hors gabarit fourgon
    
    // 🚛 CAMION
    'petit_camion': 'petit_camion',
    'camion_leger': 'camion_leger',
    'camion_moyen': 'camion_moyen',
    'camion_grand': 'camion_complet',
    'camion_remorque': 'camion_complet',
    'camion_hors_gabarit': 'camion_complet' // Fallback pour hors gabarit camion
  };

  // Fonction pour obtenir le nom simplifié d'un format
  const getSimplifiedFormatName = (format) => {
    if (!format) return '';
    
    // Pour l'onglet "Car" (interurbaine/sous-régionale), afficher des noms simplifiés
    if (currentItem.selectedCategory === 'car') {
      // Extraire la taille avec une regex plus précise
      const sizeMatch = format.name.match(/(XS|SM|S|M|L|XL|2XL|3XL|Grand fourgon|Taille hors Gabarit)/);
      if (sizeMatch) {
        const size = sizeMatch[1];
        if (size === 'Taille hors Gabarit') {
          // Pour l'onglet "Car", afficher simplement "Colis hors Gabarit" pour tous
          return '📦 Colis hors Gabarit';
        }
        return `📦 Colis ${size}`;
      }
    }
    return format.name;
  };

  // Fonction pour gérer le changement de format de colis
  const handlePackageFormatChange = (formatId) => {
    setSelectedPackageFormat(formatId);
    const selectedFormat = packageFormats.find(format => format.id === formatId);
    
    if (selectedFormat) {
      // Activer la saisie manuelle pour les formats Camion
      if (selectedFormat.category === 'camion') {
      setShowCustomDimensions(true);
      setCurrentItem(prev => ({
        ...prev,
          packageFormat: formatId,
        weight: '',
        length: '',
        width: '',
        height: ''
      }));
    } else {
        // Utiliser les poids et dimensions fixes calculés selon la formule
      setShowCustomDimensions(false);
        setCurrentItem(prev => ({
          ...prev,
          packageFormat: formatId,
          weight: selectedFormat.weightFixed || '',
          length: selectedFormat.dimensions.length || '',
          width: selectedFormat.dimensions.width || '',
          height: selectedFormat.dimensions.height || ''
        }));
      }
      
      // 🚚 NOUVEAU : Stocker le code véhicule directement pour la tarification
      const vehicleCode = PACKAGE_FORMAT_TO_VEHICLE_CODE[formatId];
      if (vehicleCode) {
        console.log('🚚 Format de colis sélectionné:', formatId, '→ Code véhicule:', vehicleCode);
        updateExpeditionData('vehicleCode', vehicleCode); // Stocker le code string directement
      }
    }
  };

  // 🚚 NOUVEAU : Récupérer le transporteur assigné depuis localStorage (optimisé avec useMemo)
  const assignedCarrier = React.useMemo(() => {
    const userData = getUserData();
    const transporteur = userData?.transporteur;
    console.log('utilisateur', userData);
    if (transporteur) {
      console.log('✅ Transporteur trouvé:', transporteur);
    } else {
      console.log('ℹ️ Aucun transporteur assigné');
    }
    
    return transporteur;
  }, [getUserData]);

  // État pour l'expédition au format EVA (CORRIGÉ)
  const [expeditionData, setExpeditionData] = useState({
    // Informations de base EVA
    ride_type_id: 2, // Par défaut : livraison
    vehicle_type_id: 2, // Motorcycle par défaut
    zone_id: 10, // Côte d'Ivoire selon l'exemple EVA
    business_id: null,

    // Mode d'expédition et service
    mode_expedition: 'home_delivery',
    delais_livraison: '',
    type_service: '',
    // Adresses avec coordonnées GPS (MAINTENANT VISIBLES)
    from_latitude: '',
    from_longitude: '',
    from_address: '',
    departure_city: '', // Ville ou commune de départ
    departure_country: 'Côte d\'Ivoire', // Pays de départ par défaut : Côte d'Ivoire
    to_latitude: '',
    to_longitude: '',
    to_address: '',
    destination_city: '', // Ville ou commune de destination
    destination_country: '', // Pays de destination
    
    // Informations client
    customer_first_name: '',
    customer_last_name: '',
    customer_phone_number: '+225',
    customer_email: '',
    
    // Informations destinataire
    recipient_first_name: '',
    recipient_last_name: '',
    recipient_phone: '',
    
    // Informations point de retrait
    pickup_company_name: '',
    pickup_phone_number: '+225',
    pickup_email: '',
    pickup_note: '',
    order_note: '',
    
    // Articles à livrer
    pickup_items: [],
    
    // Options de livraison
    tip_amount: 0,
    total_price: 0,
    
    // 🚀 NOUVEAU : Type de service
    
    // 🛡️ NOUVEAU : Assurance
    isInsured: false, // Assurance cochable par l'utilisateur
    declared_value: '', // Valeur déclarée du colis
    
    // 🤝 NOUVEAU : Service de manutention
    isManutentionRequested: false, // Service d'aide à la manutention
    
    // Point relais (optionnel)
    use_relay_point: false,
    selected_relay_point_id: null,
    order_number: null,
    onsite_timestamp: null,
    packageType: 'standard',
    shippingMode: 'home_delivery',
    selectedRelayPoint: null,
    selectedCarrier: '1', // Transporteur ID 1 par défaut
  });

  // 🗺️ État pour stocker les trajets depuis l'API
  const [trajets, setTrajets] = useState([]);
  const [trajetsLoading, setTrajetsLoading] = useState(false);

  // 🔝 Scroll automatique vers le haut lors du changement d'étape
  useEffect(() => {
    scrollToTop();
  }, [currentStep]);

  // 🗺️ Charger les trajets depuis l'API
  const loadTrajets = async () => {
    setTrajetsLoading(true);
    try {
      const response = await TrajetAPI.getTrajets();
      const allTrajets = response.data || [];
      // Filtrer uniquement les trajets actifs
      const activeTrajets = allTrajets.filter(t => {
        const isActive = t.is_active !== undefined ? (t.is_active === true || t.is_active === 1) : true;
        return isActive;
      });
      setTrajets(activeTrajets);
    } catch (error) {
      console.error('Erreur lors du chargement des trajets:', error);
      setTrajets([]);
    } finally {
      setTrajetsLoading(false);
    }
  };

  // 🗺️ Charger les trajets au montage du composant
  useEffect(() => {
    loadTrajets();
  }, []);

  // 🗺️ Fonction pour extraire les options uniques depuis les trajets
  const getTrajetOptions = useMemo(() => {
    return (type, filterByCountry = null) => {
      // Filtrer les trajets par type
      let filteredTrajets = trajets.filter(t => t.type === type);
      
      // Si un filtre par pays est demandé, filtrer aussi par pays
      if (filterByCountry && type === 'ville') {
        // Pour les villes, vérifier si le trajet correspond au pays
        // On suppose que les trajets de type "ville" ont un champ country ou qu'on peut le déduire
        // Pour l'instant, on retourne toutes les villes si le filtre n'est pas applicable
        // Cette logique peut être ajustée selon la structure réelle des données
      }
      
      // Extraire les valeurs uniques de depart et destination
      const options = new Set();
      filteredTrajets.forEach(t => {
        if (t.depart) options.add(t.depart);
        if (t.destination) options.add(t.destination);
      });
      
      return Array.from(options).sort();
    };
  }, [trajets]);

  // 🗺️ Options pour les pays de départ (sous_regionale) - UNIQUEMENT les valeurs de "depart" des trajets de type "pays"
  const departureCountryOptions = useMemo(() => {
    if (expeditionData.type_service === 'sous_regionale') {
      const paysTrajets = trajets.filter(t => t.type === 'pays');
      const options = new Set();
      paysTrajets.forEach(t => {
        if (t.depart) options.add(t.depart);
      });
      return Array.from(options).sort();
    }
    return [];
  }, [expeditionData.type_service, trajets]);

  // 🗺️ Options pour les pays de destination (sous_regionale) - UNIQUEMENT les valeurs de "destination" des trajets de type "pays"
  const destinationCountryOptions = useMemo(() => {
    if (expeditionData.type_service === 'sous_regionale') {
      const paysTrajets = trajets.filter(t => t.type === 'pays');
      const options = new Set();
      paysTrajets.forEach(t => {
        if (t.destination) options.add(t.destination);
      });
      return Array.from(options).sort();
    }
    return [];
  }, [expeditionData.type_service, trajets]);

  // 🗺️ Fonction helper pour vérifier si une ville appartient à un pays
  const isCityInCountry = useMemo(() => {
    return (cityName, countryName) => {
      if (!cityName || !countryName) return false;
      const citiesInCountry = getCitiesByCountry(countryName);
      return citiesInCountry && Object.keys(citiesInCountry).includes(cityName);
    };
  }, []);

  // 🗺️ Options pour les villes de départ (interurbaine/sous_regionale) - UNIQUEMENT les valeurs de "depart" des trajets de type "ville"
  const departureCityOptionsFromTrajets = useMemo(() => {
    if (expeditionData.type_service === 'interurbaine' || expeditionData.type_service === 'sous_regionale') {
      // Filtrer les trajets de type "ville" et extraire UNIQUEMENT les valeurs de "depart"
      const villeTrajets = trajets.filter(t => t.type === 'ville');
      const options = new Set();
      villeTrajets.forEach(t => {
        if (t.depart) {
          // Pour sous_regionale, filtrer par pays si un pays est sélectionné
          if (expeditionData.type_service === 'sous_regionale' && expeditionData.departure_country) {
            if (isCityInCountry(t.depart, expeditionData.departure_country)) {
              options.add(t.depart);
            }
          } else {
            // Pour interurbaine, prendre tous les departs
            options.add(t.depart);
          }
        }
      });
      return Array.from(options).sort();
    }
    return [];
  }, [expeditionData.type_service, expeditionData.departure_country, trajets, isCityInCountry]);

  // 🗺️ Détecter si le pays de départ n'a pas de trajets ville
  const hasDepartureCountryCityRoutes = useMemo(() => {
    if (expeditionData.type_service === 'sous_regionale' && expeditionData.departure_country) {
      return departureCityOptionsFromTrajets.length > 0;
    }
    return true; // Pour les autres cas, considérer comme true
  }, [expeditionData.type_service, expeditionData.departure_country, departureCityOptionsFromTrajets]);

  // 🗺️ Options pour les villes de destination (interurbaine/sous_regionale) - UNIQUEMENT les valeurs de "destination" des trajets de type "ville"
  const destinationCityOptionsFromTrajets = useMemo(() => {
    if (expeditionData.type_service === 'interurbaine' || expeditionData.type_service === 'sous_regionale') {
      // Filtrer les trajets de type "ville" et extraire UNIQUEMENT les valeurs de "destination"
      const villeTrajets = trajets.filter(t => t.type === 'ville');
      const options = new Set();
      villeTrajets.forEach(t => {
        if (t.destination) {
          // Pour sous_regionale, filtrer par pays si un pays est sélectionné
          if (expeditionData.type_service === 'sous_regionale' && expeditionData.destination_country) {
            if (isCityInCountry(t.destination, expeditionData.destination_country)) {
              options.add(t.destination);
            }
          } else {
            // Pour interurbaine, prendre toutes les destinations
            options.add(t.destination);
          }
        }
      });
      return Array.from(options).sort();
    }
    return [];
  }, [expeditionData.type_service, expeditionData.destination_country, trajets, isCityInCountry]);

  // 🗺️ Détecter si le pays de destination n'a pas de trajets ville
  const hasDestinationCountryCityRoutes = useMemo(() => {
    if (expeditionData.type_service === 'sous_regionale' && expeditionData.destination_country) {
      return destinationCityOptionsFromTrajets.length > 0;
    }
    return true; // Pour les autres cas, considérer comme true
  }, [expeditionData.type_service, expeditionData.destination_country, destinationCityOptionsFromTrajets]);

  // 🗺️ Options pour les communes de départ (autres services) - UNIQUEMENT les valeurs de "depart"
  const departureCommuneOptions = useMemo(() => {
    if (expeditionData.type_service && 
        expeditionData.type_service !== 'interurbaine' && 
        expeditionData.type_service !== 'sous_regionale') {
      const communeTrajets = trajets.filter(t => t.type === 'commune');
      const options = new Set();
      communeTrajets.forEach(t => {
        if (t.depart) options.add(t.depart);
      });
      return Array.from(options).sort();
    }
    return [];
  }, [expeditionData.type_service, trajets]);

  // 🗺️ Options pour les communes de destination (autres services) - UNIQUEMENT les valeurs de "destination"
  const destinationCommuneOptions = useMemo(() => {
    if (expeditionData.type_service && 
        expeditionData.type_service !== 'interurbaine' && 
        expeditionData.type_service !== 'sous_regionale') {
      const communeTrajets = trajets.filter(t => t.type === 'commune');
      const options = new Set();
      communeTrajets.forEach(t => {
        if (t.destination) options.add(t.destination);
      });
      return Array.from(options).sort();
    }
    return [];
  }, [expeditionData.type_service, trajets]);

  // 🌍 Fonctions pour gérer les villes par pays (service sous-régionale)
  const getCitiesForCountry = React.useMemo(() => {
    return (country) => {
      if (!country) return [];
      const cities = getCitiesByCountry(country);
      return Object.keys(cities).sort();
    };
  }, []);

  // 🗺️ Options pour les villes (utilise les trajets si disponibles, sinon fallback)
  const departureCityOptions = React.useMemo(() => {
    // Pour interurbaine/sous_regionale : utiliser les trajets de type "ville" - UNIQUEMENT depart
    if ((expeditionData.type_service === 'interurbaine' || expeditionData.type_service === 'sous_regionale')) {
      if (departureCityOptionsFromTrajets.length > 0) {
        return departureCityOptionsFromTrajets;
      }
      // Fallback vers l'ancienne méthode si pas de trajets
      if (expeditionData.type_service === 'sous_regionale' && expeditionData.departure_country) {
        return getCitiesForCountry(expeditionData.departure_country);
      }
    }
    // Pour les autres services : utiliser les trajets de type "commune" - UNIQUEMENT depart
    if (departureCommuneOptions.length > 0) {
      return departureCommuneOptions;
    }
    // Fallback vers les communes d'Abidjan
    return ABIDJAN_COMMUNES;
  }, [expeditionData.type_service, expeditionData.departure_country, departureCityOptionsFromTrajets, departureCommuneOptions, getCitiesForCountry]);

  const destinationCityOptions = React.useMemo(() => {
    // Pour interurbaine/sous_regionale : utiliser les trajets de type "ville" - UNIQUEMENT destination
    if ((expeditionData.type_service === 'interurbaine' || expeditionData.type_service === 'sous_regionale')) {
      if (destinationCityOptionsFromTrajets.length > 0) {
        return destinationCityOptionsFromTrajets;
      }
      // Fallback vers l'ancienne méthode si pas de trajets
      if (expeditionData.type_service === 'sous_regionale' && expeditionData.destination_country) {
        return getCitiesForCountry(expeditionData.destination_country);
      }
    }
    // Pour les autres services : utiliser les trajets de type "commune" - UNIQUEMENT destination
    if (destinationCommuneOptions.length > 0) {
      return destinationCommuneOptions;
    }
    // Fallback vers les communes d'Abidjan
    return ABIDJAN_COMMUNES;
  }, [expeditionData.type_service, expeditionData.destination_country, destinationCityOptionsFromTrajets, destinationCommuneOptions, getCitiesForCountry]);

  // 🏙️ Fonction pour obtenir la liste des lieux selon le type de service (pour affichage générique - utilise depart)
  const getLocationOptions = React.useMemo(() => {
    if (expeditionData.type_service === 'interurbaine') {
      // Utiliser les trajets de type "ville" si disponibles (depart uniquement)
      return departureCityOptionsFromTrajets.length > 0 ? departureCityOptionsFromTrajets : ALL_CITIES;
    } else if (expeditionData.type_service === 'sous_regionale') {
      // Utiliser les trajets de type "pays" si disponibles (depart uniquement)
      return departureCountryOptions.length > 0 ? departureCountryOptions : ALL_COUNTRIES;
    } else {
      // Utiliser les trajets de type "commune" si disponibles (depart uniquement)
      return departureCommuneOptions.length > 0 ? departureCommuneOptions : ABIDJAN_COMMUNES;
    }
  }, [expeditionData.type_service, departureCityOptionsFromTrajets, departureCountryOptions, departureCommuneOptions]);

  // 🌍 Forcer le mode "relay_point" pour le service interurbaine et sous-régionale
  useEffect(() => {
    if ((expeditionData.type_service === 'interurbaine' || expeditionData.type_service === 'sous_regionale') && expeditionData.shippingMode === 'home_delivery') {
      console.log('🌍 Service interurbaine/sous-régionale détecté - passage automatique en mode point relais');
      updateExpeditionData('shippingMode', 'relay_point');
    }
  }, [expeditionData.type_service, expeditionData.shippingMode]);

  // 🇨🇮 Définir Abidjan comme ville de départ par défaut pour le service sous-régionale avec CI
  useEffect(() => {
    if (expeditionData.type_service === 'sous_regionale') {
      // Toujours s'assurer que CI est sélectionné comme pays de départ par défaut
      if (expeditionData.departure_country !== 'Côte d\'Ivoire') {
        console.log('🇨🇮 Service sous-régionale - définition de CI comme pays de départ par défaut');
        updateExpeditionData('departure_country', 'Côte d\'Ivoire');
      }
      // Définir Abidjan comme ville par défaut si CI est sélectionné et pas de ville
      if (expeditionData.departure_country === 'Côte d\'Ivoire' && !expeditionData.departure_city) {
        console.log('🇨🇮 Service sous-régionale avec CI - définition d\'Abidjan comme ville de départ par défaut');
        updateExpeditionData('departure_city', 'Abidjan');
      }
    }
  }, [expeditionData.type_service, expeditionData.departure_country, expeditionData.departure_city]);

  // 🛡️ useEffect pour forcer l'assurance pour les types de colis sécurisés et gros volume
  useEffect(() => {
    if (isInsuranceMandatory(expeditionData.packageType) && !expeditionData.isInsured) {
      console.log('🛡️ Assurance obligatoire pour le type de colis:', expeditionData.packageType);
      updateExpeditionData('isInsured', true);
      // Définir une valeur déclarée par défaut si elle n'existe pas
      if (!expeditionData.declared_value || expeditionData.declared_value === '') {
        updateExpeditionData('declared_value', '50000');
      }
    }
  }, [expeditionData.packageType, expeditionData.isInsured, expeditionData.declared_value]);

  // Gérer l'onglet actif selon le type de service sélectionné (désactivé)
  // useEffect(() => {
  //   if (expeditionData.type_service === 'interurbaine') {
  //     setActiveServiceTab('interurban');
  //   } else if (['flash', 'express', 'standard', 'economique'].includes(expeditionData.type_service)) {
  //     setActiveServiceTab('local');
  //   }
  // }, [expeditionData.type_service]);

  // Pré-remplir les données avec celles de l'estimation
  useEffect(() => {
    const estimationData = getEstimationData();
    if (estimationData) {
      console.log('🔄 Pré-remplissage avec les données d\'estimation:', estimationData);
      
      // Mettre à jour les données d'expédition avec celles de l'estimation
      setExpeditionData(prev => ({
        ...prev,
        // Adresses
        from_address: estimationData.pickupLocation?.address || estimationData.pickupLocation || '',
        to_address: estimationData.deliveryLocation?.address || estimationData.deliveryLocation || '',
        
        // Dimensions et poids
        weight: estimationData.weight || 0,
        length: estimationData.dimensions?.length || 0,
        width: estimationData.dimensions?.width || 0,
        height: estimationData.dimensions?.height || 0,
        
        // Mode et service
        mode_expedition: estimationData.deliveryMode === 'relais' ? 'relay_point' : 'home_delivery',
        type_service: estimationData.serviceType || 'standard',
        
        // Valeur déclarée
        declared_value: estimationData.declaredValue || 0,
        
        // Prix estimé
        estimated_price: estimationData.estimatedPrice || 0
      }));
      
      // Nettoyer les données du localStorage après utilisation
      localStorage.removeItem('ksl_expedition_data');
    }
  }, []);

  // 🎯 Détection automatique du type de service DÉSACTIVÉE - L'utilisateur doit choisir manuellement
  // useEffect(() => {
  //   if (expeditionData.from_address && expeditionData.to_address) {
  //     const detectedService = detectServiceType(expeditionData.from_address, expeditionData.to_address);
  //     if (detectedService !== expeditionData.type_service) {
  //       console.log('🔄 Détection automatique du service:', detectedService);
  //       updateExpeditionData('type_service', detectedService);
  //       
  //       // Mettre à jour le délai selon le service détecté
  //       const serviceDelays = {
  //         'flash': '2h à 4h',
  //         'express': '24H - 0-1 JOUR',
  //         'standard': '24h à 48h',
  //         'economique': '48h à 78h',
  //         'interurbaine': '86H - 2-4 JOURS'
  //       };
  //       updateExpeditionData('delais_livraison', serviceDelays[detectedService] || '24h à 48h');
  //     }
  //   }
  // }, [expeditionData.from_address, expeditionData.to_address]);

  // 🏙️ Pré-remplissage automatique des adresses selon la sélection commune/ville - DÉSACTIVÉ
  // useEffect(() => {
  //   if (selectedCommune && expeditionData.type_service !== 'interurbaine') {
  //     // Pré-remplir l'adresse de départ avec la commune sélectionnée
  //     const communeAddress = `${selectedCommune}, Abidjan, Côte d'Ivoire`;
  //     if (expeditionData.from_address !== communeAddress) {
  //       updateExpeditionData('from_address', communeAddress);
  //       console.log('🏙️ Adresse de départ pré-remplie avec la commune:', communeAddress);
  //     }
  //   }
  // }, [selectedCommune, expeditionData.type_service, expeditionData.from_address]);

  // useEffect(() => {
  //   if (selectedDepartureCity && expeditionData.type_service === 'interurbaine') {
  //     // Pré-remplir l'adresse de départ avec la ville sélectionnée
  //     const cityAddress = `${selectedDepartureCity}, Côte d'Ivoire`;
  //     if (expeditionData.from_address !== cityAddress) {
  //       updateExpeditionData('from_address', cityAddress);
  //       console.log('🌍 Adresse de départ pré-remplie avec la ville:', cityAddress);
  //     }
  //   }
  // }, [selectedDepartureCity, expeditionData.type_service, expeditionData.from_address]);

  // Articles - état séparé pour faciliter la gestion
  const [currentItem, setCurrentItem] = useState({
    name: '',
    category: 'Général',
    selectedCategory: 'moto', // Catégorie sélectionnée pour l'affichage (sera mise à jour par useEffect)
    showFormats: false, // Afficher/masquer la liste des formats
    weight: '',
    length: '',
    width: '',
    height: '',
    quantity: 1
  });

  // 🚐 Gérer les catégories selon le type de service (seulement au changement de type de service)
  useEffect(() => {
    // Ne pas réinitialiser si on a déjà des articles ajoutés
    if (expeditionData.pickup_items.length > 0) {
      console.log('📦 Articles déjà présents - pas de réinitialisation de l\'onglet');
      return;
    }
    
    if (expeditionData.type_service === 'interurbaine' || expeditionData.type_service === 'sous_regionale') {
      console.log('🚐 Service interurbaine/sous-régionale détecté - passage automatique à la catégorie car');
      setCurrentItem(prev => ({ ...prev, selectedCategory: 'car' }));
    } else {
      // Services locaux : sélectionner "moto" par défaut
      console.log('🏍️ Service local détecté - passage automatique à la catégorie moto');
      setCurrentItem(prev => ({ ...prev, selectedCategory: 'moto' }));
    }
  }, [expeditionData.type_service]);

  // Mock data pour les catégories et véhicules
  const [categories] = useState([
  'Documents / Imprimés / Enveloppe',
  'Textile & Accessoires',
  'Électronique & Objets connectés',
  'Produits alimentaires',
  'Produits de beauté & Hygiène',
  'Articles pour bébé & Enfants',
  'Pièces détachées & Outils',
  'Maison, Déco & Électroménagers',
  'Santé & Produits médicaux',
  'Fournitures & Accessoires de bureau',
  'Autres / Divers'
]);

  // Fonction helper pour obtenir l'icône d'une catégorie
  const getCategoryIcon = (category) => {
    const iconMap = {
      'Documents / Imprimés / Enveloppe': '📄',
      'Textile & Accessoires': '👕',
      'Électronique & Objets connectés': '💻',
      'Produits alimentaires': '🍎',
      'Produits de beauté & Hygiène': '💄',
      'Articles pour bébé & Enfants': '👶',
      'Pièces détachées & Outils': '🔧',
      'Maison, Déco & Électroménagers': '🏠',
      'Santé & Produits médicaux': '💊',
      'Fournitures & Accessoires de bureau': '📎',
      'Autres / Divers': '📦'
    };
    return iconMap[category] || '📦';
  };

  // Gestion des étapes (TON STYLE) - MODIFIÉ : Étapes 1 et 2 combinées
  const steps = [
    { id: 1, title: 'Expéditeur & Destinataire', description: 'Informations complètes' },
    { id: 2, title: 'Articles', description: 'Que livrer ?' },
    { id: 3, title: 'Devis', description: 'Calculer le prix' },
    { id: 4, title: 'Paiement', description: 'Choisir mode paiement' },
    { id: 5, title: 'Confirmation', description: 'Récapitulatif final' }
  ];

  // 🔄 Charger les transporteurs
  const loadCarriers = React.useCallback(async () => {
    try {
      setIsLoadingCarriers(true);
      const response = await carrierAPI.getAllCarriers();
      const data = response?.data;
      const carriersList = Array.isArray(data) ? data : (Array.isArray(data?.results) ? data.results : []);
      setCarriers(carriersList);

      // Sélection par défaut par NOM (l'API ne retourne pas d'ID)
      if (carriersList.length > 0) {
        const preferredName = 'EVA LIVRAISON';
        const found = carriersList.find(c => (c?.nom || c?.name || '').toString().trim().toLowerCase() === preferredName.toLowerCase());
        if (found && (found.nom || found.name)) {
          updateExpeditionData('selectedCarrier', found.nom || found.name);
          console.log('✅ Transporteur sélectionné par défaut:', found.nom || found.name);
        } else {
          updateExpeditionData('selectedCarrier', carriersList[0].nom || carriersList[0].name || '');
          console.warn('⚠️ Transporteur préféré non trouvé, sélection du premier disponible');
        }
      }
    } catch (error) {
      console.error('❌ Erreur chargement transporteurs:', error);
      setCarriersError('Erreur lors du chargement des transporteurs');
    } finally {
      setIsLoadingCarriers(false);
    }
  }, []);

  // Fonction pour charger les points relais selon le type de colis
  const loadRelayPoints = async () => {
    setIsLoadingRelayPoints(true);
    setRelayPointsError(null);
    
    try {
      console.log('🏪 [loadRelayPoints] → Chargement de tous les points relais...');
      
      const response = await relayAPI.getAllRelays();
      console.log('✅ Points relais récupérés:', response.data);
      console.log('📊 Nombre de points relais trouvés:', response.data?.length || 0);
      
      // Transformer les données pour correspondre au format attendu
      let transformedRelayPoints = [];
      
      if (response?.data?.results) {
        // Format avec pagination
        transformedRelayPoints = response.data.results.map(relay => ({
          id: relay.id,
          name: relay.nom || relay.name,
          address: relay.adresse || relay.address,
          phone: relay.gerant?.phone || relay.phone || 'N/A',
          acceptedTypes: relay.typeColis || relay.accepted_types || ['standard'],
          city: relay.ville || relay.city,
          village: relay.vilage || relay.village,
          openingHours: relay.horaires_ouverture || relay.opening_hours,
          status: relay.statut || relay.status,
          latitude: relay.latitude,
          longitude: relay.longitude,
          description: relay.description || '',
          capacity: relay.capacite || relay.capacity || 50
        }));
      } else if (response?.data && Array.isArray(response.data)) {
        // Format direct (tableau)
        transformedRelayPoints = response.data.map(relay => ({
          id: relay.id,
          name: relay.nom || relay.name,
          address: relay.adresse || relay.address,
          phone: relay.gerant?.phone || relay.phone || 'N/A',
          acceptedTypes: relay.typeColis || relay.accepted_types || ['standard'],
          city: relay.ville || relay.city,
          village: relay.vilage || relay.village,
          openingHours: relay.horaires_ouverture || relay.opening_hours,
          status: relay.statut || relay.status,
          latitude: relay.latitude,
          longitude: relay.longitude,
          description: relay.description || '',
          capacity: relay.capacite || relay.capacity || 50
        }));
      } else {
        console.warn('⚠️ Format de réponse inattendu pour les points relais:', response);
        transformedRelayPoints = [];
      }
      
      // Filtrer seulement les points relais actifs
      const activeRelayPoints = transformedRelayPoints.filter(relay => 
        relay.status === 'actif' || relay.status === 'active'
      );
      
      setRelayPoints(activeRelayPoints);
      console.log('✅ Points relais actifs chargés:', activeRelayPoints.length);
      
    } catch (error) {
      console.error('❌ Erreur chargement points relais:', error);
      setRelayPointsError('Impossible de charger les points relais');
    } finally {
      setIsLoadingRelayPoints(false);
    }
  };

  // 📍 Gestion des adresses
  const handlePickupLocationSelect = (location) => {
    console.log('📍 handlePickupLocationSelect appelé avec:', location);
    setExpeditionData(prev => ({
      ...prev,
      from_address: location.address,
      from_latitude: location.latitude?.toString() || '',
      from_longitude: location.longitude?.toString() || ''
    }));
  };

  const handleDeliveryLocationSelect = (location) => {
    console.log('📍 handleDeliveryLocationSelect appelé avec:', location);
    setExpeditionData(prev => ({
      ...prev,
      to_address: location.address,
      to_latitude: location.latitude?.toString() || '',
      to_longitude: location.longitude?.toString() || ''
    }));
  };

  // Références pour suivre si le géocodage automatique est en cours
  const geocodingFromAddressRef = useRef(false);
  const geocodingToAddressRef = useRef(false);

  // Fonction pour géocoder une adresse et mettre à jour les coordonnées
  const geocodeAddress = React.useCallback((address, type = 'from') => {
    if (!window.google || !window.google.maps || !address || address.trim() === '') {
      return;
    }

    // Marquer que le géocodage est en cours
    if (type === 'from') {
      geocodingFromAddressRef.current = true;
    } else {
      geocodingToAddressRef.current = true;
    }

    const geocoder = new window.google.maps.Geocoder();
    
    geocoder.geocode({ address: address }, (results, status) => {
      if (type === 'from') {
        geocodingFromAddressRef.current = false;
      } else {
        geocodingToAddressRef.current = false;
      }

      if (status === window.google.maps.GeocoderStatus.OK && results && results.length > 0) {
        const result = results[0];
        const locationData = {
          address: result.formatted_address,
          latitude: result.geometry.location.lat(),
          longitude: result.geometry.location.lng(),
          placeId: result.place_id,
          name: result.formatted_address
        };

        console.log(`📍 Adresse ${type === 'from' ? 'de ramassage' : 'de livraison'} géocodée automatiquement:`, locationData);
        
        // Mettre à jour directement les coordonnées dans expeditionData
        if (type === 'from') {
          setExpeditionData(prev => ({
            ...prev,
            from_address: locationData.address,
            from_latitude: locationData.latitude?.toString() || '',
            from_longitude: locationData.longitude?.toString() || ''
          }));
        } else {
          setExpeditionData(prev => ({
            ...prev,
            to_address: locationData.address,
            to_latitude: locationData.latitude?.toString() || '',
            to_longitude: locationData.longitude?.toString() || ''
          }));
        }
      } else {
        console.warn(`⚠️ Impossible de géocoder l'adresse ${type === 'from' ? 'de ramassage' : 'de livraison'}:`, address);
      }
    });
  }, []);

  // 🔄 Déclencher le géocodage automatique quand from_address est pré-rempli et que les coordonnées sont vides
  useEffect(() => {
    // Attendre que Google Maps soit chargé
    if (!window.google || !window.google.maps) {
      return;
    }

    // Vérifier si l'adresse de ramassage a été pré-remplie et que les coordonnées sont vides
    if (
      expeditionData.from_address && 
      expeditionData.from_address.trim() !== '' &&
      (!expeditionData.from_latitude || !expeditionData.from_longitude) &&
      !geocodingFromAddressRef.current
    ) {
      // Vérifier si l'adresse correspond à un format de pré-remplissage automatique (format: "Ville, Pays")
      const isAutoFilled = expeditionData.from_address.includes(',');
      
      if (isAutoFilled) {
        console.log('🔄 Déclenchement du géocodage automatique pour from_address:', expeditionData.from_address);
        // Délai pour éviter les géocodages multiples
        const timer = setTimeout(() => {
          geocodeAddress(expeditionData.from_address, 'from');
        }, 500);
        
        return () => clearTimeout(timer);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expeditionData.from_address, geocodeAddress]);

  // 🔄 Déclencher le géocodage automatique quand to_address est pré-rempli et que les coordonnées sont vides
  useEffect(() => {
    // Attendre que Google Maps soit chargé
    if (!window.google || !window.google.maps) {
      return;
    }

    // Vérifier si l'adresse de livraison a été pré-remplie et que les coordonnées sont vides
    if (
      expeditionData.to_address && 
      expeditionData.to_address.trim() !== '' &&
      (!expeditionData.to_latitude || !expeditionData.to_longitude) &&
      !geocodingToAddressRef.current &&
      expeditionData.shippingMode === 'home_delivery' // Seulement pour livraison à domicile
    ) {
      // Vérifier si l'adresse correspond à un format de pré-remplissage automatique (format: "Ville, Pays")
      const isAutoFilled = expeditionData.to_address.includes(',');
      
      if (isAutoFilled) {
        console.log('🔄 Déclenchement du géocodage automatique pour to_address:', expeditionData.to_address);
        // Délai pour éviter les géocodages multiples
        const timer = setTimeout(() => {
          geocodeAddress(expeditionData.to_address, 'to');
        }, 500);
        
        return () => clearTimeout(timer);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expeditionData.to_address, expeditionData.shippingMode, geocodeAddress]);

  // Fonction utilitaire pour calculer les coordonnées finales selon le mode de livraison
  const calculateFinalCoordinates = () => {
    let finalFromLatitude = expeditionData.from_latitude;
    let finalFromLongitude = expeditionData.from_longitude;
    let finalFromAddress = expeditionData.from_address;
    let finalToLatitude = expeditionData.to_latitude;
    let finalToLongitude = expeditionData.to_longitude;
    let finalToAddress = expeditionData.to_address;
    
    // Validation des coordonnées de départ (adresse de ramassage)
    if (!expeditionData.from_latitude || !expeditionData.from_longitude) {
      throw new Error('❌ Coordonnées GPS de ramassage manquantes. Veuillez saisir l\'adresse de ramassage complète ou cliquer sur "Utiliser ma position" pour obtenir automatiquement les coordonnées GPS.');
    }
    
    // Pour le mode point relais : TO = coordonnées du point relais de destination
    if (expeditionData.shippingMode === 'relay_point' && expeditionData.selectedRelayPoint) {
      console.log('📍 Mode point relais détecté - recherche des coordonnées du point relais de destination...');
      
      let selectedRelay = null;
      if (typeof expeditionData.selectedRelayPoint === 'object') {
        selectedRelay = expeditionData.selectedRelayPoint;
      } else {
        const relayId = parseInt(expeditionData.selectedRelayPoint);
        selectedRelay = relayPoints.find(relay => relay.id === relayId);
      }
      
      if (selectedRelay) {
        // Vérifier si le point relais de destination a des coordonnées GPS
        if (!selectedRelay.latitude || !selectedRelay.longitude) {
          throw new Error(`❌ Coordonnées GPS manquantes pour le point relais de destination "${selectedRelay.name}". Veuillez contacter l'administrateur pour configurer les coordonnées GPS de ce point relais.`);
        }
        
        finalToLatitude = selectedRelay.latitude;
        finalToLongitude = selectedRelay.longitude;
        finalToAddress = selectedRelay.address || selectedRelay.name;
        
        console.log('✅ Coordonnées TO mises à jour avec celles du point relais de destination:');
        console.log('📍 Point relais destination:', selectedRelay.name);
        console.log('📍 Latitude:', finalToLatitude);
        console.log('📍 Longitude:', finalToLongitude);
        console.log('📍 Adresse:', finalToAddress);
      } else {
        throw new Error(`❌ Point relais de destination introuvable. Veuillez sélectionner un point relais valide pour la destination.`);
      }
    }
    // Pour le mode livraison à domicile : TO = coordonnées de l'adresse de livraison
    else if (expeditionData.shippingMode === 'home_delivery') {
      if (!expeditionData.to_latitude || !expeditionData.to_longitude) {
        throw new Error('❌ Coordonnées GPS de livraison manquantes. Veuillez saisir l\'adresse de livraison complète ou cliquer sur "Utiliser ma position" pour obtenir automatiquement les coordonnées GPS.');
      }
    }
    
    return {
      fromLatitude: finalFromLatitude,
      fromLongitude: finalFromLongitude,
      fromAddress: finalFromAddress,
      toLatitude: finalToLatitude,
      toLongitude: finalToLongitude,
      toAddress: finalToAddress
    };
  };

  // 🧮 Calculer l'estimation (mis à jour avec ClientSendParcel)
  const handleEstimation = async () => {
    // Validation avec toasts pour l'estimation
    if (!expeditionData.from_address || !expeditionData.to_address) {
      toast.error('Veuillez saisir les adresses de ramassage et de livraison');
      return;
    }

    if (expeditionData.pickup_items.length === 0) {
      toast.error('Veuillez ajouter au moins un article');
      return;
    }

    setIsCalculatingQuote(true);

    try {
      // 📍 Calculer les coordonnées finales selon le mode de livraison
      const coordinates = calculateFinalCoordinates();
      const { fromLatitude, fromLongitude, toLatitude, toLongitude } = coordinates;
      
      console.log('🧮 ========== CALCUL TARIF KARTIAN ==========');
      console.log('📋 Données d\'entrée:', expeditionData);
      
      // 1️⃣ Utiliser uniquement les villes sélectionnées (pas d'extraction depuis les adresses)
      const departure_city = expeditionData.departure_city || '';
      const destination_city = expeditionData.destination_city || '';
      
      console.log('📍 ÉTAPE 1 - Villes sélectionnées:');
      console.log('   - Ville origine sélectionnée:', departure_city);
      console.log('   - Ville destination sélectionnée:', destination_city);
      console.log('   - Adresse origine:', expeditionData.from_address);
      console.log('   - Adresse destination:', expeditionData.to_address);
      
      // 🔒 VALIDATION STRICTE : Vérifier que les villes sont bien sélectionnées
      if (!departure_city) {
        toast.error('⚠️ Ville/Commune de départ requise');
        setIsCalculatingQuote(false);
        return;
      }
      
      if (!destination_city) {
        toast.error('⚠️ Ville/Commune de destination requise');
        setIsCalculatingQuote(false);
        return;
      }
      
      console.log('✅ Villes validées - Utilisation des valeurs sélectionnées uniquement');
      
      // 2️⃣ Calculer le poids total en utilisant les poids fixes pour la tarification
      const totalWeight = expeditionData.pickup_items.reduce((sum, item) => {
        // Utiliser le poids fix pour la tarification si disponible, sinon le poids saisi
        const weight = item.weightForCalculation || parseFloat(item.weight) || 0;
        const quantity = parseInt(item.quantity) || 1;
        const itemWeight = weight * quantity;
        
        // Validation pour éviter NaN
        if (isNaN(itemWeight)) {
          console.warn(`⚠️ Poids invalide pour ${item.name}: ${weight} kg × ${item.quantity}`);
          return sum;
        }
        
        console.log(`   - ${item.name}: ${weight} kg (fix) × ${quantity} = ${itemWeight} kg`);
        return sum + itemWeight;
      }, 0);
      
      // Calculer le volume total en utilisant les volumes fixes pour la tarification
      let maxLength = 0, maxWidth = 0, maxHeight = 0;
      let totalVolumeCm3 = 0;

      expeditionData.pickup_items.forEach(item => {
        const length = parseFloat(item.length) || 0;
        const width = parseFloat(item.width) || 0;
        const height = parseFloat(item.height) || 0;
        const quantity = parseInt(item.quantity) || 1;
        
        // Utiliser le volume fix pour la tarification si disponible
        const volumeForCalculation = item.volumeForCalculation || (length * width * height);
        const itemVolumeCm3 = volumeForCalculation * quantity;
        
        // Validation des dimensions pour l'affichage
        if (length < 0 || width < 0 || height < 0 || isNaN(length) || isNaN(width) || isNaN(height)) {
          console.warn(`⚠️ Dimensions invalides pour ${item.name}: ${length}×${width}×${height} cm`);
        } else {
          if (length > maxLength) maxLength = length;
          if (width > maxWidth) maxWidth = width;
          if (height > maxHeight) maxHeight = height;
        }
        
        // Validation du volume
        if (isNaN(itemVolumeCm3) || itemVolumeCm3 < 0) {
          console.warn(`⚠️ Volume invalide pour ${item.name}: ${itemVolumeCm3} cm³`);
          return;
        }
        
        totalVolumeCm3 += itemVolumeCm3;
        console.log(`   - ${item.name}: ${volumeForCalculation.toFixed(0)} cm³ (fix) × ${quantity} = ${itemVolumeCm3.toFixed(0)} cm³`);
      });
      
      // 🔧 Validation finale des valeurs calculées
      let finalTotalWeight = totalWeight;
      if (isNaN(finalTotalWeight) || finalTotalWeight < 0) {
        console.warn('⚠️ Poids total invalide, utilisation de 1 kg par défaut');
        finalTotalWeight = 1;
      }
      
      if (isNaN(totalVolumeCm3) || totalVolumeCm3 < 0) {
        console.warn('⚠️ Volume total invalide, utilisation de 1000 cm³ par défaut');
        totalVolumeCm3 = 1000;
      }
      
      // console.log('📦 ÉTAPE 2 - Calculs poids et volume (CORRIGÉ):');
      // console.log('   - Poids total:', finalTotalWeight.toFixed(2), 'kg');
      // console.log('   - Volume total:', totalVolumeCm3.toFixed(0), 'cm³ =', (totalVolumeCm3 / 1000).toFixed(2), 'litres');
      // console.log('   - Dimensions max:', { 
      //   maxLength: maxLength.toFixed(1), 
      //   maxWidth: maxWidth.toFixed(1), 
      //   maxHeight: maxHeight.toFixed(1) 
      // }, 'cm');
      
      // 3️⃣ Utiliser le type de service sélectionné par l'utilisateur
      let serviceType = expeditionData.type_service; // Utiliser la sélection utilisateur (pas de fallback)
      
      // console.log('🚚 ÉTAPE 3 - Type de service:');
      // console.log('   - Service sélectionné par l\'utilisateur:', serviceType);
      // console.log('   - Délai de livraison:', expeditionData.delais_livraison || '24h à 48h');
      // console.log('   - Véhicule ID pour référence:', expeditionData.vehicle_type_id);
      
      // 4️⃣ Gestion du point relais
      let isRelayPoint = expeditionData.shippingMode === 'relay_point';
      let relayPointFee = 0;
      
      if (isRelayPoint) {
        relayPointFee = -100; // Réduction de 100 FCFA
        // console.log('🏪 ÉTAPE 4 - Point relais:');
        // console.log('   - Mode point relais activé');
        // console.log('   - Réduction appliquée:', relayPointFee, 'FCFA');
      }
      
      // 5️⃣ Assurance (selon choix utilisateur)
      const declaredValue = parseFloat(expeditionData.declared_value) || 0;
      const isInsured = expeditionData.isInsured && declaredValue > 0 && !isNaN(declaredValue); // Validation supplémentaire
      
      console.log('🛡️ ÉTAPE 5 - Assurance:');
      console.log('   - Assurance demandée par utilisateur:', expeditionData.isInsured);
      console.log('   - Valeur déclarée:', declaredValue.toFixed(0), 'FCFA');
      console.log('   - Assurance activée (final):', isInsured);
      
      // 6️⃣ Calculer le tarif avec notre service (NOUVELLE VERSION API)
      
      // 🚚 ANCIENNE LOGIQUE COMMENTÉE : PRIORITÉ 1 = TRANSPORTEUR DU GÉRANT DU POINT DE RELAIS, PRIORITÉ 2 = TRANSPORTEUR ASSIGNÉ, PRIORITÉ 3 = TRANSPORTEUR PAR DÉFAUT
      /*
      let selectedCarrierId = 1; // Transporteur par défaut
      let selectedCarrierCode = 'EVA'; // Code par défaut
      
      // PRIORITÉ 1: Si mode point relais, utiliser le transporteur du gérant du point de relais
      if (expeditionData.shippingMode === 'relay_point' && selectedRelayPoint) {
        console.log('🏪 Mode point relais détecté - recherche du transporteur du gérant...');
        console.log('   - Point relais sélectionné:', selectedRelayPoint.nom || selectedRelayPoint.name);
        console.log('   - Gérant:', selectedRelayPoint.gerant);
        
        if (selectedRelayPoint.gerant && selectedRelayPoint.gerant.transporteur) {
          selectedCarrierId = selectedRelayPoint.gerant.transporteur;
          selectedCarrierCode = 'RELAY'; // Code pour transporteur de point relais
          console.log('✅ PRIORITÉ 1: Transporteur du gérant du point de relais:', selectedCarrierId);
          console.log('   - Gérant:', selectedRelayPoint.gerant.first_name, selectedRelayPoint.gerant.last_name);
          console.log('   - ID transporteur gérant:', selectedCarrierId);
        } else {
          console.warn('⚠️ Point relais sélectionné sans gérant ou transporteur');
        }
      }
      // PRIORITÉ 2: Si authentifié, utiliser le transporteur assigné
      else if (isAuthenticated && assignedCarrier) {
        selectedCarrierId = assignedCarrier.id || expeditionData.carrier_id || expeditionData.carrierId || 7;
        selectedCarrierCode = assignedCarrier.code || 'EVA';
        console.log('✅ PRIORITÉ 2: Transporteur assigné à l\'utilisateur:', selectedCarrierId);
      }
      // PRIORITÉ 3: Transporteur par défaut (déjà défini)
      else {
        console.log('✅ PRIORITÉ 3: Transporteur par défaut:', selectedCarrierId);
      }
      */

      // 🚚 LOGIQUE DE SÉLECTION DU TRANSPORTEUR
      let selectedCarrierId = null;
      let selectedCarrierCode = 'EVA';
      
      if (expeditionData.shippingMode === 'relay_point' && selectedRelayPoint) {
        // MODE POINT RELAIS: Transporteur du gérant du point de relais
        selectedCarrierCode = 'RELAY';
        // console.log('🏪 Mode point relais détecté - recherche du transporteur du gérant...');
        // console.log('   - Point relais sélectionné:', selectedRelayPoint.nom || selectedRelayPoint.name);
        // console.log('   - ID du point relais:', selectedRelayPoint.id);
        
        try {
          // Récupérer tous les points relais avec le service API pour vérification
          console.log('🔍 Récupération des détails du point relais via API...');
          const relayResponse = await relayAPI.getAllRelays();
          const allRelays = relayResponse.data;
          
          // console.log('📋 Tous les points relais récupérés:', allRelays.length);
          
          // Trouver le point relais sélectionné dans la liste complète
          const relayDetails = allRelays.find(relay => relay.id === selectedRelayPoint.id);
          
          if (relayDetails) {
            // console.log('✅ Point relais trouvé dans la liste complète:');
            // console.log('   - Nom:', relayDetails.nom || relayDetails.name);
            // console.log('   - Gérant:', relayDetails.gerant);
            // console.log('   - Transporteur gérant:', relayDetails.gerant?.transporteur);
            // console.log('   - Détails complets:', JSON.stringify(relayDetails, null, 2));
            
            if (relayDetails.gerant && relayDetails.gerant.transporteur) {
              selectedCarrierId = relayDetails.gerant.transporteur;
              // console.log('✅ TRANSPORTEUR DU GÉRANT DU POINT DE RELAIS:', selectedCarrierId);
              // console.log('   - Gérant:', relayDetails.gerant.first_name, relayDetails.gerant.last_name);
              // console.log('   - ID transporteur gérant:', selectedCarrierId);
            } else {
              // console.error('❌ ERREUR: Point relais sans gérant ou transporteur dans les détails API !');
              // console.error('   - Point relais:', relayDetails.nom || relayDetails.name);
              // console.error('   - Gérant existe:', !!relayDetails.gerant);
              // console.error('   - Transporteur existe:', relayDetails.gerant?.transporteur);
              
              setErrorModalData({
                title: '🏪 Point relais non configuré',
                message: `❌ Le point relais "${relayDetails.nom || relayDetails.name}" n'a pas de gérant ou de transporteur configuré.\n\n💡 Solutions :\n• Choisissez un autre point relais\n• Contactez l'administrateur pour configurer ce point relais\n\n🔍 Debug API:\n• Gérant existe: ${!!relayDetails.gerant}\n• Transporteur: ${relayDetails.gerant?.transporteur || 'Non défini'}`,
                type: 'relay_carrier_not_configured'
              });
              setShowErrorModal(true);
              toast.error('Point relais non configuré - Voir les détails dans la modal', { duration: 5000 });
              setIsCalculatingQuote(false);
              return;
            }
          } else {
            // console.error('❌ ERREUR: Point relais non trouvé dans la liste API !');
            // console.error('   - ID recherché:', selectedRelayPoint.id);
            // console.error('   - Points relais disponibles:', allRelays.map(r => ({ id: r.id, nom: r.nom || r.name })));
            
            setErrorModalData({
              title: '🏪 Point relais non trouvé',
              message: `❌ Le point relais sélectionné (ID: ${selectedRelayPoint.id}) n'a pas été trouvé dans la liste des points relais disponibles.\n\n💡 Solutions :\n• Choisissez un autre point relais\n• Actualisez la page\n• Contactez le support technique`,
              type: 'relay_carrier_not_configured'
            });
            setShowErrorModal(true);
            toast.error('Point relais non trouvé - Voir les détails dans la modal', { duration: 5000 });
            setIsCalculatingQuote(false);
            return;
          }
        } catch (error) {
          console.error('❌ ERREUR lors de la récupération des détails du point relais:', error);
          
          setErrorModalData({
            title: '🏪 Erreur de récupération',
            message: `❌ Erreur lors de la récupération des détails du point relais.\n\n💡 Solutions :\n• Vérifiez votre connexion internet\n• Actualisez la page\n• Contactez le support technique\n\n🔍 Erreur: ${error.message}`,
            type: 'relay_carrier_not_configured'
          });
          setShowErrorModal(true);
          toast.error('Erreur de récupération du point relais', { duration: 5000 });
          setIsCalculatingQuote(false);
          return;
        }
      } else if (expeditionData.shippingMode === 'home_delivery') {
        // MODE LIVRAISON À DOMICILE: Logique selon l'authentification
        console.log('🏠 Mode livraison à domicile détecté');
        console.log('   - Utilisateur authentifié:', isAuthenticated);
        console.log('   - Transporteur assigné:', assignedCarrier);
        
        if (isAuthenticated && assignedCarrier) {
          // Utilisateur connecté avec transporteur assigné
          selectedCarrierId = assignedCarrier.id;
          selectedCarrierCode = assignedCarrier.code || 'EVA';
          console.log('✅ Livraison à domicile - Transporteur assigné à l\'utilisateur:', selectedCarrierId);
        } else {
          // Utilisateur non connecté ou sans transporteur assigné → EVA LIVRAISON (ID 1)
          selectedCarrierId = 1;
          selectedCarrierCode = 'EVA';
          console.log('✅ Livraison à domicile - Transporteur par défaut EVA LIVRAISON (ID 1)');
        }
      } else {
        // Mode non reconnu ou mode point relais sans point sélectionné
        if (expeditionData.shippingMode === 'relay_point') {
          // Mode point relais mais aucun point sélectionné
        setErrorModalData({
          title: '🏪 Mode point relais requis',
          message: `❌ Pour utiliser le transporteur du gérant, vous devez sélectionner le mode "Point relais" et choisir un point de dépôt.\n\n💡 Solutions :\n• Sélectionnez le mode "Dépôt en point relais"\n• Choisissez un point relais disponible\n\n🔍 Debug:\n• Mode actuel: ${expeditionData.shippingMode}\n• Point relais sélectionné: ${!!selectedRelayPoint}`,
          type: 'relay_carrier_not_configured'
        });
        setShowErrorModal(true);
        toast.error('Mode point relais requis - Voir les détails dans la modal', { duration: 5000 });
        } else {
          // Mode non reconnu
          console.error('❌ Mode d\'expédition non reconnu:', expeditionData.shippingMode);
          setErrorModalData({
            title: '❌ Mode d\'expédition invalide',
            message: `Le mode d'expédition "${expeditionData.shippingMode}" n'est pas reconnu.`,
            type: 'invalid_shipping_mode'
          });
          setShowErrorModal(true);
          toast.error('Mode d\'expédition invalide', { duration: 5000 });
        }
        setIsCalculatingQuote(false);
        return;
      }
      
      console.log('📦 ÉTAPE 5 - Préparation des données d\'envoi:');
      console.log('   - Ville départ (sélectionnée):', departure_city);
      console.log('   - Ville destination (sélectionnée):', destination_city);
      console.log('   - Transporteur (gérant):', selectedCarrierId);
      
      const shipmentData = {
        departure_city, // ✅ Utilise uniquement la valeur sélectionnée
        destination_city, // ✅ Utilise uniquement la valeur sélectionnée
        weight: finalTotalWeight,
        length: maxLength,
        width: maxWidth,
        height: maxHeight,
        volumeCm3: totalVolumeCm3,
        serviceType,
        packageType: expeditionData.packageType || 'standard', // Type de colis
        carrierId: selectedCarrierId,
        carrierCode: selectedCarrierCode,
        declaredValue,
        isInsured,
        isManutentionRequested: expeditionData.isManutentionRequested || false, // Service de manutention
        distance: 0, // Sera calculé automatiquement par le service si coordonnées GPS disponibles
        // Coordonnées GPS pour calcul automatique de la distance
        from_latitude: fromLatitude,
        from_longitude: fromLongitude,
        to_latitude: toLatitude,
        to_longitude: toLongitude,
        isDepotRelayPoint: expeditionData.shippingMode === 'relay_point',
        isPickupRelayPoint: expeditionData.shippingMode === 'relay_point',
        isHolidayWeekend: false,
        vehicleType: expeditionData.vehicleCode || 'xs_moto' // Code véhicule exact (string) pour la tarification
      };

      // 🚚 DEBUG : Vérifier le code véhicule envoyé
      console.log('🚚 ========== VÉRIFICATION CODE VÉHICULE ==========');
      // console.log('📦 Format de colis des articles:', expeditionData.pickup_items.map(item => item.packageFormat));
      // console.log('🚗 Code véhicule envoyé à la tarification:', shipmentData.vehicleType);
      // console.log('✅ Code attendu par la tarification (ex: xs_moto, camion_leger, etc.)');
      // console.log('===============================================');
      
      // console.log('📊 ÉTAPE 6 - Appel du service de tarification (API):');
      // console.log('   - Utilisateur authentifié:', isAuthenticated);
      // console.log('   - Mode d\'expédition:', expeditionData.shippingMode);
      // console.log('   - Point relais sélectionné:', selectedRelayPoint?.nom || selectedRelayPoint?.name || 'Aucun');
      // console.log('   - Transporteur assigné:', assignedCarrier);
      // console.log('   - ID du transporteur assigné:', assignedCarrier?.id);
      // console.log('   - Données envoyées au service:', shipmentData);
      // console.log('   - Transporteur ID final:', shipmentData.carrierId);
      // console.log('   - Code transporteur:', shipmentData.carrierCode);
      // console.log('   - Logique: ', expeditionData.shippingMode === 'relay_point' ? 'Transporteur du gérant du point de relais' : (expeditionData.shippingMode === 'home_delivery' ? (isAuthenticated && assignedCarrier ? 'Transporteur assigné à l\'utilisateur' : 'EVA LIVRAISON (ID 1)') : 'Mode non reconnu'));
      
      // 🔧 Validation supplémentaire du transporteur
      if (!shipmentData.carrierId && !shipmentData.carrierCode) {
        throw new Error('Aucun transporteur disponible pour le calcul du tarif');
      }
      
      const tariffResult = await calculateTariff(shipmentData);
      
      console.log('💰 ÉTAPE 7 - Résultat du service de tarification (API):');
      // console.log('   - Grille tarifaire:', tariffResult.details?.tariffGrid?.name || 'Non définie');
      // console.log('   - Prix de base:', (tariffResult.breakdown?.basePrice || 0).toFixed(0), 'FCFA');
      // console.log('   - Tarif poids:', (tariffResult.breakdown?.weightTariff || 0).toFixed(0), 'FCFA');
      // console.log('   - Tarif volume:', (tariffResult.breakdown?.volumeTariff || 0).toFixed(0), 'FCFA');
      // console.log('   - Tarif distance:', (tariffResult.breakdown?.distanceTariff || 0).toFixed(0), 'FCFA');
      // console.log('   - Prix final:', (tariffResult.finalPrice || 0).toFixed(0), 'FCFA');
      
      // 7️⃣ Appliquer les ajustements spéciaux
      let finalPrice = tariffResult.finalPrice;
      
      console.log('🎯 ÉTAPE 8 - Ajustements spéciaux:');
      console.log('   - Prix avant ajustements:', finalPrice.toFixed(0), 'FCFA');
      
      // Ajustement point relais
      if (isRelayPoint) {
        finalPrice += relayPointFee;
        console.log('   - Après réduction point relais:', finalPrice.toFixed(0), 'FCFA');
      }
      // Frais de weekend/jour férié (optionnel)
      const today = new Date();
      const isWeekend = today.getDay() === 0 || today.getDay() === 6;
      if (isWeekend) {
        const weekendFee = 500; // Frais de weekend
        finalPrice += weekendFee;
        console.log('   - Après frais weekend:', finalPrice.toFixed(0), 'FCFA');
      }
      
      // S'assurer que le prix final est positif
      finalPrice = Math.max(finalPrice, 100); // Minimum 100 FCFA
      
      console.log('✅ PRIX FINAL CALCULÉ:', finalPrice.toFixed(0), 'FCFA');
      console.log('==========================================');
      
      // Mettre à jour le résultat avec le prix final ajusté
      const result = {
        ...tariffResult,
        finalPrice: finalPrice,
        // 🆕 NOUVEAU : Ajouter le breakdown avec les données de tarification spécifiques
        breakdown: {
          ...tariffResult.breakdown,
          // Données de tarification spécifiques
          tarifLivreur: tariffResult.breakdown?.tarifLivreur || 0,
          tariffCompagni: tariffResult.breakdown?.tariffCompagni || 0,
          tarifAide: tariffResult.breakdown?.tarifAide || 0
        }
      };
      
      setQuoteData(result);
      
      // 🎁 Initialiser les prix pour la gestion des codes promo
      const originalAmount = result.finalPrice || 0;
      setOriginalPrice(originalAmount);
      setFinalPrice(originalAmount);
      setDiscountAmount(0);
      
      setShowQuoteModal(true);
      
      toast.success('Devis calculé avec succès !');
      
    } catch (error) {
      console.error('Erreur lors du calcul de tarification:', error);
      
      // Gestion spécifique des erreurs de transporteur non configuré
      if (error.message && error.message.includes('Transporteur non configuré')) {
        // Déterminer le type d'erreur selon le contexte
        let errorType = 'carrier_not_configured';
        let errorTitle = '🚚 Transporteur non configuré';
        
        // Si c'est un point relais, ajuster le message
        if (expeditionData.shippingMode === 'relay_point' && selectedRelayPoint) {
          errorType = 'relay_carrier_not_configured';
          errorTitle = '🏪 Transporteur du point relais non configuré';
        }
        
        // Afficher un modal d'erreur esthétique
        setErrorModalData({
          title: errorTitle,
          message: error.message,
          type: errorType
        });
        setShowErrorModal(true);
        
        // Aussi afficher un toast pour confirmation
        toast.error('Transporteur non configuré - Voir les détails dans la modal', { duration: 5000 });
      }
      // Gestion spécifique des erreurs de grille tarifaire
      else if (error.message && (error.message.includes('Aucune grille tarifaire') || error.message.includes('n\'est pas encore configuré'))) {
        // Afficher un modal d'erreur esthétique
        setErrorModalData({
          title: '🚨 Configuration manquante',
          message: error.message,
          type: 'configuration'
        });
        setShowErrorModal(true);
        
        // Aussi afficher un toast pour confirmation
        toast.error('Configuration manquante - Voir les détails dans la modal', { duration: 5000 });
      } 
      // Gestion spécifique des erreurs de trajet non disponible
      else if (error.message && (error.message.includes('Service non disponible pour ce trajet') || error.message.includes('Ce trajet n\'existe pas'))) {
        // Afficher un modal d'erreur esthétique
        setErrorModalData({
          title: '🚨 Trajet non disponible',
          message: error.message,
          type: 'route_unavailable'
        });
        setShowErrorModal(true);
        
        // Aussi afficher un toast pour confirmation
        toast.error('Trajet non disponible - Voir les détails dans la modal', { duration: 5000 });
      } 
      else {
        toast.error('Erreur lors du calcul de tarification. Veuillez réessayer.');
      }
    } finally {
      setIsCalculatingQuote(false);
    }
  };

  // 🔄 Charger les données au montage
  useEffect(() => {
    loadCarriers();
  }, [loadCarriers]);

  // Fonction utilitaire pour normaliser les chaînes (insensible à la casse, accents et espaces)
  const normalizeString = (str) => {
    if (!str) return '';
    return str
      .toLowerCase()
      .normalize('NFD') // Décompose les caractères accentués
      .replace(/[\u0300-\u036f]/g, '') // Supprime les accents
      .trim() // Supprime les espaces en début et fin
      .replace(/\s+/g, ' '); // Normalise les espaces multiples
  };

  // Filtrage des points relais selon le type de colis ET la destination
  const filteredRelayPoints = relayPoints.filter(relay => {
    // 1. Filtre par type de colis
    const typeMatch = relay.acceptedTypes && relay.acceptedTypes.includes(expeditionData.packageType || 'standard');
    
    // 2. Filtre par destination selon le type de service
    let destinationMatch = true;
    
    if (expeditionData.destination_city) {
      const normalizedDestination = normalizeString(expeditionData.destination_city);
      
      if (expeditionData.type_service === 'interurbaine' || expeditionData.type_service === 'sous_regionale') {
        // Service interurbaine et sous-régionale : filtrer par ville
        const relayCity = relay.city || relay.ville || '';
        const normalizedRelayCity = normalizeString(relayCity);
        destinationMatch = normalizedRelayCity.includes(normalizedDestination) || normalizedDestination.includes(normalizedRelayCity);
      } else {
        // Autres services : filtrer par village/commune
        const relayVillage = relay.village || relay.vilage || '';
        const normalizedRelayVillage = normalizeString(relayVillage);
        destinationMatch = normalizedRelayVillage.includes(normalizedDestination) || normalizedDestination.includes(normalizedRelayVillage);
      }
    }
    
    return typeMatch && destinationMatch;
  });

  // Charger les points relais au montage
  useEffect(() => {
    loadRelayPoints();
  }, []);

  // Recharger les points relais quand la destination change (pour le filtrage)
  useEffect(() => {
    if (expeditionData.destination_city && expeditionData.shippingMode === 'relay_point') {
      console.log('🔄 Destination changée, filtrage des points relais...');
      console.log('📍 Destination:', expeditionData.destination_city);
      console.log('🚚 Type de service:', expeditionData.type_service);
      console.log('🏪 Points relais filtrés:', filteredRelayPoints.length);
    }
  }, [expeditionData.destination_city, expeditionData.type_service, expeditionData.shippingMode, filteredRelayPoints.length]);


  // Clé API Google Maps
  const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'AIzaSyAuZ70BPg_cJ01v626EvSEkHlOetRFZGXM';

  // Géocoder les coordonnées GPS en adresse avec Google Maps
  const reverseGeocode = React.useCallback(async (lat, lng) => {
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${GOOGLE_MAPS_API_KEY}&language=fr&region=ci`
      );
      const data = await response.json();
      
      if (data.status === 'OK' && data.results && data.results.length > 0) {
        return data.results[0];
      }
      return null;
    } catch (error) {
      console.error('Erreur lors du reverse geocoding:', error);
      return null;
    }
  }, [GOOGLE_MAPS_API_KEY]);

  // Fonction pour obtenir la position actuelle
  const getCurrentLocation = (type) => {
    if (!navigator.geolocation) {
      toast.error('Géolocalisation non supportée par votre navigateur');
      return;
    }

    // Afficher un toast de chargement
    const loadingToast = toast.loading('🔄 Récupération de votre position GPS...');
    
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude.toFixed(6);
        const lng = position.coords.longitude.toFixed(6);
        
        console.log('📍 Position GPS obtenue:', { lat, lng, type });
        
        // Fermer le toast de chargement
        toast.dismiss(loadingToast);
        
        // Obtenir l'adresse complète via Google Maps
        const loadingAddressToast = toast.loading('🔍 Recherche de l\'adresse...');
        const placeData = await reverseGeocode(lat, lng);
        toast.dismiss(loadingAddressToast);
        
        if (type === 'from') {
          updateExpeditionData('from_latitude', lat);
          updateExpeditionData('from_longitude', lng);
          
          // Si on a réussi à obtenir l'adresse
          if (placeData) {
            updateExpeditionData('from_address', placeData.formatted_address);
            toast.success(`✅ Adresse de ramassage: ${placeData.formatted_address}`, { duration: 5000 });
          } else {
            updateExpeditionData('from_address', `${lat}, ${lng}`);
            toast.success(`✅ Position récupérée: ${lat}, ${lng}`, { duration: 5000 });
          }
        } else if (type === 'to') {
          updateExpeditionData('to_latitude', lat);
          updateExpeditionData('to_longitude', lng);
          
          // Si on a réussi à obtenir l'adresse
          if (placeData) {
            updateExpeditionData('to_address', placeData.formatted_address);
            toast.success(`✅ Adresse de livraison: ${placeData.formatted_address}`, { duration: 5000 });
          } else {
            updateExpeditionData('to_address', `${lat}, ${lng}`);
            toast.success(`✅ Position récupérée: ${lat}, ${lng}`, { duration: 5000 });
          }
        }
      },
      (error) => {
        console.error('Erreur géolocalisation:', error);
        toast.dismiss(loadingToast);
        
        let errorMessage = 'Impossible d\'obtenir votre position GPS';
        switch(error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Permission de géolocalisation refusée. Activez-la dans les paramètres.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Position GPS indisponible. Vérifiez votre connexion.';
            break;
          case error.TIMEOUT:
            errorMessage = 'Délai d\'attente dépassé pour la géolocalisation.';
            break;
          default:
            errorMessage = 'Erreur lors de la récupération de la position GPS.';
            break;
        }
        
        toast.error(`⚠️ ${errorMessage}`, { duration: 5000 });
      },
      {
        enableHighAccuracy: true, // Utiliser la meilleure précision disponible
        timeout: 15000, // Timeout de 15 secondes
        maximumAge: 0 // Ne pas utiliser de position en cache
      }
    );
  };

  // Fonction pour mettre à jour les coordonnées GPS selon le mode d'expédition
  const updateCoordinatesForMode = React.useCallback(() => {
    if (expeditionData.shippingMode === 'relay_point' && selectedRelayPoint) {
      // Mode point relais : utiliser les coordonnées du point relais sélectionné
      updateExpeditionData('to_latitude', selectedRelayPoint.latitude?.toString() || '');
      updateExpeditionData('to_longitude', selectedRelayPoint.longitude?.toString() || '');
      console.log('📍 Coordonnées GPS mises à jour avec celles du point relais:', selectedRelayPoint.latitude, selectedRelayPoint.longitude);
    } else if (expeditionData.shippingMode === 'home_delivery') {
      // Mode livraison à domicile : utiliser la géolocalisation si disponible
      if (useMyLocationDelivery) {
        getCurrentLocation('to');
      }
    }
  }, [expeditionData.shippingMode, selectedRelayPoint, useMyLocationDelivery, getCurrentLocation]);

  // Mettre à jour les coordonnées GPS selon le mode d'expédition
  useEffect(() => {
    updateCoordinatesForMode();
  }, [expeditionData.shippingMode, selectedRelayPoint, useMyLocationDelivery, updateCoordinatesForMode]);

  // Pré-remplissage automatique des infos expéditeur avec l'utilisateur connecté
  useEffect(() => {
    const userData = getUserData();
    if (userData) {
      setExpeditionData(prev => ({
        ...prev,
        customer_first_name: userData.firstName || userData.first_name || '',
        customer_last_name: userData.lastName || userData.last_name || '',
        customer_email: userData.email || '',
        customer_phone_number: userData.phone || '+225'
      }));
      // Pré-remplir le téléphone de paiement si disponible
      if (!paymentPhoneNumber && (userData.phone || '').startsWith('+')) {
        const digits = (userData.phone || '').replace(/\D/g, '');
        // suppose +225XXXXXXXX
        if (digits.length >= 11) {
          setPaymentCountryCode('+225');
          setPaymentPhoneNumber(digits.slice(-8));
        }
      }
    }
  }, [user, getUserData, paymentPhoneNumber]);

  useEffect(() => {
    // 📱 Mettre à jour expeditionData avec seulement le numéro local (sans code pays)
    // Comme dans ClientSendParcel.js
    updateExpeditionData('customer_phone_number', customerPhoneNumberLocal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerPhoneNumberLocal, customerPhoneCountryCode]);

  useEffect(() => {
    // 📱 Mettre à jour expeditionData avec seulement le numéro local (sans code pays)
    // Comme dans ClientSendParcel.js
    updateExpeditionData('recipient_phone', pickupPhoneNumberLocal);
    updateExpeditionData('pickup_phone_number', pickupPhoneNumberLocal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickupPhoneNumberLocal, recipientPhoneCountryCode]);

  // 📱 Synchroniser pickupPhoneNumberLocal depuis expeditionData.recipient_phone (pour point relais, etc.)
  useEffect(() => {
    if (expeditionData.recipient_phone && !pickupPhoneNumberLocal) {
      // Extraire le numéro local du numéro complet (enlever le code pays)
      const phone = String(expeditionData.recipient_phone || '');
      const countryCodePattern = /^\+\d{1,4}/;
      const match = phone.match(countryCodePattern);
      
      if (match) {
        const code = match[0];
        const localNumber = phone.substring(code.length).trim();
        
        // Mettre à jour le code pays si nécessaire
        if (code !== recipientPhoneCountryCode) {
          setRecipientPhoneCountryCode(code);
        }
        
        // Mettre à jour le numéro local seulement s'il est vide
        if (localNumber && !pickupPhoneNumberLocal) {
          setPickupPhoneNumberLocal(localNumber);
        }
      } else if (phone && !pickupPhoneNumberLocal) {
        // Si pas de code pays, utiliser le numéro tel quel
        setPickupPhoneNumberLocal(phone);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expeditionData.recipient_phone]);

  // Fonctions utilitaires
  const updateExpeditionData = (field, value) => {
    setExpeditionData(prev => ({
      ...prev,
      [field]: value,
      // Recalculer le véhicule si les items changent
      ...(field === 'pickup_items' && { vehicle_type_id: calculateVehicleType() })
    }));
  };

  // Calculer automatiquement le type de véhicule selon le poids total
  const calculateVehicleType = () => {
    console.log('🔄 ============ DÉBUT CALCUL VÉHICULE ============');
    console.log('📦 Articles actuels:', expeditionData.pickup_items);
    
    const totalWeight = expeditionData.pickup_items.reduce((sum, item) => {
      const itemWeight = parseFloat(item.weight) * parseInt(item.quantity);
      console.log(`  📦 ${item.name}: ${item.weight}kg × ${item.quantity} = ${itemWeight}kg`);
      return sum + itemWeight;
    }, 0);

    console.log('⚖️ Poids total:', totalWeight, 'kg');
    
    // Chercher le véhicule approprié selon le poids exact
    console.log('🔍 ============ SÉLECTION VÉHICULE ============');
    for (const vehicle of VEHICLE_TYPES) {
      const canCarry = totalWeight >= vehicle.min_weight && totalWeight < vehicle.max_weight;
      console.log(`${vehicle.icon} ${vehicle.name}: ${vehicle.min_weight}-${vehicle.max_weight}kg → ${canCarry ? '✅' : '❌'} (${totalWeight}kg)`);
      
      if (canCarry) {
        console.log(`✅ VÉHICULE SÉLECTIONNÉ: ${vehicle.icon} ${vehicle.name}`);
        console.log(`📊 Poids: ${totalWeight}kg (plage: ${vehicle.min_weight}-${vehicle.max_weight}kg)`);
        console.log(`💰 Tarif: ${vehicle.tariff} unités`);
        console.log('==========================================');
        return vehicle.id;
      }
    }
    
    // Fallback : si le poids dépasse le maximum, prendre le plus gros véhicule
    if (totalWeight >= 40000) {
      console.log('⚠️ Poids très élevé, sélection du plus gros véhicule');
      return 14; // Camion complet
    }
    
    // Fallback par défaut
    console.log('⚠️ Fallback: Véhicule par défaut (XS Moto)');
    return 1; // XS Moto par défaut
  };

  // 🛡️ Fonction pour vérifier si l'assurance est obligatoire
  const isInsuranceMandatory = (packageType) => {
    return packageType === 'securise' || packageType === 'gros_volume';
  };

  // Ajouter un article
  const addItem = () => {
    if (!currentItem.name || !currentItem.packageFormat) {
      setEvaError('Veuillez remplir au minimum le nom et le format de colis de l\'article');
      toast.error('⚠️ Veuillez remplir au minimum le nom et le format de colis de l\'article');
      return;
    }

    // Validation spéciale pour les formats qui nécessitent une saisie manuelle
    const selectedFormat = packageFormats.find(format => format.id === currentItem.packageFormat);
    if (selectedFormat && selectedFormat.isCustom) {
      if (!currentItem.weight || !currentItem.length || !currentItem.width || !currentItem.height) {
        setEvaError('Pour les formats hors gabarit, veuillez saisir le poids et toutes les dimensions de l\'article');
        toast.error('⚠️ Pour les formats hors gabarit, veuillez saisir le poids et toutes les dimensions de l\'article');
      return;
      }
    }

    // 🚗 Validation de cohérence des catégories de véhicules
    if (expeditionData.pickup_items.length > 0) {
      const existingItem = expeditionData.pickup_items[0];
      const existingFormat = packageFormats.find(format => format.id === existingItem.packageFormat);
      
      if (existingFormat && selectedFormat) {
        // Déterminer la catégorie effective de manière bidirectionnelle
        const getEffectiveCategory = (format, selectedCategory) => {
          // Si on est sur l'onglet "Car", on accepte Moto et Fourgon
          if (selectedCategory === 'car') {
            return 'car';
          }
          // Sinon, utiliser la catégorie du format
          return format.category;
        };
        
        // Déterminer les catégories effectives
        const existingCategory = getEffectiveCategory(existingFormat, currentItem.selectedCategory);
        const newCategory = getEffectiveCategory(selectedFormat, currentItem.selectedCategory);
        
        // Vérifier si les catégories de véhicules sont différentes
        if (existingCategory !== newCategory) {
          const vehicleNames = {
            'moto': 'Moto',
            'fourgon': 'Fourgon',
            'camion': 'Camion',
            'car': 'Car'
          };
          
          setEvaError(`Vous ne pouvez pas mélanger des formats de véhicules différents. L'article existant utilise un format ${vehicleNames[existingCategory]} et vous essayez d'ajouter un format ${vehicleNames[newCategory]}.`);
          toast.error(`⚠️ Formats de véhicules incompatibles !\n\nL'article existant utilise un format ${vehicleNames[existingCategory]} et vous essayez d'ajouter un format ${vehicleNames[newCategory]}.\n\nVeuillez utiliser le même type de véhicule pour tous les articles.`);
          return;
        }
      }
    }

    // 🚛 Validation du poids par type de véhicule (basé sur le poids max de chaque catégorie)
    const getVehicleWeightLimit = (category) => {
      if (category === 'car') {
        // Pour l'onglet "Car", prendre le max entre moto et fourgon
        const motoMax = Math.max(...packageFormats.filter(f => f.category === 'moto').map(f => f.weightMax));
        const fourgonMax = Math.max(...packageFormats.filter(f => f.category === 'fourgon').map(f => f.weightMax));
        return Math.max(motoMax, fourgonMax);
      } else {
        // Pour les autres catégories, prendre le poids max des formats de cette catégorie
        const categoryFormats = packageFormats.filter(f => f.category === category);
        return Math.max(...categoryFormats.map(f => f.weightMax));
      }
    };

    const vehicleWeightLimit = getVehicleWeightLimit(currentItem.selectedCategory);

    // Calculer le poids total actuel pour le type de véhicule sélectionné
    const currentTotalWeight = expeditionData.pickup_items.reduce((sum, item) => {
      const itemFormat = packageFormats.find(format => format.id === item.packageFormat);
      if (itemFormat) {
        const getEffectiveCategory = (format, selectedCategory) => {
          if (selectedCategory === 'car') {
            return 'car';
          }
          return format.category;
        };
        
        const itemCategory = getEffectiveCategory(itemFormat, currentItem.selectedCategory);
        const currentCategory = getEffectiveCategory(selectedFormat, currentItem.selectedCategory);
        
        // Ne compter que les articles du même type de véhicule
        if (itemCategory === currentCategory) {
          return sum + (parseFloat(item.weight) * parseInt(item.quantity));
        }
      }
      return sum;
    }, 0);

    // Calculer le poids du nouvel article
    const newItemWeight = parseFloat(currentItem.weight) * parseInt(currentItem.quantity);
    const totalWeightAfterAdd = currentTotalWeight + newItemWeight;

    // Vérifier si le poids total dépasse la limite du véhicule
    if (totalWeightAfterAdd > vehicleWeightLimit) {
      const vehicleNames = {
        'moto': 'Moto',
        'fourgon': 'Fourgon',
        'camion': 'Camion',
        'car': 'Car'
      };

      // Proposer des alternatives
      let alternatives = [];
      if (currentItem.selectedCategory === 'moto') {
        alternatives = ['Fourgon', 'Camion'];
      } else if (currentItem.selectedCategory === 'fourgon') {
        alternatives = ['Camion'];
      } else if (currentItem.selectedCategory === 'car') {
        alternatives = ['Camions'];
      }

      const alternativesText = alternatives.length > 0 ? `\n\nVeuillez considérer utiliser un véhicule ${alternatives.join(' ou ')} pour ce poids.` : '';

      setEvaError(`Le poids total (${totalWeightAfterAdd.toFixed(1)}kg) dépasse la limite autorisée pour ${vehicleNames[currentItem.selectedCategory]} (${vehicleWeightLimit}kg).${alternativesText}`);
      toast.error(`⚠️ Limite de poids dépassée !\n\nLe poids total (${totalWeightAfterAdd.toFixed(1)}kg) dépasse la limite autorisée pour ${vehicleNames[currentItem.selectedCategory]} (${vehicleWeightLimit}kg).${alternativesText}`);
      return;
    }

    // Utiliser les poids et volumes fixes pour la tarification
    const weightForCalculation = selectedFormat ? selectedFormat.weightFixed : (parseFloat(currentItem.weight) || 0);
    const volumeForCalculation = selectedFormat ? selectedFormat.volumeFixed : (parseFloat(currentItem.length) * parseFloat(currentItem.width) * parseFloat(currentItem.height) || 0);

    const newItem = {
      ...currentItem,
      // Utiliser les valeurs du format sélectionné si disponible, sinon les valeurs saisies
      weight: selectedFormat && !selectedFormat.isCustom ? selectedFormat.weightFixed : (parseFloat(currentItem.weight) || 0),
      length: selectedFormat && !selectedFormat.isCustom ? selectedFormat.dimensions.length : (parseFloat(currentItem.length) || 0),
      width: selectedFormat && !selectedFormat.isCustom ? selectedFormat.dimensions.width : (parseFloat(currentItem.width) || 0),
      height: selectedFormat && !selectedFormat.isCustom ? selectedFormat.dimensions.height : (parseFloat(currentItem.height) || 0),
      quantity: parseInt(currentItem.quantity),
      // Ajouter les valeurs pour le calcul de tarification
      weightForCalculation: weightForCalculation,
      volumeForCalculation: volumeForCalculation
    };

    // Vérifier la limite de poids total (40000kg)
    const globalTotalWeight = expeditionData.pickup_items.reduce((sum, item) => {
      const itemWeight = parseFloat(item.weight) * parseInt(item.quantity);
      return sum + itemWeight;
    }, 0);
    
    const globalNewItemWeight = parseFloat(newItem.weight) * parseInt(newItem.quantity);
    const newTotalWeight = globalTotalWeight + globalNewItemWeight;
    
    if (newTotalWeight > 40000) {
      setEvaError('⚠️ Le poids total ne peut pas dépasser 40000kg. Nous ne prenons pas en compte les poids au-delà de 40000kg.');
      // Afficher un toast
      toast.error('⚠️ Limite de poids dépassée ! Le poids total ne peut pas dépasser 40000kg.');
      return;
    }

    const updatedItems = [...expeditionData.pickup_items, newItem];
    
    // Calculer le nouveau type de véhicule AVEC les nouveaux items
    const calculateNewVehicleType = () => {
      const totalWeight = updatedItems.reduce((sum, item) => {
        const itemWeight = parseFloat(item.weight) * parseInt(item.quantity);
        return sum + itemWeight;
      }, 0);

      console.log('🔄 ============ CALCUL VÉHICULE AVEC NOUVEAUX ITEMS ============');
      console.log('📦 Nouveaux items:', updatedItems);
      console.log('⚖️ Nouveau poids total:', totalWeight, 'kg');
      
      // Chercher le véhicule approprié selon le poids
      for (const vehicle of VEHICLE_TYPES) {
        const canCarry = totalWeight >= vehicle.min_weight && totalWeight < vehicle.max_weight;
        console.log(`${vehicle.icon} ${vehicle.name}: ${vehicle.min_weight}-${vehicle.max_weight}kg → ${canCarry ? '✅' : '❌'} (${totalWeight}kg)`);
        
        if (canCarry) {
          console.log(`✅ VÉHICULE SÉLECTIONNÉ: ${vehicle.icon} ${vehicle.name}`);
          return vehicle.id;
        }
      }
      
      // Fallback
      if (totalWeight <= 10) return 3;
      if (totalWeight < 51) return 2;
      if (totalWeight < 201) return 1;
      if (totalWeight < 601) return 5;
      if (totalWeight < 2001) return 4;
      return 7;
    };
    
    const newVehicleType = calculateNewVehicleType();
    
    // Mettre à jour les items ET le véhicule en une seule fois
    updateExpeditionData('pickup_items', updatedItems);
    updateExpeditionData('vehicle_type_id', newVehicleType);

    // Reset le formulaire d'article
    setCurrentItem({
      name: '',
      category: 'Général',
      packageFormat: '',
      selectedCategory: currentItem.selectedCategory, // Conserver la catégorie sélectionnée
      showFormats: false, // Fermer la liste des formats
      weight: '',
      length: '',
      width: '',
      height: '',
      quantity: 1,
      description: ''
    });
    setEvaError(null);
  };

  const removeItem = (index) => {
    setExpeditionData(prev => ({
      ...prev,
      pickup_items: prev.pickup_items.filter((_, i) => i !== index)
    }));
  };

  // Déterminer automatiquement le véhicule selon le poids total des articles
  const getVehicleIdForWeight = React.useCallback((totalWeight) => {
    if (totalWeight == null || isNaN(totalWeight)) return expeditionData.vehicle_type_id;
    // Parcours des types définis
    for (const v of VEHICLE_TYPES) {
      const minW = Number(v.min_weight) || 0;
      const maxW = Number(v.max_weight);
      if (typeof maxW === 'number' && isFinite(maxW)) {
        if (totalWeight >= minW && totalWeight < maxW) return v.id;
      } else {
        // Pas de borne supérieure finie
        if (totalWeight >= minW) return v.id;
      }
    }
    // Fallback sur véhicule actuel si aucun match
    return expeditionData.vehicle_type_id;
  }, [expeditionData.vehicle_type_id]);

  useEffect(() => {
    // Recalcul du poids total à chaque changement d'articles
    const totalWeight = (expeditionData.pickup_items || []).reduce((sum, item) => {
      const w = parseFloat(item?.weight);
      const q = parseInt(item?.quantity) || 1;
      return sum + (isNaN(w) ? 0 : w) * q;
    }, 0);

    const targetVehicleId = getVehicleIdForWeight(totalWeight);
    if (targetVehicleId && targetVehicleId !== expeditionData.vehicle_type_id) {
      updateExpeditionData('vehicle_type_id', targetVehicleId);
    }
  }, [expeditionData.pickup_items, expeditionData.vehicle_type_id, getVehicleIdForWeight]);

  // 🌍 useEffect pour initialiser les codes pays selon les numéros existants (uniquement au chargement initial)
  useEffect(() => {
    // Initialiser le code pays client une seule fois si un numéro complet existe et qu'on ne l'a pas encore fait
    if (!hasInitializedCustomerCode.current && expeditionData.customer_phone_number) {
      const countryCodePattern = /^\+\d{1,4}/;
      const match = expeditionData.customer_phone_number.match(countryCodePattern);
      if (match && match[0] !== customerPhoneCountryCode) {
        setCustomerPhoneCountryCode(match[0]);
        hasInitializedCustomerCode.current = true;
      }
    }
    // Initialiser le code pays destinataire une seule fois si un numéro complet existe et qu'on ne l'a pas encore fait
    if (!hasInitializedRecipientCode.current && expeditionData.recipient_phone) {
      const countryCodePattern = /^\+\d{1,4}/;
      const match = expeditionData.recipient_phone.match(countryCodePattern);
      if (match && match[0] !== recipientPhoneCountryCode) {
        setRecipientPhoneCountryCode(match[0]);
        hasInitializedRecipientCode.current = true;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expeditionData.customer_phone_number, expeditionData.recipient_phone]);

  // Validation silencieuse (sans toasts) pour la navigation
  const validateStep = (step) => {
    switch (step) {
      case 1:
        if (!expeditionData.type_service) return false;
        if (!expeditionData.customer_first_name) return false;
        if (!expeditionData.customer_last_name) return false;
        if (!expeditionData.customer_phone_number) return false;
        if (!expeditionData.departure_city) return false;
        if (!expeditionData.destination_city) return false;
        if (!expeditionData.from_address) return false;
        if (!expeditionData.to_address) return false;
        if (!expeditionData.recipient_first_name) return false;
        // Vérifier que le téléphone du destinataire est rempli et contient au moins un chiffre après le code pays
        if (!expeditionData.recipient_phone || expeditionData.recipient_phone.trim() === '' || expeditionData.recipient_phone.replace(/^\+\d{1,4}/, '').replace(/\D/g, '').length === 0) return false;
        return true;
      case 2:
        if (expeditionData.pickup_items.length === 0) return false;
        return true;
      case 3:
        if (!quoteData) return false;
        return true;
      case 4:
        if (!selectedPaymentMethod) return false;
        if (!paymentPhoneNumber) return false;
        return true;
      default:
        return true;
    }
  };

  // Validation avec toasts pour la soumission
  const validateStepWithToasts = (step) => {
    switch (step) {
      case 1:
        // Validation détaillée avec messages spécifiques
        if (!expeditionData.type_service) {
          toast.error('⚠️ Type de service requis');
          return false;
        }
        if (!expeditionData.customer_first_name) {
          toast.error('⚠️ Prénom requis');
          return false;
        }
        if (!expeditionData.customer_last_name) {
          toast.error('⚠️ Nom requis');
          return false;
        }
        if (!expeditionData.customer_phone_number) {
          toast.error('⚠️ Téléphone requis');
          return false;
        }
        if (!expeditionData.departure_city) {
          toast.error('⚠️ Ville/Commune de départ requise');
          return false;
        }
        if (!expeditionData.destination_city) {
          toast.error('⚠️ Ville/Commune de destination requise');
          return false;
        }
        if (!expeditionData.from_address) {
          toast.error('⚠️ Adresse de ramassage requise');
          return false;
        }
        if (!expeditionData.to_address) {
          toast.error('⚠️ Adresse de livraison requise');
          return false;
        }
        if (!expeditionData.recipient_first_name) {
          toast.error('⚠️ Prénom du destinataire requis');
          return false;
        }
        // Vérifier que le téléphone du destinataire est rempli et valide
        if (!expeditionData.recipient_phone || expeditionData.recipient_phone.trim() === '' || expeditionData.recipient_phone.replace(/^\+\d{1,4}/, '').replace(/\D/g, '').length === 0) {
          toast.error('⚠️ Téléphone du destinataire requis');
          return false;
        }
        return true;
      case 2:
        if (expeditionData.pickup_items.length === 0) {
          toast.error('⚠️ Au moins un article requis');
          return false;
        }
        return true;
      case 3:
        if (!quoteData) {
          toast.error('⚠️ Devis requis - Calculez d\'abord le devis');
          return false;
        }
        return true;
      case 4:
        if (!selectedPaymentMethod) {
          toast.error('⚠️ Mode de paiement requis');
          return false;
        }
        if (!paymentPhoneNumber) {
          toast.error('⚠️ Numéro de téléphone pour paiement requis');
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const handleNextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => {
        const next = prev + 1;
        // Auto-chargement des modes de paiement à l'arrivée sur l'étape 4
        if (next === 4 && !isLoadingPaymentMethods) {
          loadPaymentMethods();
        }
        // Scroll automatique vers le haut de la page après le changement d'étape
        scrollToTop();
        return next;
      });
    } else {
      // Utiliser la validation avec toasts pour afficher les erreurs spécifiques
      validateStepWithToasts(currentStep);
    }
  };

  const handlePrevStep = () => {
    setCurrentStep(prev => Math.max(1, prev - 1));
    // Scroll automatique vers le haut de la page
    scrollToTop();
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };



  // 🎯 Fonction pour détecter automatiquement le type de service DÉSACTIVÉE
  // L'utilisateur doit maintenant choisir manuellement le type de service
  // const detectServiceType = (originAddress, destinationAddress) => {
  //   // ... fonction commentée car non utilisée
  // };

  // 🌍 Détection pays via IP (simplifié)
  const detectUserCountry = async () => {
    try {
      const response = await fetch('https://ipapi.co/json/');
      if (response.ok) {
        const data = await response.json();
        const detected = data.country_code;
        const SUPPORTED = ['CI','KE','BF','GN','GA','ML','SN','BJ','CM'];
        return SUPPORTED.includes(detected) ? detected : 'CI';
      }
    } catch (e) {
      // ignore and fallback
    }
    return 'CI';
  };

  // 💳 Charger les modes de paiement (Clapay)
  const getPaymentIcon = (operator) => {
    if (!operator) return '/OM.png';
    const op = operator.toLowerCase();
    if (op.includes('wave')) return '/WAVE.png';
    if (op.includes('orange') || op.includes('om')) return '/OM.png';
    if (op.includes('mtn')) return '/MTN.png';
    if (op.includes('moov')) return '/MOOV.png';
    if (op.includes('free')) return '/WAVE.png';
    if (op.includes('emoney')) return '/WAVE.png';
    if (op.includes('freemoney')) return '/WAVE.png';
    if (op.includes('airtel')) return '/MTN.png';
    if (op.includes('visa')) return '/OM.png';
    if (op.includes('mastercard')) return '/OM.png';
    return '/OM.png';
  };

  const getPaymentType = (operator) => {
    if (!operator) return 'autre';
    const op = operator.toLowerCase();
    if (op.includes('wave') || op.includes('orange') || op.includes('mtn') || op.includes('moov') || op.includes('free') || op.includes('emoney') || op.includes('freemoney') || op.includes('airtel')) return 'mobile_money';
    if (op.includes('visa') || op.includes('mastercard')) return 'card';
    return 'autre';
  };

  // Mapper: indicatif téléphonique -> code pays ISO (CI, CM, BF, ...)
  const mapDialCodeToIso = (dialCode) => {
    switch (dialCode) {
      case '+225': return 'CI';
      case '+237': return 'CM';
      case '+226': return 'BF';
      case '+224': return 'GN';
      case '+241': return 'GA';
      case '+223': return 'ML';
      case '+221': return 'SN';
      case '+229': return 'BJ';
      default: return 'CI';
    }
  };

  // Mapper: nom opérateur -> code opérateur (OM, MOOV, MTN, WAVE)
  const mapOperatorToCode = (name) => {
    if (!name) return 'OM';
    const n = name.toString().toLowerCase();
    if (n.includes('wave')) return 'WAVE';
    if (n.includes('moov')) return 'MOOV';
    if (n.includes('mtn')) return 'MTN';
    if (n.includes('orange') || n.includes('om')) return 'OM';
    return 'OM';
  };

  const loadPaymentMethods = async () => {
    setIsLoadingPaymentMethods(true);
    try {
      const userCountry = await detectUserCountry();
      const countryCode = userCountry; // déjà code ISO
      const clapayUrl = `https://nowallet-api.mpayment.africa/nowallet/api/fees/by/country?country=${countryCode}`;
      const response = await fetch(clapayUrl, {
        method: 'GET',
        headers: {
          accept: 'application/json',
          Authorization: 'Bearer d41c1b19dce70da75aa3701887dff1aa14f2b7e082a700b2aa94e1427d7ec3e01b8e83090e89e41430161d46e2fbbbaa4c048285625d6dbacafbc8c6efaf5cd674d2e0b3df0181a6492abfd3f9e560b0707a89bb98dfe542f0aea0e59908763b',
        },
      });
      if (!response.ok) throw new Error(`Erreur API Clapay: ${response.status}`);
      const clapayData = await response.json();
      if (Array.isArray(clapayData)) {
        const transformed = clapayData.map((method, index) => ({
          id: method.id || index + 1,
          nom: method.operator || method.description || 'Méthode de paiement',
          operatorCode: (method.operators_code || method.operator || method.code || '').toString().toUpperCase(),
          icone: getPaymentIcon(method.operator),
          description: method.description || `${method.operator} - ${method.currency}`,
          montant_min: 0,
          montant_max: 999999999,
          type: getPaymentType(method.operator),
          clapayData: {
            fee_cashin: method.fee_cashin,
            fee_cashout: method.fee_cashout,
            fee_merchant: method.fee_merchant,
            currency: method.currency,
            country: method.country,
            merchant: method.merchant,
          },
        }));
        setPaymentMethods(transformed);
        if (transformed.length > 0 && !selectedPaymentMethod) setSelectedPaymentMethod(transformed[0]);
        setPaymentMethodsError(null);
      } else {
        throw new Error('Format Clapay invalide');
      }
    } catch (err) {
      setPaymentMethodsError(err.message);
      const fallback = [
        { id: 1, nom: 'Espèces', icone: '💵', description: 'Paiement en espèces à la livraison', type: 'cash' },
        { id: 2, nom: 'Orange Money', icone: '🟠', description: 'Paiement mobile Orange Money', type: 'mobile_money' },
        { id: 3, nom: 'Carte Bancaire', icone: '💳', description: 'Paiement par carte bancaire', type: 'card' },
      ];
      setPaymentMethods(fallback);
      setSelectedPaymentMethod(fallback[0]);
    } finally {
      setIsLoadingPaymentMethods(false);
    }
  };

  // 🎁 Fonction pour vérifier un code promo
  const verifyPromoCode = async (code) => {
    if (!code.trim()) {
      setPromoError('Veuillez saisir un code promo');
      return false;
    }
    setIsValidatingPromo(true);
    setPromoError(null);
    try {
      const response = await promoCodeAPI.verifyPromoCode({ 
        code: code.trim(),
        montant: originalPrice 
      });
      if (response.data && response.data.is_discounted) {
        setPromoCodeData(response.data);
        setPromoError(null);
        return true;
      } else {
        setPromoError('Code promo invalide ou non applicable');
        setPromoCodeData(null);
        return false;
      }
    } catch (error) {
      console.error('Erreur vérification code promo:', error);
      let errorMessage = 'Erreur lors de la vérification du code promo';
      if (error.response?.data) {
        if (error.response.data.message) {
          errorMessage = Array.isArray(error.response.data.message) 
            ? error.response.data.message[0] 
            : error.response.data.message;
        } else if (error.response.data.error) {
          errorMessage = Array.isArray(error.response.data.error) 
            ? error.response.data.error[0] 
            : error.response.data.error;
        } else if (error.response.data.detail) {
          errorMessage = Array.isArray(error.response.data.detail) 
            ? error.response.data.detail[0] 
            : error.response.data.detail;
        } else if (Array.isArray(error.response.data)) {
          errorMessage = error.response.data[0];
        } else if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      setPromoError(errorMessage);
      setPromoCodeData(null);
      return false;
    } finally {
      setIsValidatingPromo(false);
    }
  };

  // 🎁 Fonction pour appliquer un code promo
  const applyPromoCode = React.useCallback((originalAmount) => {
    if (!promoCodeData || !originalAmount) {
      return {
        originalAmount,
        finalAmount: originalAmount,
        discountAmount: 0
      };
    }
    const discountAmount = promoCodeData.discount_amount || 0;
    const finalAmount = promoCodeData.final_price || originalAmount;
    return {
      originalAmount,
      finalAmount,
      discountAmount
    };
  }, [promoCodeData]);

  // 🎁 useEffect pour mettre à jour les prix quand le code promo change
  useEffect(() => {
    if (promoCodeData && originalPrice > 0) {
      const { finalAmount, discountAmount: newDiscountAmount } = applyPromoCode(originalPrice);
      setFinalPrice(finalAmount);
      setDiscountAmount(newDiscountAmount);
    } else if (!promoCodeData && originalPrice > 0) {
      setFinalPrice(originalPrice);
      setDiscountAmount(0);
    }
  }, [promoCodeData, originalPrice, applyPromoCode]);

  // 🎁 Fonction pour supprimer un code promo
  const removePromoCode = () => {
    setPromoCode('');
    setPromoCodeData(null);
    setPromoError(null);
    setDiscountAmount(0);
    setFinalPrice(originalPrice);
  };
//INITIATEFROMCLIENT SUCCESSFUL
  const checkPaymentStatus = async (signature) => {
    try {
      const response = await modepaiementAPI.paiementcheckin({ signature });
      const paymentData = response.data;
      if (paymentData.status === 'INITIATEFROMCLIENT') {
        if (paymentCheckIntervalRef.current) {
          clearInterval(paymentCheckIntervalRef.current);
          paymentCheckIntervalRef.current = null;
        }
        await createExpeditionAfterPayment(paymentData);
        setPaymentFinalStatus('success');
        setShowPaymentRedirect(true);
      } else if (paymentData.status === 'FAILED' || paymentData.status === 'CLOSED') {
        if (paymentCheckIntervalRef.current) {
          clearInterval(paymentCheckIntervalRef.current);
          paymentCheckIntervalRef.current = null;
        }
        setPaymentStep('error');
        setPaymentFinalStatus('failed');
        setShowPaymentRedirect(true);
      }
    } catch (error) {
      // keep polling
    }
  };

  // Fonction pour générer un numéro de commande unique
  const generateOrderNumber = () => {
    const now = new Date();
    const dateStr = now.toISOString().slice(0, 10).replace(/-/g, '');
    const randomStr = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `CMD-${dateStr}-${randomStr}`;
  };

  // Fonction pour générer un code de retrait unique
  const generateCodeRetrait = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let code = '';
    for (let i = 0; i < 6; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
  };

  const createExpeditionAfterPayment = async (paymentData) => {
    try {
      // Calculer les coordonnées finales
      const coordinates = calculateFinalCoordinates();

      // Mapper le type de colis
      const typeColisMap = {
        'standard': 'standard',
        'froid': 'froid',
        'securise': 'sécurisé',
        'gros_volume': 'gros volume',
        'doc_admin': 'document administratif'
      };
      const typeColis = typeColisMap[expeditionData.packageType] || expeditionData.packageType || 'standard';

      // Mapper le mode d'expédition
      const modeExpeditionMap = {
        'home_delivery': 'Livraison à domicile',
        'relay_point': 'Point relais'
      };
      const modeExpedition = modeExpeditionMap[expeditionData.shippingMode] || expeditionData.mode_expedition || 'Livraison à domicile';

      // Construire le payload selon le format attendu par le backend
      const payload = {
        ride_type_id: expeditionData.ride_type_id || 2,
        vehicle_type_id: expeditionData.vehicle_type_id || 1,
        zone_id: expeditionData.zone_id || 10,
        order_number: expeditionData.order_number || generateOrderNumber(),
        code_retrait: generateCodeRetrait(),
        statut: 'en attente',
        montant: parseFloat(promoCodeData?.final_price || quoteData?.finalPrice || expeditionData.total_price || 0),
        mode_paiement: selectedPaymentMethod?.nom || expeditionData.mode_paiement || 'espèces',
        type_colis: typeColis,
        statut_colis: 'préparé',
        pointrelais: expeditionData.shippingMode === 'relay_point' && selectedRelayPoint?.id 
          ? parseInt(selectedRelayPoint.id) 
          : null,
        pickup_note: expeditionData.pickup_note || '',
        order_note: expeditionData.order_note || '',
        mode_expedition: modeExpedition,
        tip_amount: parseFloat(expeditionData.tip_amount || 0),
        
        // 🚚 Type de service et délai de livraison
        type_service: expeditionData.type_service || '',
        delais_livraison: expeditionData.delais_livraison || '',
        
        // 🆕 NOUVEAU : Données de tarification spécifiques
        tarifLivreur: quoteData?.breakdown?.tarifLivreur || 0,
        tariffCompagni: quoteData?.breakdown?.tariffCompagni || 0,
        tarifAide: quoteData?.breakdown?.tarifAide || 0,
        
        // Adresse expéditeur
        adresse_expediteur: {
          latitude: parseFloat(coordinates.fromLatitude || expeditionData.from_latitude || 0),
          longitude: parseFloat(coordinates.fromLongitude || expeditionData.from_longitude || 0),
          address: coordinates.fromAddress || expeditionData.from_address || '',
          customer_first_name: expeditionData.customer_first_name || (isAuthenticated ? getUserData()?.first_name : guestFirstName) || '',
          customer_last_name: expeditionData.customer_last_name || (isAuthenticated ? getUserData()?.last_name : guestLastName) || '',
          customer_phone_number: (() => {
            const cc = String(customerPhoneCountryCode || '').replace(/^\+?/, '+').trim();
            const local = String(customerPhoneNumberLocal || expeditionData.customer_phone_number || '').replace(/\D/g, '');
            return local ? cc + local : expeditionData.customer_phone_number;
          })(),
          customer_email: expeditionData.customer_email || (isAuthenticated ? getUserData()?.email : guestEmail) || ''
        },
        
        // Adresse destinataire
        adresse_destinataire: {
          latitude: parseFloat(coordinates.toLatitude || expeditionData.to_latitude || 0),
          longitude: parseFloat(coordinates.toLongitude || expeditionData.to_longitude || 0),
          address: coordinates.toAddress || expeditionData.to_address || '',
          pickup_company_name: expeditionData.pickup_company_name || '',
          pickup_phone_number: (() => {
            const cc = String(recipientPhoneCountryCode || '').replace(/^\+?/, '+').trim();
            let local = '';
            if (pickupPhoneNumberLocal && pickupPhoneNumberLocal.trim() !== '') {
              local = String(pickupPhoneNumberLocal).replace(/\D/g, '');
            } else {
              // Extraire le numéro local depuis expeditionData (enlever le code pays si présent)
              const phone = expeditionData.recipient_phone || expeditionData.pickup_phone_number || '';
              if (phone) {
                const phoneString = String(phone);
                const countryCodePattern = /^\+\d{1,4}/;
                const match = phoneString.match(countryCodePattern);
                if (match) {
                  // Si le numéro contient déjà le code pays, l'enlever
                  local = phoneString.substring(match[0].length).replace(/\D/g, '');
                } else {
                  // Sinon, utiliser directement le numéro (déjà local)
                  local = phoneString.replace(/\D/g, '');
                }
              }
            }
            const sanitizedLocal = local ? (local.replace(/^0+/, '') || local) : '';
            return sanitizedLocal ? cc + sanitizedLocal : expeditionData.recipient_phone || expeditionData.pickup_phone_number || "";
          })(),
          // Ajouter les informations destinataire si disponibles
          ...(expeditionData.recipient_first_name && {
            recipient_first_name: expeditionData.recipient_first_name
          }),
          ...(expeditionData.recipient_last_name && {
            recipient_last_name: expeditionData.recipient_last_name
          })
        },
        
        // Infos colis
        infocolis: expeditionData.pickup_items.map(item => ({
          name: item.name || '',
          category: item.category || item.selectedCategory || 'Général',
          weight: parseFloat(item.weight || 0),
          length: parseFloat(item.length || 0),
          width: parseFloat(item.width || 0),
          height: parseFloat(item.height || 0),
          quantity: parseInt(item.quantity || 1)
        }))
      };

      console.log('📦 Payload d\'expédition créé:', payload);
      
      const response = await expeditionAPI.createExpedition(payload);
      toast.success('✅ Expédition créée avec succès après paiement');
      setPaymentStep('success');
      setCurrentStep(5);
      setPaymentCreatedExpedition(response?.data);
    } catch (e) {
      console.error('❌ Erreur création d\'expédition:', e);
      toast.error("❌ Erreur lors de la création d'expédition après paiement");
      setPaymentStep('error');
      setPaymentFinalStatus('failed');
      setShowPaymentRedirect(true);
    }
  };

  const handlePaymentValidation = async () => {
    if (!selectedPaymentMethod) {
      alert('❌ Veuillez sélectionner un mode de paiement');
      return;
    }
    try {
      setPaymentStep('initializing');
      const phoneDigits = (paymentPhoneNumber || '').replace(/\D/g, '');
      const derivedOperatorCode = selectedPaymentMethod?.operatorCode || mapOperatorToCode(selectedPaymentMethod?.nom);
      // 🎁 Utiliser le prix final avec réduction si un code promo est appliqué
      const paymentAmount = promoCodeData?.final_price || Math.max(parseFloat(quoteData?.finalPrice || 0), 1);
      
      const paymentData = {
        amount: paymentAmount, // Prix final à payer (avec réduction si applicable)
        country_code: mapDialCodeToIso(paymentCountryCode),
        operators_code: derivedOperatorCode,
        phone_number: phoneDigits,
        description: 'Paiement expédition',
        
        // 🎁 Informations du code promo si appliqué
        promo_code: promoCodeData?.promo_code || null,
        promo_discount_amount: promoCodeData?.discount_amount || 0,
        promo_final_price: promoCodeData?.final_price || null,
        original_amount: originalPrice || Math.max(parseFloat(quoteData?.finalPrice || 0), 1)
      };
      // Ajouter info utilisateur si non authentifié
      if (!isAuthenticated) {
        paymentData.email = guestEmail;
        paymentData.last_name = guestLastName;
        paymentData.first_name = guestFirstName;
      }
      // Logs de debug
      console.log('[Paiement] selectedPaymentMethod:', selectedPaymentMethod);
      console.log('[Paiement] derivedOperatorCode:', derivedOperatorCode);
      console.log('[Paiement] paymentData payload:', paymentData);
      if (!paymentData.amount) throw new Error('Montant invalide');
      if (!paymentData.country_code) throw new Error('Code pays manquant');
      if (!paymentData.operators_code) throw new Error('Opérateur manquant');
      if (!paymentData.phone_number) throw new Error('Téléphone manquant');

      const response = await modepaiementAPI.initpaiement(paymentData);
      setExpeditionData(prev => ({ ...prev, mode_paiement: selectedPaymentMethod?.nom }));
      const paymentUrl = response?.data?.payment_url;
      const signature = response?.data?.signature;
      if (!paymentUrl) throw new Error("Lien de paiement manquant");
      try { window.localStorage.setItem('ksl_last_payment_url', paymentUrl); } catch {}
      if (typeof window !== 'undefined') window.open(paymentUrl, '_blank');
      if (signature) {
        if (paymentCheckIntervalRef.current) clearInterval(paymentCheckIntervalRef.current);
        paymentCheckIntervalRef.current = setInterval(() => {
          checkPaymentStatus(signature);
        }, 5000);
        // On passe en attente après initialisation
        setPaymentStep('pending');
      }
    } catch (err) {
      setPaymentStep('error');
      toast.error("Erreur initialisation paiement");
    }
  };

    return (
    <div className="container-ksl mx-auto max-w-9xl px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
      <div className="space-y-4 sm:space-y-6">
        {/* 🔝 Élément de référence pour le scroll automatique */}
        <div ref={topElementRef}></div>
        
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-2 sm:space-y-0">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
              Nouvelle expédition 
            </h1>
            <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
              Créez votre livraison
            </p>
          </div>
        </div>

      {/* Stepper amélioré */}
      <div className="bg-white dark:bg-dark-bg-secondary p-4 sm:p-6 rounded-xl border border-gray-200 dark:border-gray-700 shadow-lg">
        {/* Version desktop */}
        <div className="hidden md:flex items-center justify-between">
          {steps.map((step, index) => (
            <React.Fragment key={step.id}>
              <div className="flex flex-col items-center">
                <div className={cn(
                  'w-12 h-12 rounded-full flex items-center justify-center text-sm font-medium shadow-md transition-all duration-300 transform hover:scale-110',
                  currentStep === step.id && step.id === steps.length ? 'bg-green-500 text-white shadow-green-500/50' :
                  currentStep === step.id ? 'bg-ksl-red text-white shadow-ksl-red/50' :
                  currentStep > step.id ? 'bg-green-500 text-white shadow-green-500/50' :
                  'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-400 shadow-gray-300/50'
                )}>
                  {(currentStep > step.id) || (currentStep === step.id && step.id === steps.length) ? '✓' : step.id}
                </div>
                <span className={cn(
                  'mt-2 text-sm font-medium text-center transition-colors duration-300',
                  currentStep === step.id && step.id === steps.length ? 'text-green-600' :
                  currentStep === step.id ? 'text-ksl-red' :
                  currentStep > step.id ? 'text-green-600' :
                  'text-gray-500'
                )}>
                  {step.title}
                </span>
                <span className="text-xs text-gray-400 mt-1 text-center">
                  {step.description}
                </span>
              </div>
              {index < steps.length - 1 && (
                <div className={cn(
                  'flex-1 h-1 mx-4 rounded-full transition-all duration-300',
                  currentStep > step.id ? 'bg-green-500 shadow-green-500/50' : 'bg-gray-200 dark:bg-gray-700'
                )} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Version mobile */}
        <div className="md:hidden">
          <div className="flex items-center justify-center mb-4">
            <div className={cn(
              'w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium shadow-md transition-all duration-300',
              currentStep === steps.length ? 'bg-green-500 text-white shadow-green-500/50' :
              currentStep >= 1 ? 'bg-ksl-red text-white shadow-ksl-red/50' :
              'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-400 shadow-gray-300/50'
            )}>
              {currentStep === steps.length ? '✓' : currentStep}
            </div>
            <div className="ml-3">
              <div className="text-sm font-medium text-gray-900 dark:text-white">
                {steps.find(s => s.id === currentStep)?.title || 'Étape'}
              </div>
              <div className="text-xs text-gray-500">
                {steps.find(s => s.id === currentStep)?.description || ''}
              </div>
            </div>
          </div>
          
          {/* Barre de progression mobile */}
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
            <div 
              className={cn(
                'h-2 rounded-full transition-all duration-300',
                currentStep === steps.length ? 'bg-green-500' : 'bg-ksl-red'
              )}
              style={{ width: `${(currentStep / steps.length) * 100}%` }}
            />
          </div>
          
          {/* Indicateur d'étapes */}
          <div className="flex justify-center mt-3 space-x-1">
            {steps.map((step) => (
              <div
                key={step.id}
                className={cn(
                  'w-2 h-2 rounded-full transition-all duration-300',
                  currentStep >= step.id ? 'bg-ksl-red' : 'bg-gray-300 dark:bg-gray-600'
                )}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Contenu principal */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        
        {/* Formulaire principal */}
        <div className="lg:col-span-2">
          <Card className="p-4 sm:p-6 lg:p-8 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
            
            {/* Étape 1: Expéditeur ET Destinataire */}
            {currentStep === 1 && (
              <div className="space-y-6 sm:space-y-8">
                {/* 🚚 NOUVEAU : Sélection du type de service au début - MODERNISÉ avec bouton déroulement */}
                  <div className="mb-6">
                  <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                    {/* Bouton pour dérouler/rouler la section */}
                      <button
                        type="button"
                      onClick={() => setIsServiceTypeSectionExpanded(!isServiceTypeSectionExpanded)}
                      className="w-full flex items-center justify-between p-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 rounded-lg"
                    >
                      <div className="flex items-center space-x-3">
                        <Truck className="w-4 h-4 text-ksl-red" />
                        <div className="text-left">
                          <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                            🚚 Choisir le type de service *
                          </h3>
                          {expeditionData.type_service ? (
                            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                              {(() => {
                                const selectedService = serviceTypes.find(s => s.key === expeditionData.type_service);
                                return selectedService ? `${selectedService.name} - ${selectedService.delais}` : 'Service sélectionné';
                              })()}
                            </p>
                          ) : (
                            <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                              Aucun service sélectionné
                            </p>
                          )}
                    </div>
                  </div>
                      <div className="flex items-center space-x-2">
                          {expeditionData.type_service && (
                            <CheckCircle className="w-4 h-4 text-green-600" />
                          )}
                          <svg 
                            className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${
                            isServiceTypeSectionExpanded ? 'rotate-180' : ''
                          }`} 
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                        </div>
                      </button>

                    {/* Section déroulable */}
                    {isServiceTypeSectionExpanded && (
                      <div className="px-3 pb-3 border-t border-gray-200 dark:border-gray-600">
                        {/* 📑 Onglets pour organiser les types de service */}
                        <div className="flex space-x-2 mb-3 mt-2 border-b border-gray-200 dark:border-gray-600">
                          <button
                            type="button"
                            onClick={() => setActiveServiceTab('abidjan')}
                            className={`flex-1 px-4 py-2 text-sm font-medium transition-all duration-200 ${
                              activeServiceTab === 'abidjan'
                                ? 'text-ksl-red border-b-2 border-ksl-red bg-ksl-red/5 dark:bg-ksl-red/10'
                                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                            }`}
                          >
                            🏙️ Partout à Abidjan
                          </button>
                          <button
                            type="button"
                            onClick={() => setActiveServiceTab('interurbain')}
                            className={`flex-1 px-4 py-2 text-sm font-medium transition-all duration-200 ${
                              activeServiceTab === 'interurbain'
                                ? 'text-ksl-red border-b-2 border-ksl-red bg-ksl-red/5 dark:bg-ksl-red/10'
                                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                            }`}
                          >
                            🌍 Interurbain
                          </button>
                        </div>

                        {/* 🔍 Champ de recherche moderne */}
                        <div className="relative mb-2">
                          <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                            <Search className="h-4 w-4 text-gray-400" />
                    </div>
                          <input
                            type="text"
                            placeholder="Rechercher un type de service..."
                            value={searchServiceType}
                            className="block w-full pl-8 pr-2 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-ksl-red focus:border-ksl-red transition-all duration-200"
                            onChange={(e) => setSearchServiceType(e.target.value)}
                          />
                  </div>
                  
                        {/* 📋 Liste moderne des types de service */}
                        <div className="max-h-60 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700">
                          {filteredServiceTypes.length === 0 ? (
                            <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                              <Truck className="w-8 h-8 mx-auto mb-2 opacity-50" />
                              <p>Aucun type de service trouvé</p>
                            </div>
                          ) : (
                            filteredServiceTypes.map(service => (
                              <div
                                key={service.key}
                                onClick={() => {
                                  updateExpeditionData('type_service', service.key);
                                  updateExpeditionData('delais_livraison', service.delais);
                                  updateExpeditionData('departure_city', '');
                                  updateExpeditionData('destination_city', '');
                                  if (service.key === 'sous_regionale') {
                                    updateExpeditionData('departure_country', 'Côte d\'Ivoire');
                                    updateExpeditionData('destination_country', '');
                                  }
                                  if (service.key === 'interurbaine' || service.key === 'sous_regionale') {
                                    setCurrentItem(prev => ({ ...prev, selectedCategory: 'car' }));
                                    updateExpeditionData('shippingMode', 'relay_point');
                                  } else if (expeditionData.shippingMode === 'relay_point' && (expeditionData.type_service === 'interurbaine' || expeditionData.type_service === 'sous_regionale')) {
                                    updateExpeditionData('shippingMode', 'home_delivery');
                                  }
                                  setIsServiceTypeSectionExpanded(false);
                                }}
                                className={`p-4 border-b border-gray-100 dark:border-gray-600 cursor-pointer transition-all duration-200 hover:bg-gray-50 dark:hover:bg-gray-600 ${
                                  expeditionData.type_service === service.key
                                    ? 'bg-ksl-red/10 border-l-4 border-l-ksl-red'
                                    : ''
                                }`}
                              >
                                <div className="flex items-start space-x-3">
                                  <div className={`flex-shrink-0 w-4 h-4 rounded-full border-2 mt-1 ${
                                    expeditionData.type_service === service.key
                                      ? 'bg-ksl-red border-ksl-red'
                                      : 'border-gray-300 dark:border-gray-500'
                                  }`}>
                                    {expeditionData.type_service === service.key && (
                                      <div className="w-full h-full rounded-full bg-white scale-50"></div>
                                    )}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center space-x-2">
                                      <h4 className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                        {service.name}
                                      </h4>
                                      {expeditionData.type_service === service.key && (
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-ksl-red text-white">
                                          Sélectionné
                                        </span>
                                      )}
                                    </div>
                                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                      {service.description}
                                    </p>
                                    <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                                      ⏱️ Délai: {service.delais}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                  
                        {expeditionData.type_service && (
                          <div className="mt-2 p-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                            <div className="flex items-center space-x-2">
                              <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                              <span className="text-sm font-medium text-green-800 dark:text-green-200">
                                Type de service sélectionné
                              </span>
                            </div>
                            {(expeditionData.type_service === 'interurbaine' || expeditionData.type_service === 'sous_regionale') && (
                              <Alert variant="warning" className="mt-3">
                      <Info className="w-4 h-4" />
                      <p className="text-sm">
                                  <strong>Service {serviceTypes.find(s => s.key === expeditionData.type_service)?.name} sélectionné</strong>
                          <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400">
                            🌍 Automatique
                          </span>
                        <br />
                        Délai de livraison estimé: <strong>{expeditionData.delais_livraison}</strong>
                        <br />
                                  {serviceTypes.find(s => s.key === expeditionData.type_service)?.description}
                      </p>
                    </Alert>
                  )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>


           

                {/* Section Expéditeur */}
                <div className="space-y-4 sm:space-y-6">
                  <div className="flex items-center space-x-3">
                    <User className="w-5 h-5 sm:w-6 sm:h-6 text-ksl-red" />
                    <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
                      Informations de ramassage
                    </h2>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                    <Input
                      label="Nom et prénom *"
                      value={getFullName()}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Mettre à jour l'état local pour préserver la saisie en cours (y compris les espaces)
                        setCustomerFullNameInput(value);
                        // Séparer et mettre à jour les champs individuels
                        const { first_name, last_name } = splitFullName(value);
                        updateExpeditionData('customer_first_name', first_name);
                        updateExpeditionData('customer_last_name', last_name);
                      }}
                      onBlur={() => {
                        // À la perte de focus, réinitialiser l'état local pour que getFullName() utilise les données séparées
                        setCustomerFullNameInput('');
                      }}
                      leftIcon={User}
                      placeholder="Ex: Jean Dupont"
                      required
                    />
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Téléphone *
                      </label>
                      <div className="flex gap-2">
                        <div className="w-28">
                          <CountryCodeSelect
                            value={customerPhoneCountryCode}
                            onChange={setCustomerPhoneCountryCode}
                          />
                        </div>
                        <div className="flex-1">
                          <Input
                                type="tel"
                                value={customerPhoneNumberLocal}
                                onChange={(e) => {
                                  setCustomerPhoneNumberLocal(e.target.value);
                                }}
                            leftIcon={Phone}
                                placeholder="XX XX XX XX XX"
                                className="w-full"
                                required
                          />
                        </div>
                      </div>
                    </div>
                  </div>


                  {/* 🏙️ NOUVEAU : Champ Départ et Adresse de ramassage sur la même ligne */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Champ Départ */}
                  <div className="space-y-2">
                    {/* Sélection du pays de départ pour service sous-régionale */}
                    {expeditionData.type_service === 'sous_regionale' && (
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          Pays de départ *
                        </label>
                        <SearchableSelect
                          value={expeditionData.departure_country || ''}
                          onChange={(value) => {
                            updateExpeditionData('departure_country', value);
                            updateExpeditionData('departure_city', '');
                          }}
                          options={departureCountryOptions.length > 0 ? departureCountryOptions : ALL_COUNTRIES}
                          placeholder="Sélectionnez un pays de départ..."
                          label="pays"
                          showFlags={true}
                        />
                        <p className="hidden text-xs text-gray-500 dark:text-gray-400">
                          Service sous-régionale : sélectionnez le pays de départ
                        </p>
                      </div>
                    )}

                    {/* Sélection de la ville/commune */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        {expeditionData.type_service === 'interurbaine' 
                          ? 'Ville de départ' 
                          : expeditionData.type_service === 'sous_regionale' 
                            ? 'Ville de départ'
                            : 'Commune de départ'
                        } *
                    </label>
                    {!hasDepartureCountryCityRoutes && expeditionData.type_service === 'sous_regionale' && expeditionData.departure_country ? (
                      <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                        <p className="text-sm text-yellow-800 dark:text-yellow-200 flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4" />
                          Ce pays n'a pas encore de trajets ville configurés
                        </p>
                      </div>
                    ) : (
                    <SearchableSelect
                      value={expeditionData.departure_city || ''}
                      onChange={(value) => {
                        updateExpeditionData('departure_city', value);
                      }}
                        options={departureCityOptions}
                      placeholder={expeditionData.type_service === 'interurbaine' 
                          ? 'Sélectionnez une ville de départ...' 
                          : expeditionData.type_service === 'sous_regionale'
                        ? 'Sélectionnez une ville de départ...' 
                        : 'Sélectionnez une commune de départ...'
                      }
                        label={expeditionData.type_service === 'interurbaine' || expeditionData.type_service === 'sous_regionale' ? 'ville' : 'commune'}
                        disabled={expeditionData.type_service === 'sous_regionale' && !expeditionData.departure_country}
                        required
                    />
                    )}
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {expeditionData.type_service === 'interurbaine' 
                        ? ' Service interubain : sélectionnez la ville de départ'
                          : expeditionData.type_service === 'sous_regionale'
                            ? ' Service sous-régionale : sélectionnez la ville de départ'
                        : ' Service local : sélectionnez la commune d\'Abidjan'
                      }
                        {expeditionData.type_service === 'sous_regionale' && !expeditionData.departure_country && (
                          <span className="text-orange-600 dark:text-orange-400 ml-2">
                            • Sélectionnez d'abord un pays
                          </span>
                        )}
                    </p>
                    </div>
                    </div>
                      
                    {/* Adresse de ramassage */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Adresse de ramassage * 
                      <span className="text-xs text-gray-500 ml-1">(avec recherche GPS)</span>
                    </label>
                    <MapLocation
                      key="pickup-location"
                      value={expeditionData.from_address}
                      onChange={(address) => updateExpeditionData('from_address', address)}
                      onLocationSelect={handlePickupLocationSelect}
                      placeholder="Rechercher l'adresse de ramassage..."
                      className="w-full"
                      required
                    />
                    </div>
                  </div>

                  {/* Coordonnées GPS Expéditeur - CACHÉES MAIS RÉCUPÉRÉES EN ARRIÈRE-PLAN */}
                  <div className="hidden">
                    <Input
                      label="Latitude *"
                      type="number"
                      step="any"
                      value={expeditionData.from_latitude}
                      onChange={(e) => updateExpeditionData('from_latitude', e.target.value)}
                      placeholder="5.336987"
                    />
                    <Input
                      label="Longitude *"
                      type="number"
                      step="any"
                      value={expeditionData.from_longitude}
                      onChange={(e) => updateExpeditionData('from_longitude', e.target.value)}
                      placeholder="-4.008215"
                    />
                  </div>
                </div>

                {/* Séparateur visuel */}
                <div className="hidden border-t border-gray-200 dark:border-gray-700 pt-6">
                  <div className="flex items-center justify-center mb-6">
                    <div className="bg-gray-100 dark:bg-gray-800 px-4 py-2 rounded-full">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        📦 Configuration de la livraison
                      </span>
                    </div>
                  </div>
                </div>

                {/* Section Destinataire */}
                <div className="space-y-4 sm:space-y-6">
                  <div className="flex items-center space-x-3">
                    <MapPin className="w-5 h-5 sm:w-6 sm:h-6 text-ksl-red" />
                    <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
                      Informations de livraison
                    </h2>
                  </div>

                  {/* Zone Côte d'Ivoire FIXE */}
                  {/* <Alert variant="info">
                    <Info className="w-4 h-4" />
                    <div>
                      <p className="font-medium">Zone de livraison: Côte d'Ivoire</p>
                      <p className="text-sm">Livraison disponible sur tout le territoire ivoirien</p>
                    </div>
                  </Alert> */}

                  {/* Transporteur */}
                  <div className="hidden mb-4 sm:mb-6 p-3 sm:p-4 bg-gray-50 dark:bg-gray-800 rounded-lg shadow-md">
                    <label className="block text-sm font-medium text-white dark:text-white mb-3">
                      🚛 Transporteur
                      {isLoadingCarriers && <Loader className="w-4 h-4 ml-2 animate-spin inline" />}
                    </label>
                    
                    {carriersError && (
                      <Alert variant="error" className="mb-3">
                        <AlertCircle className="w-4 h-4" />
                        {carriersError}
                      </Alert>
                    )}
                    
                    <select
                      value={expeditionData.selectedCarrier || ''}
                      onChange={e => updateExpeditionData('selectedCarrier', e.target.value)}
                      className="block w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 bg-white dark:bg-dark-bg-secondary focus:outline-none focus:ring-2 focus:ring-ksl-red focus:border-ksl-red transition-all duration-300 shadow-sm hover:shadow-md"
                      disabled={true}
                    >
                      <option value="">Sélectionnez un transporteur...</option>
                      {carriers.map(carrier => (
                        <option key={(carrier.nom || carrier.name)} value={(carrier.nom || carrier.name)}>
                          {carrier.nom || carrier.name}
                        </option>
                      ))}
                    </select>
                    
                    <p className="text-xs text-gray-500 mt-2">
                      Transporteur ID 1 sélectionné par défaut
                    </p>
                    
                    {carriers.length === 0 && !isLoadingCarriers && (
                      <p className="text-sm text-gray-500 mt-2">
                        Aucun transporteur disponible pour le moment.
                      </p>
                    )}
                  </div>

                  {/* Type de colis - MODERNISÉ avec bouton déroulement */}
                  <div className="mb-4 sm:mb-6">
                    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                      {/* Bouton pour dérouler/rouler la section */}
                        <button
                          type="button"
                        onClick={() => setIsPackageTypeSectionExpanded(!isPackageTypeSectionExpanded)}
                        className="w-full flex items-center justify-between p-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 rounded-lg"
                      >
                        <div className="flex items-center space-x-3">
                          <Package className="w-4 h-4 text-ksl-red" />
                          <div className="text-left">
                            <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                              📦 Choisir le type de colis *
                            </h3>
                            {expeditionData.packageType ? (
                              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                {(() => {
                                  const packageTypesList = [
                                    { value: 'standard', label: 'Standard' },
                                    { value: 'froid', label: 'Froid' },
                                    { value: 'securise', label: 'Sécurisé' },
                                    { value: 'gros_volume', label: 'Gros volume' },
                                    { value: 'doc_admin', label: 'Doc administratif' }
                                  ];
                                  const selectedType = packageTypesList.find(t => t.value === expeditionData.packageType);
                                  return selectedType ? selectedType.label : 'Type de colis sélectionné';
                                })()}
                              </p>
                            ) : (
                              <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                                Aucun type de colis sélectionné
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          {expeditionData.packageType && (
                            <CheckCircle className="w-4 h-4 text-green-600" />
                          )}
                          <svg 
                            className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${
                              isPackageTypeSectionExpanded ? 'rotate-180' : ''
                            }`} 
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                          </div>
                        </button>

                      {/* Section déroulable */}
                      {isPackageTypeSectionExpanded && (
                        <div className="px-3 pb-3 border-t border-gray-200 dark:border-gray-600">
                          {/* 🔍 Champ de recherche moderne */}
                          <div className="relative mb-2 mt-2">
                            <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                              <Search className="h-4 w-4 text-gray-400" />
                    </div>
                            <input
                              type="text"
                              placeholder="Rechercher un type de colis..."
                              value={searchPackageType}
                              className="block w-full pl-8 pr-2 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-ksl-red focus:border-ksl-red transition-all duration-200"
                              onChange={(e) => setSearchPackageType(e.target.value)}
                            />
                          </div>

                          {/* 📋 Liste moderne des types de colis */}
                          <div className="max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700">
                            {filteredPackageTypes.length === 0 ? (
                              <div className="p-3 text-center text-gray-500 dark:text-gray-400">
                                <Package className="w-6 h-6 mx-auto mb-2 opacity-50" />
                                <p className="text-sm">Aucun type de colis trouvé</p>
                              </div>
                            ) : (
                              filteredPackageTypes.map(type => (
                                <div
                                  key={type.value}
                                  onClick={() => {
                                    updateExpeditionData('packageType', type.value);
                                    setIsPackageTypeSectionExpanded(false);
                                  }}
                                  className={`p-3 border-b border-gray-100 dark:border-gray-600 cursor-pointer transition-all duration-200 hover:bg-gray-50 dark:hover:bg-gray-600 ${
                                    expeditionData.packageType === type.value
                                      ? 'bg-ksl-red/10 border-l-4 border-l-ksl-red'
                                      : ''
                                  }`}
                                >
                                  <div className="flex items-start space-x-3">
                                    <div className={`flex-shrink-0 w-3 h-3 rounded-full border-2 mt-1 ${
                                      expeditionData.packageType === type.value
                                        ? 'bg-ksl-red border-ksl-red'
                                        : 'border-gray-300 dark:border-gray-500'
                                    }`}>
                                      {expeditionData.packageType === type.value && (
                                        <div className="w-full h-full rounded-full bg-white scale-50"></div>
                                      )}
                                    </div>
                                    <div className="flex items-center space-x-2 flex-1 min-w-0">
                                      <span className="text-base">{type.emoji}</span>
                                      <h4 className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                        {type.label}
                                      </h4>
                                      {expeditionData.packageType === type.value && (
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-ksl-red text-white">
                                          Sélectionné
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                          
                          {expeditionData.packageType && (
                            <div className="mt-2 p-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                              <div className="flex items-center space-x-2">
                                <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                                <span className="text-xs font-medium text-green-800 dark:text-green-200">
                                  Type de colis sélectionné
                                </span>
                              </div>
                            </div>
                          )}
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                      Le type de colis détermine les points relais disponibles et les tarifs appliqués
                    </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Mode d'expédition */}
                  <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-gray-50 dark:bg-gray-800 rounded-lg shadow-md">
                    <label className="block text-sm font-medium text-white dark:text-white mb-3">
                      Mode d'expédition *
                    </label>
                    <div className={cn(
                      "gap-3 sm:gap-4",
                      expeditionData.type_service === 'interurbaine' 
                        ? "grid grid-cols-1" 
                        : "grid grid-cols-1 md:grid-cols-2"
                    )}>
                      {/* Option Livraison à domicile - masquée pour interurbaine */}
                      {expeditionData.type_service !== 'interurbaine' && (
                                              <button
                          type="button"
                          className={cn(
                            'px-4 sm:px-6 py-3 sm:py-4 rounded-lg border-2 transition-all duration-300 transform hover:scale-105 hover:shadow-md', 
                            expeditionData.shippingMode === 'home_delivery' 
                              ? 'border-ksl-red bg-ksl-red/5 dark:bg-ksl-red/10'
                               : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                          )}
                          onClick={() => updateExpeditionData('shippingMode', 'home_delivery')}
                        >
                          <div className="flex flex-col items-center">
                            <span className="text-xl sm:text-2xl mb-1 sm:mb-2">🏠</span>
                            <span className="font-medium text-gray-900 dark:text-white text-xs sm:text-sm">Livraison à domicile</span>
                          </div>
                        </button>
                      )}
                      
                      {/* Option Point relais - toujours visible */}
                        <button
                          type="button"
                          className={cn(
                            'px-4 sm:px-6 py-3 sm:py-4 rounded-lg border-2 transition-all duration-300 transform hover:scale-105 hover:shadow-md', 
                            expeditionData.shippingMode === 'relay_point' 
                              ? 'border-ksl-red bg-ksl-red/5 dark:bg-ksl-red/10'
                               : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                          )}
                          onClick={() => updateExpeditionData('shippingMode', 'relay_point')}
                        >
                          <div className="flex flex-col items-center">
                            <span className="text-xl sm:text-2xl mb-1 sm:mb-2">🏪</span>
                            <span className="font-medium text-gray-900 dark:text-white text-xs sm:text-sm">Dépôt en point relais</span>
                          </div>
                        </button>
                    </div>
                    
                    {/* Message conditionnel */}
                    {expeditionData.type_service === 'interurbaine' ? (
                      <p className="text-xs text-orange-600 dark:text-orange-400 mt-3 text-center">
                        Service interurbaine : Seul le dépôt en point relais est disponible
                      </p>
                    ) : (
                    <p className="text-xs text-gray-500 mt-3 text-center">
                      Choisissez entre livraison directe ou dépôt en point relais
                    </p>
                    )}
                  </div>

                  {/* 🏙️ NOUVEAU : Champ Destination - TOUJOURS VISIBLE */}
                  {/* Destination et Adresse de livraison sur la même ligne */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Destination */}
                  <div className="space-y-2">
                    {/* Sélection du pays de destination pour service sous-régionale */}
                    {expeditionData.type_service === 'sous_regionale' && (
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          Pays de destination *
                        </label>
                        <SearchableSelect
                          value={expeditionData.destination_country || ''}
                          onChange={(value) => {
                            updateExpeditionData('destination_country', value);
                            updateExpeditionData('destination_city', '');
                          }}
                          options={destinationCountryOptions.length > 0 ? destinationCountryOptions : ALL_COUNTRIES}
                          placeholder="Sélectionnez un pays de destination..."
                          label="pays"
                          showFlags={true}
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          🌍 Service sous-régionale : sélectionnez le pays de destination
                        </p>
                      </div>
                    )}

                    {/* Sélection de la ville/commune */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        {expeditionData.type_service === 'interurbaine' 
                          ? 'Ville de destination' 
                          : expeditionData.type_service === 'sous_regionale' 
                            ? 'Ville de destination'
                            : 'Commune de destination'
                        } *
                    </label>
                    {!hasDestinationCountryCityRoutes && expeditionData.type_service === 'sous_regionale' && expeditionData.destination_country ? (
                      <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                        <p className="text-sm text-yellow-800 dark:text-yellow-200 flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4" />
                          Ce pays n'a pas encore de trajets ville configurés
                        </p>
                      </div>
                    ) : (
                    <SearchableSelect
                      value={expeditionData.destination_city || ''}
                      onChange={(value) => {
                        updateExpeditionData('destination_city', value);
                      }}
                        options={destinationCityOptions}
                      placeholder={expeditionData.type_service === 'interurbaine' 
                          ? 'Sélectionnez une ville de destination...' 
                          : expeditionData.type_service === 'sous_regionale'
                        ? 'Sélectionnez une ville de destination...' 
                        : 'Sélectionnez une commune de destination...'
                      }
                        label={expeditionData.type_service === 'interurbaine' || expeditionData.type_service === 'sous_regionale' ? 'ville' : 'commune'}
                        disabled={expeditionData.type_service === 'sous_regionale' && !expeditionData.destination_country}
                        required
                    />
                    )}
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {expeditionData.type_service === 'interurbaine' 
                        ? ' Service interubain : sélectionnez la ville de destination'
                          : expeditionData.type_service === 'sous_regionale'
                            ? ' Service sous-régionale : sélectionnez la ville de destination'
                        : ' Service local : sélectionnez la commune d\'Abidjan'
                      }
                      {expeditionData.destination_city && expeditionData.shippingMode === 'relay_point' && (
                        <span className="block mt-1 text-xs text-blue-600 dark:text-blue-400">
                          📍 {filteredRelayPoints.length} point(s) relais disponible(s) à {expeditionData.destination_city}
                            {(expeditionData.type_service === 'interurbaine' || expeditionData.type_service === 'sous_regionale')
                            ? ' (filtré par ville)' 
                            : ' (filtré par village/commune)'
                          }
                        </span>
                      )}
                    </p>
                    </div>
                    </div>

                    {/* Adresse de livraison - affichée seulement en mode home_delivery */}
                    {expeditionData.shippingMode === 'home_delivery' && (
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          📍 Adresse de livraison *
                          <span className="text-xs text-gray-500 ml-1">(avec recherche GPS)</span>
                        </label>
                        <MapLocation
                          key="delivery-location"
                          value={expeditionData.to_address}
                          onChange={(address) => updateExpeditionData('to_address', address)}
                          onLocationSelect={handleDeliveryLocationSelect}
                          placeholder="Rechercher l'adresse de livraison..."
                          className="w-full"
                          required
                        />
                      </div>
                    )}
                  </div>

                  {/* Sélecteur de points relais - affiché seulement si mode relay_point */}
                  {expeditionData.shippingMode === 'relay_point' && (
                    <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg shadow-md border border-blue-200 dark:border-blue-700">
                      <label className="block text-sm font-medium text-blue-900 dark:text-blue-100 mb-3">
                        🏪 Point relais *
                        {isLoadingRelayPoints && <Loader className="w-4 h-4 ml-2 animate-spin inline" />}
                      </label>
                      
                      {relayPointsError && (
                        <Alert variant="error" className="mb-3">
                          <AlertCircle className="w-4 h-4" />
                          {relayPointsError}
                        </Alert>
                      )}
                      
                      {/* Afficher le sélecteur seulement s'il y a des points relais disponibles */}
                      {filteredRelayPoints.length > 0 ? (
                      <select
                        value={selectedRelayPoint?.id || ''}
                        onChange={(e) => {
                          const selectedId = e.target.value;
                          const relay = filteredRelayPoints.find(r => r.id && r.id.toString() === selectedId);
                          
                          console.log('🏪 Point relais sélectionné:');
                          console.log('   - ID sélectionné:', selectedId);
                          console.log('   - Point relais trouvé:', relay);
                          console.log('   - Gérant:', relay?.gerant);
                          console.log('   - Transporteur gérant:', relay?.gerant?.transporteur);
                          
                          setSelectedRelayPoint(relay);
                          
                          if (relay) {
                            // Mettre à jour l'adresse de livraison avec les infos du point relais
                            updateExpeditionData('to_address', relay.address || relay.adresse);
                            // Ne plus pré-remplir recipient_first_name, recipient_last_name et recipient_phone
                            // L'utilisateur doit les saisir manuellement
                            
                            // Mettre à jour les coordonnées GPS du point relais
                            updateExpeditionData('to_latitude', relay.latitude?.toString() || '');
                            updateExpeditionData('to_longitude', relay.longitude?.toString() || '');
                            
                            console.log('📍 Point relais sélectionné:', relay);
                            console.log('📍 Coordonnées GPS du point relais:', relay.latitude, relay.longitude);
                          }
                        }}
                        className="block w-full px-4 py-3 border-2 border-blue-300 dark:border-blue-600 rounded-lg text-blue-900 dark:text-blue-100 bg-white dark:bg-dark-bg-secondary focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 shadow-sm hover:shadow-md"
                        disabled={isLoadingRelayPoints}
                        required
                      >
                        <option value="">
                          Sélectionnez un point relais... ({filteredRelayPoints.length} compatibles)
                          {expeditionData.destination_city && ` - Filtrés par ${(expeditionData.type_service === 'interurbaine' || expeditionData.type_service === 'sous_regionale') ? 'ville' : 'village/commune'}`}
                        </option>
                        {filteredRelayPoints.map(relay => (
                          <option key={relay.id || 'unknown'} value={relay.id || ''}>
                            {relay.name || relay.nom} - {relay.address || relay.adresse}
                          </option>
                        ))}
                      </select>
                      ) : (
                        /* Message quand aucun point relais n'est disponible */
                        <div className="p-4 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-700 rounded-lg">
                          <div className="flex items-center space-x-3">
                            <div className="flex-shrink-0">
                              <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center">
                                <span className="text-white text-sm font-bold">⚠️</span>
                              </div>
                            </div>
                            <div className="flex-1">
                              <h3 className="text-sm font-medium text-orange-800 dark:text-orange-200">
                                Aucun point relais disponible
                              </h3>
                              <p className="text-sm text-orange-700 dark:text-orange-300 mt-1">
                                {expeditionData.destination_city 
                                  ? `Aucun point relais disponible pour ce trajet vers ${expeditionData.destination_city}.`
                                  : 'Aucun point relais disponible pour cette destination.'
                                }
                              </p>
                              <p className="text-xs text-orange-600 dark:text-orange-400 mt-2">
                                💡 Vous pouvez essayer de changer la destination ou utiliser la livraison à domicile.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {selectedRelayPoint && (
                        <div className="mt-3 p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                          <div className="flex items-start space-x-3">
                            <div className="flex-shrink-0">
                              <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                                <span className="text-white text-sm font-bold">🏪</span>
                              </div>
                            </div>
                            <div className="flex-1">
                              <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100">
                                {selectedRelayPoint.nom || selectedRelayPoint.name}
                              </h4>
                              <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                                {selectedRelayPoint.adresse || selectedRelayPoint.address}
                              </p>
                              {(selectedRelayPoint.telephone || selectedRelayPoint.phone) && (
                                <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                                  📞 {selectedRelayPoint.telephone || selectedRelayPoint.phone}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                      
                      <p className="text-xs text-blue-600 dark:text-blue-400 mt-2">
                        Points relais compatibles avec le type de colis "{expeditionData.packageType || 'standard'}"
                        {expeditionData.destination_city && (
                          <span className="block mt-1">
                            Filtrés par {expeditionData.type_service === 'interurbaine' ? 'ville' : 'village/commune'} : {expeditionData.destination_city}
                          </span>
                        )}
                      </p>
                    </div>
                  )}

                  {/* Adresse de livraison déjà intégrée dans la grille ci-dessus */}

                  {/* Information pour mode point relais */}
                  {expeditionData.shippingMode === 'relay_point' && (
                    <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0">
                          <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                            <span className="text-white text-sm font-bold">🏪</span>
                          </div>
                        </div>
                        <div className="flex-1">
                          <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100">
                            Mode Point Relais
                          </h4>
                          <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                            L'adresse de livraison sera automatiquement celle du point relais sélectionné ci-dessous.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}



                  {/* Coordonnées GPS Destinataire - CACHÉES MAIS RÉCUPÉRÉES EN ARRIÈRE-PLAN */}
                  <div className="hidden">
                    <Input
                      label="Latitude *"
                      type="number"
                      step="any"
                      value={expeditionData.to_latitude}
                      onChange={(e) => updateExpeditionData('to_latitude', e.target.value)}
                      placeholder="5.347500"
                    />
                    <Input
                      label="Longitude *"
                      type="number"
                      step="any"
                      value={expeditionData.to_longitude}
                      onChange={(e) => updateExpeditionData('to_longitude', e.target.value)}
                      placeholder="-4.015400"
                    />
                  </div>

                  {/* Informations destinataire */}
                  <div className="space-y-4">
                    <h3 className="font-medium text-gray-900 dark:text-white flex items-center">
                      <User className="w-4 h-4 mr-2 text-ksl-red" />
                      <span className="text-sm sm:text-base">Informations du destinataire</span>
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                      <Input
                        label="Nom et prénom du destinataire *"
                        value={getRecipientFullName()}
                        onChange={(e) => {
                          const value = e.target.value;
                          // Mettre à jour l'état local pour préserver la saisie en cours (y compris les espaces)
                          setRecipientFullNameInput(value);
                          // Séparer et mettre à jour les champs individuels
                          const { first_name, last_name } = splitRecipientFullName(value);
                          updateExpeditionData('recipient_first_name', first_name);
                          updateExpeditionData('recipient_last_name', last_name);
                        }}
                        onBlur={() => {
                          // À la perte de focus, réinitialiser l'état local pour que getRecipientFullName() utilise les données séparées
                          setRecipientFullNameInput('');
                        }}
                        leftIcon={User}
                        placeholder="Ex: Marie Martin"
                        required
                      />
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Numéro de téléphone du destinataire *
                      </label>
                      <div className="flex">
                        <CountryCodeSelect
                          value={recipientPhoneCountryCode}
                          onChange={setRecipientPhoneCountryCode}
                        />
                        <div className="flex-1">
                          <Input
                            type="tel"
                            value={pickupPhoneNumberLocal}
                            onChange={(e) => {
                              setPickupPhoneNumberLocal(e.target.value);
                            }}
                            leftIcon={Phone}
                            placeholder="XX XX XX XX XX"
                            className="w-full"
                            required
                          />
                        </div>
                      </div>
                    </div>
                    </div>
                  </div>
                </div>

                {/* Navigation */}
                <div className="flex flex-col sm:flex-row justify-between pt-6 space-y-3 sm:space-y-0">
                  <Button
                    variant="outline"
                    onClick={handlePrevStep}
                    disabled={currentStep === 1}
                    className="w-full sm:w-auto"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    <span className="text-sm sm:text-base">Précédent</span>
                  </Button>
                  <Button
                    onClick={handleNextStep}
                    className="w-full sm:w-auto"
                  >
                    <span className="text-sm sm:text-base">Suivant</span>
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            )}

            {/* Étape 2: Articles */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <div className="flex items-center space-x-3">
                  <Package className="w-6 h-6 text-ksl-red" />
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                    Articles à livrer
                  </h2>
                </div>

                {/* Formulaire d'ajout d'article */}
                <Card className="p-4 bg-gray-50 dark:bg-gray-800">
                  <h3 className="font-medium text-gray-900 dark:text-white mb-4">
                    Ajouter un article
                  </h3>
                  
                  {/* Onglets par catégorie - EN HAUT */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                      Type de véhicule *
                    </label>
                    <div className="flex space-x-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                      {/* Si service interurbaine ou sous-régionale : afficher seulement "Car" (formats camion) */}
                      {(expeditionData.type_service === 'interurbaine' || expeditionData.type_service === 'sous_regionale') ? (
                        /* Deux onglets pour interurbaine/sous-régionale */
                        <>
                          <button
                            type="button"
                            onClick={() => setCurrentItem(prev => ({ ...prev, selectedCategory: 'car' }))}
                            className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                              currentItem.selectedCategory === 'car'
                                ? 'bg-white dark:bg-gray-600 text-ksl-red shadow-sm'
                                : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                            }`}
                          >
                            🚐 Car
                          </button>
                          <button
                            type="button"
                            onClick={() => setCurrentItem(prev => ({ ...prev, selectedCategory: 'camion' }))}
                            className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                              currentItem.selectedCategory === 'camion'
                                ? 'bg-white dark:bg-gray-600 text-ksl-red shadow-sm'
                                : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                            }`}
                          >
                            🚛 Camions
                          </button>
                        </>
                      ) : (
                        /* Sinon afficher les 3 onglets normaux */
                        ['moto', 'fourgon', 'camion'].map((category) => (
                          <button
                            key={category}
                            type="button"
                            onClick={() => setCurrentItem(prev => ({ ...prev, selectedCategory: category }))}
                            className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                              currentItem.selectedCategory === category
                                ? 'bg-white dark:bg-gray-600 text-ksl-red shadow-sm'
                                : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                            }`}
                          >
                            {category === 'moto' && '🏍️ Moto'}
                            {category === 'fourgon' && '🚐 Fourgon ou Voiture'}
                            {category === 'camion' && '🚛 Camion'}
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <Input
                      label="Nom de l'article *"
                      value={currentItem.name}
                      onChange={(e) => setCurrentItem(prev => ({...prev, name: e.target.value}))}
                      placeholder="Ex: Ordinateur portable"
                    />
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Catégorie
                      </label>
                      <select
                        value={currentItem.category}
                        onChange={(e) => setCurrentItem(prev => ({...prev, category: e.target.value}))}
                        className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 bg-white dark:bg-dark-bg-secondary focus:outline-none focus:ring-2 focus:ring-ksl-red focus:border-transparent"
                      >
                        {categories.map(cat => (
                          <option key={cat} value={cat}>{getCategoryIcon(cat)} {cat}</option>
                        ))}
                      </select>
                    </div>
                    
                    {/* Sélecteur de format de colis */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                        Format de colis *
                      </label>
                      
                      {/* Bouton pour afficher/masquer les formats */}
                      <button
                        type="button"
                        onClick={() => setCurrentItem(prev => ({ ...prev, showFormats: !prev.showFormats }))}
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-dark-bg-secondary text-left flex items-center justify-between hover:border-gray-400 dark:hover:border-gray-500 transition-colors"
                      >
                        <span className="text-gray-900 dark:text-gray-100">
                          {selectedPackageFormat 
                            ? getSimplifiedFormatName(packageFormats.find(f => f.id === selectedPackageFormat))
                            : 'Sélectionnez un format...'}
                        </span>
                        <svg 
                          className={`w-5 h-5 text-gray-500 transition-transform ${currentItem.showFormats ? 'rotate-180' : ''}`}
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      
                      {/* Liste déroulante des formats - Affichée seulement si showFormats est true */}
                      {currentItem.showFormats && (
                        <div className="mt-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-dark-bg-secondary shadow-lg">
                          <div className="grid grid-cols-1 gap-2 p-2 max-h-64 overflow-y-auto">
                            {packageFormats
                              .filter(format => {
                                // Pour l'onglet "Car" (interurbaine/sous-régionale), afficher Moto et Fourgon
                                if (currentItem.selectedCategory === 'car') {
                                  return format.category === 'moto' || format.category === 'fourgon';
                                }
                                // Sinon, logique normale
                                return format.category === (currentItem.selectedCategory || 'moto');
                              })
                              .filter((format, index, array) => {
                                // Pour l'onglet "Car", éliminer les doublons de "Taille hors Gabarit"
                                if (currentItem.selectedCategory === 'car' && format.name.includes('Taille hors Gabarit')) {
                                  // Garder seulement le premier "Taille hors Gabarit" trouvé
                                  return array.findIndex(f => f.name.includes('Taille hors Gabarit')) === index;
                                }
                                return true;
                              })
                              .sort((a, b) => {
                                // Pour l'onglet "Car", placer "Taille hors Gabarit" à la fin
                                if (currentItem.selectedCategory === 'car') {
                                  if (a.name.includes('Taille hors Gabarit')) return 1;
                                  if (b.name.includes('Taille hors Gabarit')) return -1;
                                }
                                return 0;
                              })
                              .map(format => {
                                return (
                                <button
                                  key={format.id}
                                  type="button"
                                  onClick={() => {
                                    handlePackageFormatChange(format.id);
                                    setCurrentItem(prev => ({ ...prev, showFormats: false }));
                                  }}
                                  className={`p-3 text-left border rounded-lg transition-colors ${
                                    currentItem.packageFormat === format.id
                                      ? 'border-ksl-red bg-ksl-red/10 text-ksl-red'
                                      : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                                  }`}
                                >
                                  <div className="font-medium text-sm text-gray-900 dark:text-gray-100">{getSimplifiedFormatName(format)}</div>
                                  <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                    {format.description}
                                  </div>
                                </button>
                                );
                              })}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <Input
                      label="Quantité"
                      type="number"
                      min="1"
                      value={currentItem.quantity}
                      onChange={(e) => setCurrentItem(prev => ({...prev, quantity: e.target.value}))}
                    />
                  </div>

                  {/* Champs de poids et dimensions - visibles seulement pour les formats "Taille hors Gabarit" */}
                  {selectedPackageFormat && packageFormats.find(f => f.id === selectedPackageFormat)?.isCustom && (
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <Input
                        label="Poids (kg)"
                        type="number"
                        step="0.1"
                        value={currentItem.weight}
                        onChange={(e) => setCurrentItem(prev => ({...prev, weight: e.target.value}))}
                        placeholder="0.0"
                      />
                      <Input
                        label="Longueur (cm)"
                        type="number"
                        value={currentItem.length}
                        onChange={(e) => setCurrentItem(prev => ({...prev, length: e.target.value}))}
                        placeholder="0"
                      />
                      <Input
                        label="Largeur (cm)"
                        type="number"
                        value={currentItem.width}
                        onChange={(e) => setCurrentItem(prev => ({...prev, width: e.target.value}))}
                        placeholder="0"
                      />
                      <Input
                        label="Hauteur (cm)"
                        type="number"
                        value={currentItem.height}
                        onChange={(e) => setCurrentItem(prev => ({...prev, height: e.target.value}))}
                        placeholder="0"
                      />
                    </div>
                  )}

                  <Button onClick={addItem} className="w-full">
                    <Plus className="w-4 h-4 mr-2" />
                    Ajouter l'article
                  </Button>
                </Card>

                {/* Liste des articles ajoutés */}
                {expeditionData.pickup_items.length > 0 && (
                  <div className="space-y-3">
                    <h3 className="font-medium text-gray-900 dark:text-white">
                      Articles ajoutés ({expeditionData.pickup_items.length})
                    </h3>
                    
                    {expeditionData.pickup_items.map((item, index) => (
                      <Card key={index} className="p-4">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900 dark:text-white">
                              {item.name} 
                              <Badge className="ml-2">{item.category}</Badge>
                              {item.packageFormat && (
                                <Badge variant="info" className="ml-2">
                                  {getSimplifiedFormatName(packageFormats.find(f => f.id === item.packageFormat))}
                                </Badge>
                              )}
                            </h4>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              {/* {item.weight}kg •  */}
                              {item.quantity} unité(s) 
                              {/* {item.length}×{item.width}×{item.height}cm */}
                              {item.packageFormat && item.packageFormat !== 'xl' && (
                                <span className="text-blue-600 dark:text-blue-400">
                                  {' • '}{packageFormats.find(f => f.id === item.packageFormat)?.description}
                                </span>
                              )}
                            </p>
                            {/* {item.description && (
                              <p className="text-sm text-gray-500 dark:text-gray-500 mt-1 italic">
                                📝 {item.description}
                              </p>
                            )} */}
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => removeItem(index)}
                          >
                            <Minus className="w-4 h-4" />
                          </Button>
                        </div>
                      </Card>
                    ))}

                    {/* Alerte poids total - affichée directement après les articles */}
                    {expeditionData.pickup_items.reduce((sum, item) => 
                      sum + (item.weight * item.quantity), 0
                    ) > 40000 && (
                      <div className="mt-3 p-4 bg-red-100 dark:bg-red-900/20 border border-red-300 dark:border-red-700 rounded-lg">
                        <div className="flex items-center space-x-2">
                          <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
                          <div>
                            <p className="font-semibold text-red-800 dark:text-red-200">
                              ⚠️ Limite de poids dépassée
                            </p>
                            <p className="text-sm text-red-700 dark:text-red-300">
                              Le poids total ne peut pas dépasser 40000kg. Nous ne prenons pas en compte les poids au-delà de 40000kg.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Résumé automatique */}
                    <div className="mt-3 p-4 bg-blue-100 dark:bg-blue-900/20 border border-blue-300 dark:border-blue-700 rounded-lg">
                      <div className="flex items-start space-x-2">
                        <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                        <div>
                          <p className="text-sm text-blue-800 dark:text-blue-200">
                        <strong>Résumé:</strong> {expeditionData.pickup_items.reduce((sum, item) => sum + (item.quantity || 1), 0)} article(s), 
                        Poids total: {expeditionData.pickup_items.reduce((sum, item) => 
                          sum + (item.weight * item.quantity), 0
                        ).toFixed(1)}kg
                        <br />
                        Volume total: {(expeditionData.pickup_items.reduce((sum, item) => {
                          const itemVolume = (parseFloat(item.length) || 0) * (parseFloat(item.width) || 0) * (parseFloat(item.height) || 0) * parseInt(item.quantity);
                          return sum + (itemVolume / 1000000); // Convertir en m³
                        }, 0)).toFixed(3)}m³
                            {expeditionData.type_service !== 'interurbaine' && (
                              <>
                        <br />
                        {/* <strong>Véhicule recommandé:</strong> {
                          (() => {
                            const vehicle = VEHICLE_TYPES.find(v => v.id === expeditionData.vehicle_type_id);
                            return vehicle ? `${vehicle.icon} ${vehicle.name}` : 'Auto';
                          })()
                        } */}
                              </>
                            )}
                      </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Type de véhicule EVA (modifiable) ou Car de Compagnie pour interurbaine - MASQUÉ (sélection automatique) */}
                {false && expeditionData.type_service === 'interurbaine' ? (
                  /* Car de compagnie pour service interurbaine */
                  <div className="mb-6 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-2 border-blue-200 dark:border-blue-700 rounded-xl">
                    <div className="flex items-center justify-center space-x-4">
                      <div className="text-6xl">🚐</div>
                      <div className="text-center">
                        <h3 className="text-xl font-bold text-blue-900 dark:text-blue-100 mb-2">
                          Car de Compagnie
                        </h3>
                        <p className="text-blue-700 dark:text-blue-300 mb-1">
                          Service Interurbain
                        </p>
                        {expeditionData.selectedRelayPoint && (
                          <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
                            📍 {filteredRelayPoints.find(r => r.id.toString() === expeditionData.selectedRelayPoint)?.name || 'Point relais sélectionné'}
                          </p>
                        )}
                        <div className="mt-2 p-2 bg-white/50 dark:bg-gray-800/50 rounded-lg">
                          <p className="text-xs text-blue-600 dark:text-blue-400 font-medium">
                            🏢 Compagnie de Transport
                          </p>
                          <p className="text-xs text-blue-700 dark:text-blue-300">
                            {filteredRelayPoints.find(r => r.id.toString() === expeditionData.selectedRelayPoint)?.name || 'Transporteur Partenaire'}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="mt-4 text-center">
                      <p className="text-sm text-blue-600 dark:text-blue-400">
                        Transport assuré par nos partenaires transporteurs interurbains
                      </p>
                    </div>
                  </div>
                ) : (
                  /* Types de véhicule normaux */
                <div className="hidden mb-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg shadow-md">
                  <label className="block text-sm font-medium text-white dark:text-white mb-3">
                    Type de véhicule EVA (modifiable)
                  </label>
                  <div className="hidden grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {[
                      { id: 2, name: '🏍️ Moto', code: 'xs_moto', icon: '🏍️', min_weight: 0, max_weight: 51, min_volume: 0, max_volume: 0.160, description: '🏍️ Moto - Poids < 51kg, Volume < 0.16m³' },
                      { id: 1, name: 'Tricycle', code: 's_tricycle', icon: '🚗', min_weight: 51, max_weight: 201, min_volume: 0.16, max_volume: 0.54, description: 'Tricycle - Poids 51-201kg, Volume 0.16-0.54m³' },
                      { id: 4, name: '🚐 Fourgon', code: 'm_fourgon_compact', icon: '🚐', min_weight: 201, max_weight: 2001, min_volume: 0.54, max_volume: 19.2, description: '🚐 Fourgon - Poids 201-2000kg, Volume 0.54-19.2m³' },
                      { id: 7, name: '🚛 Camion', code: '2xl_petit_camion', icon: '🚛', min_weight: 2001, max_weight: 40000, min_volume: 19.2, max_volume: 999, description: '🚛 Camion - Poids > 2001kg, Volume > 19.2m³' }
                    ].map((vehicle) => (
                      <button
                        key={vehicle.id}
                        type="button"
                        onClick={() => updateExpeditionData('vehicle_type_id', vehicle.id)}
                        className={cn(
                          'relative p-4 border rounded-lg text-left transition-all hover:shadow-md',
                          expeditionData.vehicle_type_id === vehicle.id
                            ? 'border-ksl-red bg-ksl-red/10'
                            : 'border-gray-300 hover:border-gray-400 dark:border-gray-600'
                        )}
                      >
                        <div className="flex items-center space-x-2 mb-2">
                          <span className="text-2xl">{vehicle.icon}</span>
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {vehicle.name}
                          </div>
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                          <div>Poids: {vehicle.min_weight}-{vehicle.max_weight}kg</div>
                          <div>Volume: {vehicle.min_volume}-{vehicle.max_volume}m³</div>
                        </div>
                        {expeditionData.vehicle_type_id === vehicle.id && (
                          <div className="absolute top-2 right-2">
                            <CheckCircle className="w-4 h-4 text-ksl-red" />
                          </div>
                        )}
                      </button>
                      ))
                    }
                  </div>
                  </div>
                )}

                {/* Affichage du véhicule sélectionné automatiquement */}
                <div className="hidden mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="text-2xl">
                      {VEHICLE_TYPES.find(v => v.id === expeditionData.vehicle_type_id)?.icon || '🚚'}
                    </div>
                    <div>
                      <h4 className="font-medium text-green-900 dark:text-green-100">
                        Véhicule sélectionné automatiquement
                      </h4>
                      <p className="text-sm text-green-700 dark:text-green-300">
                        {VEHICLE_TYPES.find(v => v.id === expeditionData.vehicle_type_id)?.name || 'Véhicule automatique'}
                      </p>
                      <p className="text-xs text-green-600 dark:text-green-400">
                        Sélectionné selon le poids total de vos articles
                      </p>
                    </div>
                  </div>
                </div>

                {/* 📋 RÉCAPITULATIF DES CHOIX DE LIVRAISON */}
                <div className="hidden mb-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg shadow-md">
                  <label className="block text-sm font-medium text-white dark:text-white mb-3">
                    📋 Récapitulatif des choix de livraison
                  </label>
                  
                  <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg">
                    <div className="space-y-3">
                      {/* Type de colis */}
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-blue-900 dark:text-blue-100">Type de colis:</span>
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-blue-700 dark:text-blue-300">
                            {(() => {
                              
                              const types = [
                                { value: 'standard', label: 'Standard', variant: 'default', emoji: '📦' },
                                { value: 'froid', label: 'Froid', variant: 'info', emoji: '❄️' },
                                { value: 'securise', label: 'Sécurisé', variant: 'error', emoji: '🔒' },
                                { value: 'gros_volume', label: 'Gros volume', variant: 'success', emoji: '📏' },
                                { value: 'doc_admin', label: 'Doc administratif', variant: 'info', emoji: '📋' }
                              ];
                              const selectedType = types.find(t => t.value === expeditionData.packageType);
                              return selectedType ? selectedType.emoji : '📦';
                            })()}
                          </span>
                          <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
                            {(() => {
                              const types = [
                                { value: 'standard', label: 'Standard' },
                                { value: 'froid', label: 'Froid' },
                                { value: 'securise', label: 'Sécurisé' },
                                { value: 'gros_volume', label: 'Gros volume' },
                                { value: 'doc_admin', label: 'Doc administratif' }
                              ];
                              const selectedType = types.find(t => t.value === expeditionData.packageType);
                              return selectedType ? selectedType.label : 'Non défini';
                            })()}
                          </span>
                        </div>
                      </div>

                      {/* Mode d'expédition */}
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-blue-900 dark:text-blue-100">Mode d'expédition:</span>
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-blue-700 dark:text-blue-300">
                            {expeditionData.shippingMode === 'relay_point' ? '🏪' : '🚚'}
                          </span>
                          <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
                            {expeditionData.shippingMode === 'relay_point' ? 'Dépôt en point relais' : 'Livraison à domicile'}
                          </span>
                        </div>
                      </div>

                      {/* Transporteur */}
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-blue-900 dark:text-blue-100">Transporteur:</span>
                        <div className="text-right">
                          {expeditionData.selectedCarrier ? (
                            <div className="text-sm text-blue-800 dark:text-blue-200">
                              {carriers.find(c => c.id && c.id.toString() === expeditionData.selectedCarrier)?.nom || 'Transporteur sélectionné'}
                            </div>
                          ) : (
                            <span className="text-sm text-orange-600 dark:text-orange-400">À sélectionner</span>
                          )}
                        </div>
                      </div>

                      {/* Adresse de livraison */}
                      <div className="flex items-start justify-between">
                        <span className="text-sm font-medium text-blue-900 dark:text-blue-100">Adresse de livraison:</span>
                        <div className="text-right max-w-xs">
                          {expeditionData.to_address ? (
                            <div className="text-sm text-blue-800 dark:text-blue-200">
                              {expeditionData.to_address}
                              {expeditionData.to_latitude && expeditionData.to_longitude && (
                                <div className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                                  📍 {expeditionData.to_latitude}, {expeditionData.to_longitude}
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-sm text-orange-600 dark:text-orange-400">À saisir</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>


                {/* 🤝 NOUVEAU : Section Service de manutention et Assurance du colis sur la même ligne */}
                <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* 🤝 Service de manutention */}
                  <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg shadow-md">
                    <label className="block text-sm font-medium text-white dark:text-white mb-3">
                      🤝 Service d'aide à la manutention
                    </label>
                    
                    {/* Checkbox de manutention */}
                    <div className="mb-4">
                      <label className="flex items-center space-x-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={expeditionData.isManutentionRequested}
                          onChange={(e) => updateExpeditionData('isManutentionRequested', e.target.checked)}
                          className="w-4 h-4 text-ksl-red bg-gray-100 border-gray-300 rounded focus:ring-ksl-red focus:ring-2"
                        />
                        <span className="text-sm text-gray-900 dark:text-white">
                          Je souhaite une aide à la manutention
                        </span>
                      </label>
                      <p className="text-xs text-gray-500 dark:text-gray-400 ml-7 mt-1">
                        Service d'aide pour le chargement/déchargement des colis lourds ou volumineux (tarif basé sur le poids et volume)
                      </p>
                    </div>
                  </div>

                  {/* 🛡️ Assurance du colis */}
                  <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg shadow-md">
                    <label className="block text-sm font-medium text-white dark:text-white mb-3">
                      🛡️ Assurance du colis
                    </label>
                    
                    {/* Checkbox d'assurance */}
                    <div className="mb-4">
                      <label className="flex items-center space-x-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={expeditionData.isInsured}
                          onChange={(e) => updateExpeditionData('isInsured', e.target.checked)}
                          disabled={isInsuranceMandatory(expeditionData.packageType)}
                          className={`w-4 h-4 text-ksl-red bg-gray-100 border-gray-300 rounded focus:ring-ksl-red focus:ring-2 ${
                            isInsuranceMandatory(expeditionData.packageType) ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                        />
                        <span className="text-sm text-gray-900 dark:text-white">
                          {isInsuranceMandatory(expeditionData.packageType) 
                            ? 'Assurance obligatoire pour ce type de colis'
                            : 'Je souhaite assurer mon colis'
                          }
                        </span>
                      </label>
                      <p className="text-xs text-gray-500 dark:text-gray-400 ml-7 mt-1">
                        {isInsuranceMandatory(expeditionData.packageType) 
                          ? 'L\'assurance est automatiquement appliquée pour les colis sécurisés et gros volume'
                          : 'L\'assurance couvre la perte ou les dommages du colis'
                        }
                      </p>
                    </div>
                    
                    {/* Input valeur déclarée (affiché seulement si assurance cochée) */}
                    {expeditionData.isInsured && (
                      <div className="mb-4">
                        <Input
                          label="Valeur déclarée du colis (FCFA) *"
                          type="number"
                          min="1"
                          value={expeditionData.declared_value}
                          onChange={(e) => updateExpeditionData('declared_value', e.target.value)}
                          placeholder="Ex: 50000"
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          💡 Frais d'assurance: {expeditionData.declared_value ? 
                            `${formatPrice(Math.max(parseFloat(expeditionData.declared_value) * 0.02, 0))}` : 
                            '0 FCFA'
                          }
                        </p>
                      </div>
                    )}
                    
                    {/* Information sur l'assurance */}
                    <Alert variant="info" className="hidden mt-3">
                      <Info className="w-4 h-4" />
                    <div className="text-sm">
                      <p className="font-medium mb-1">ℹ️ À propos de l'assurance:</p>
                      <ul className="list-disc list-inside space-y-1 text-xs">
                        <li>Taux d'assurance: 2% de la valeur déclarée</li>
                        <li>Couvre la perte totale ou les dommages du colis</li>
                        <li>Remboursement jusqu'à la valeur déclarée</li>
                        <li>Obligatoire pour les colis de valeur supérieure à 100 000 FCFA</li>
                      </ul>
                    </div>
                  </Alert>
                  </div>
                </div>

                {/* Navigation */}
                <div className="flex justify-between pt-6">
                  <Button
                    variant="outline"
                    onClick={handlePrevStep}
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Précédent
                  </Button>
                  <Button
                    onClick={handleNextStep}
                  >
                    Suivant
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            )}

            {/* Étape 3: Devis */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <div className="flex items-center space-x-3">
                  <Calculator className="w-6 h-6 text-ksl-red" />
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                    Calculer le devis
                  </h2>
                </div>

                <div className="text-center py-8">
                  <Button
                    onClick={handleEstimation}
                    disabled={isCalculatingQuote || (expeditionData.shippingMode === 'relay_point' && filteredRelayPoints.length === 0)}
                    size="lg"
                  >
                    {isCalculatingQuote ? (
                      <>
                        <Loader className="w-4 h-4 mr-2 animate-spin" />
                        Calcul en cours...
                      </>
                    ) : (
                      <>
                        <Calculator className="w-4 h-4 mr-2" />
                        Calculer le devis
                      </>
                    )}
                  </Button>
                </div>

                {/* Navigation */}
                <div className="flex justify-between pt-6">
                  <Button
                    variant="outline"
                    onClick={handlePrevStep}
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Précédent
                  </Button>
                  <Button
                    onClick={handleNextStep}
                  >
                    Suivant
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            )}

            {/* Étape 4: Paiement (placée en face du récapitulatif, dans la colonne principale) */}
            {currentStep === 4 && (
              <div className="space-y-6 mt-6">
                {(paymentStep === 'idle' || paymentStep === 'error') && (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <CreditCard className="w-6 h-6 text-ksl-red" />
                      <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                        Choisir le mode de paiement
                      </h2>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button onClick={loadPaymentMethods} variant="outline" size="sm" disabled={isLoadingPaymentMethods}>
                        <RefreshCw className={cn('w-4 h-4', isLoadingPaymentMethods && 'animate-spin')} />
                        Actualiser
                      </Button>
                    </div>
                  </div>
                )}

                {/* Résumé du paiement */}
                <div className="p-4 rounded-lg border bg-gray-50 dark:bg-dark-bg-secondary/40">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Montant à payer</p>
                      <p className="text-lg font-semibold text-gray-900 dark:text-white">
                        {formatPrice(Math.max(parseFloat(quoteData?.finalPrice || 0), 1))}
                      </p>
                    </div>
                    <div className="flex items-center space-x-3">
                      {selectedPaymentMethod ? (
                        <>
                          <img 
                            src={selectedPaymentMethod.icone} 
                            alt={selectedPaymentMethod.nom} 
                            className="w-8 h-8 object-contain" 
                            onError={(e) => { e.target.src = '/OM.png'; }} 
                          />
                          <div className="text-right">
                            <p className="text-sm text-gray-600 dark:text-gray-400">Méthode</p>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">{selectedPaymentMethod.nom}</p>
                          </div>
                        </>
                      ) : (
                        <p className="text-sm text-gray-500">Aucune méthode sélectionnée</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Sélection du mode de paiement */}
                {(paymentStep === 'idle' || paymentStep === 'error') && (
                <div className="space-y-4">
                  <div className="hidden space-y-2">
                    <h4 className="font-medium text-gray-900 dark:text-white">Sélectionnez votre mode de paiement</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Les méthodes disponibles sont chargées dynamiquement selon votre pays détecté automatiquement via IP</p>
                  </div>

                  {isLoadingPaymentMethods ? (
                    <div className="flex items-center justify-center p-8">
                      <Loader className="w-6 h-6 animate-spin text-ksl-red" />
                      <span className="ml-2">Chargement des modes de paiement...</span>
                    </div>
                  ) : paymentMethodsError ? (
                    <Alert variant="error">
                      <AlertCircle className="w-4 h-4" />
                      {paymentMethodsError}
                    </Alert>
                  ) : paymentMethods.length === 0 ? (
                    <Alert variant="warning">
                      <AlertCircle className="w-4 h-4" />
                      Aucun mode de paiement disponible
                    </Alert>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {paymentMethods.map((method) => (
                        <div
                          key={method.id}
                          className={cn(
                            'p-4 border-2 rounded-lg cursor-pointer transition-all',
                            selectedPaymentMethod?.id === method.id
                              ? 'border-ksl-red bg-red-50 dark:bg-red-900/20'
                              : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                          )}
                          onClick={() => setSelectedPaymentMethod(method)}
                        >
                          <div className="flex items-center space-x-3">
                            <div className={cn(
                              'w-4 h-4 rounded-full border-2',
                              selectedPaymentMethod?.id === method.id ? 'border-ksl-red bg-ksl-red' : 'border-gray-300 dark:border-gray-600'
                            )} />
                            <div className="flex-1">
                              <div className="flex items-center space-x-2">
                                <img src={method.icone} alt={method.nom} className="w-8 h-8 object-contain" onError={(e) => { e.target.src = '/OM.png'; }} />
                                <span className="font-medium text-gray-900 dark:text-white">{method.nom}</span>
                                {method.clapayData?.merchant && (
                                  <Badge variant="secondary" className="text-xs">{method.clapayData.merchant}</Badge>
                                )}
                              </div>
                              {method.description && (
                                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{method.description}</p>
                              )}
                              {method.clapayData && (
                                <div className="mt-2">
                                  <div className="flex items-center justify-between text-xs">
                                    <span className="text-gray-500 dark:text-gray-400">Devise:</span>
                                    <span className="font-medium text-gray-900 dark:text-white">{method.clapayData.currency}</span>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                )}

                {/* 📱 Champ numéro de paiement */}
                {(paymentStep === 'idle' || paymentStep === 'error') && (
                <div className="space-y-3">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">📱 Numéro de téléphone pour le paiement</label>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Saisissez le numéro de téléphone associé à votre compte de paiement mobile</p>
                  </div>
                  <div className="flex space-x-3">
                    <div className="w-24">
                      <select
                        value={paymentCountryCode}
                        onChange={(e) => setPaymentCountryCode(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-white focus:ring-2 focus:ring-ksl-red focus:border-transparent"
                      >
                        <option value="+225">🇨🇮 +225</option>
                        <option value="+237">🇨🇲 +237</option>
                        <option value="+226">🇧🇫 +226</option>
                        <option value="+224">🇬🇳 +224</option>
                        <option value="+241">🇬🇦 +241</option>
                        <option value="+223">🇲🇱 +223</option>
                        <option value="+221">🇸🇳 +221</option>
                        <option value="+229">🇧🇯 +229</option>
                      </select>
                    </div>
                    <div className="flex-1">
                      <Input
                        type="tel"
                        placeholder="Numéro de téléphone"
                        value={paymentPhoneNumber}
                        onChange={(e) => setPaymentPhoneNumber(e.target.value)}
                        className="w-full"
                        required
                      />
                    </div>
                  </div>
                  {paymentPhoneNumber && (
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      Numéro complet : <span className="font-medium text-ksl-red">{paymentCountryCode}{paymentPhoneNumber}</span>
                    </div>
                  )}
                </div>
                )}

                {/* Champs invités si non authentifié */}
                {!isAuthenticated && (paymentStep === 'idle' || paymentStep === 'error') && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Prénom</label>
                      <Input value={guestFirstName} onChange={(e) => setGuestFirstName(e.target.value)} placeholder="Votre prénom" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Nom</label>
                      <Input value={guestLastName} onChange={(e) => setGuestLastName(e.target.value)} placeholder="Votre nom" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Email</label>
                      <Input type="email" value={guestEmail} onChange={(e) => setGuestEmail(e.target.value)} placeholder="email@exemple.com" />
                    </div>
                  </div>
                )}

                {/* 🚀 Indicateur de statut du paiement */}
                {paymentStep !== 'idle' && (
                  <div className="mt-4 p-4 rounded-lg border">
                    {paymentStep === 'initializing' && (
                      <div className="flex items-center space-x-3 text-blue-600 dark:text-blue-400">
                        <Loader className="w-5 h-5 animate-spin" />
                        <span className="font-medium">Initialisation du paiement en cours...</span>
                      </div>
                    )}
                    {paymentStep === 'pending' && (
                      <div className="flex items-center space-x-3 text-blue-600 dark:text-blue-400">
                        <Loader className="w-5 h-5 animate-spin" />
                        <span className="font-medium">Paiement en cours de traitement...</span>
                      </div>
                    )}
                    {(paymentStep === 'initializing' || paymentStep === 'pending') && (
                      <div className="mt-4 flex flex-col sm:flex-row gap-3">
                        <Button 
                          onClick={() => {
                            // Rouvrir la page de paiement si disponible
                            try {
                              const last = window.localStorage.getItem('ksl_last_payment_url');
                              if (last) window.open(last, '_blank');
                            } catch {}
                          }}
                          variant="outline"
                          className="w-full sm:w-auto"
                        >
                          <ExternalLink className="w-4 h-4 mr-2" />
                          Rouvrir la page de paiement
                        </Button>
                        <Button 
                          variant="outline"
                          onClick={() => setPaymentStep('idle')}
                          className="w-full sm:w-auto"
                        >
                          Revenir aux méthodes de paiement
                        </Button>
                      </div>
                    )}
                    {paymentStep === 'success' && (
                      <div className="flex items-center space-x-3 text-green-600 dark:text-green-400">
                        <CheckCircle className="w-5 h-5" />
                        <span className="font-medium">Paiement initialisé avec succès !</span>
                      </div>
                    )}
                    {paymentStep === 'error' && (
                      <div className="flex items-center space-x-3 text-red-600 dark:text-red-400">
                        <AlertCircle className="w-5 h-5" />
                        <span className="font-medium">Erreur lors de l'initialisation du paiement</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Navigation paiement */}
                <div className="flex flex-col sm:flex-row justify-between gap-3">
                  <Button
                    variant="outline"
                    onClick={handlePrevStep}
                    className="hidden w-full sm:w-auto"
                  >
                    <ArrowLeft className=" w-4 h-4 mr-2" />
                    Précédent
                  </Button>
                  {paymentStep === 'idle' || paymentStep === 'error' ? (
                    <Button
                      onClick={handlePaymentValidation}
                      className="w-full sm:w-auto"
                      size="lg"
                      disabled={!selectedPaymentMethod || !paymentPhoneNumber}
                    >
                      <CreditCard className="w-5 h-5 mr-2" />
                      Initialiser le paiement
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      onClick={() => setPaymentStep('idle')}
                      className="hidden w-full sm:w-auto"
                    >
                      Revenir aux méthodes de paiement
                    </Button>
                  )}
                </div>
              </div>
            )}
          </Card>
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-1">
          <Card className="p-4 sm:p-6 shadow-lg hover:shadow-xl transition-all duration-300">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4 text-sm sm:text-base">
              Récapitulatif
            </h3>
            
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Étape actuelle</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {steps.find(s => s.id === currentStep)?.title}
                </p>
              </div>
              
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Articles</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {expeditionData.pickup_items.length} article(s)
                </p>
              </div>
              
              {quoteData && (
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Prix estimé</p>
                  <p className="font-medium text-ksl-red text-lg">
                    {formatPrice(quoteData.finalPrice)}
                  </p>
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>

      {/* Modal de devis amélioré */}
      {showQuoteModal && quoteData && (
        <Modal
          isOpen={showQuoteModal}
          onClose={() => setShowQuoteModal(false)}
          title=""
        >
          <div className="space-y-6">
            {/* Header avec icône et titre */}
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Calculator className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                Devis calculé avec succès !
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Votre estimation est prête
              </p>
            </div>

            {/* Prix principal avec animation */}
            <div className="text-center bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/10 dark:to-blue-900/10 rounded-xl p-6 border border-green-200 dark:border-green-800">
              <div className="animate-bounce mb-2">
                <span className="text-4xl font-bold text-green-600 dark:text-green-400">
                  {formatPrice(finalPrice || quoteData.finalPrice)}
                </span>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 font-medium">
                {promoCodeData ? 'Prix final avec réduction' : 'Prix final estimé'}
              </p>
            </div>

            {/* Trajet affiché avec les adresses exactes saisies */}
            <div className="flex justify-between items-center p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
              <span className="text-sm text-gray-600 dark:text-gray-300">Trajet</span>
              <span className="font-semibold text-gray-900 dark:text-white text-sm text-right">
                {expeditionData.from_address} → {expeditionData.to_address}
              </span>
            </div>

            {/* Détails du calcul */}
            <div className="hidden bg-gray-50 dark:bg-gray-800 rounded-xl p-4">
              <h4 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                <Calculator className="w-4 h-4 mr-2 text-blue-600" />
                Détails du calcul
              </h4>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-700">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Prix de base</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {formatPrice(quoteData.breakdown?.basePrice || 0)}
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-700">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Tarif poids</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {formatPrice(quoteData.breakdown?.weightTariff || 0)}
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-700">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Tarif volume</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {formatPrice(quoteData.breakdown?.volumeTariff || 0)}
                  </span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Tarif distance</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {formatPrice(quoteData.breakdown?.distanceTariff || 0)}
                  </span>
                </div>
              </div>
            </div>

            {/* 🎁 Section Code Promo */}
            <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
              <h4 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
                <Gift className="w-4 h-4 mr-2 text-purple-600" />
                Code Promo (Optionnel)
              </h4>
              
              <div className="space-y-3">
                <div className="flex gap-2">
                  <Input
                    type="text"
                    placeholder="Entrez votre code promo"
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value)}
                    className="flex-1"
                    disabled={isValidatingPromo}
                  />
                  <Button
                    onClick={() => verifyPromoCode(promoCode)}
                    disabled={!promoCode.trim() || isValidatingPromo}
                    variant="outline"
                    size="sm"
                  >
                    {isValidatingPromo ? (
                      <Loader className="w-4 h-4 animate-spin" />
                    ) : (
                      'Appliquer'
                    )}
                  </Button>
                </div>

                {/* Affichage des erreurs */}
                {promoError && (
                  <Alert variant="error" className="text-sm">
                    {promoError}
                  </Alert>
                )}

                {/* Affichage du code promo appliqué */}
                {promoCodeData && (
                  <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3 border border-green-200 dark:border-green-800">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                        <span className="text-sm font-medium text-green-900 dark:text-green-200">
                          Code promo appliqué : {promoCodeData.promo_code}
                        </span>
                      </div>
                      <Button
                        onClick={removePromoCode}
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700"
                      >
                        Supprimer
                      </Button>
                    </div>
                  </div>
                )}

                {/* Détail des prix avec réduction */}
                {(promoCodeData || discountAmount > 0) && (
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Prix original :</span>
                      <span className="font-medium">{formatPrice(originalPrice)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Réduction :</span>
                      <span className="font-medium text-green-600 dark:text-green-400">
                        -{formatPrice(discountAmount)}
                      </span>
                    </div>
                    <div className="flex justify-between border-t border-gray-200 dark:border-gray-600 pt-2">
                      <span className="font-semibold text-gray-900 dark:text-white">Prix final :</span>
                      <span className="font-bold text-green-600 dark:text-green-400">
                        {formatPrice(finalPrice)}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Informations supplémentaires */}
            <div className="hidden bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 border border-blue-200 dark:border-blue-800">
              <div className="flex items-start space-x-3">
                <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-blue-800 dark:text-blue-200">
                  <p className="font-medium mb-1">ℹ️ Informations importantes :</p>
                  <ul className="space-y-1 text-xs">
                    <li>• Ce prix est une estimation basée sur les informations fournies</li>
                    <li>• Le prix final peut varier selon les conditions de livraison</li>
                    {expeditionData.isInsured ? (
                      <li>• Les frais d'assurance sont inclus dans cette estimation</li>
                    ) : (
                      <li>• Les frais d'assurance ne sont pas inclus dans cette estimation</li>
                    )}
                  </ul>
                </div>
              </div>
            </div>

            {/* Boutons d'action */}
            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setShowQuoteModal(false)}
                className="flex-1 sm:flex-none"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Modifier
              </Button>
              <Button
                onClick={() => {
                  setShowQuoteModal(false);
                  handleNextStep();
                }}
                className="flex-1 sm:flex-none bg-green-600 hover:bg-green-700"
              >
                <ArrowRight className="w-4 h-4 mr-2" />
                Continuer
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Modal de redirection/état de paiement */}
      <PaymentRedirect
        isOpen={showPaymentRedirect}
        onClose={() => setShowPaymentRedirect(false)}
        paymentStatus={paymentFinalStatus || (paymentStep === 'success' ? 'success' : paymentStep === 'initializing' ? 'pending' : 'failed')}
        expeditionData={paymentCreatedExpedition}
        onNewExpedition={() => {
          setShowPaymentRedirect(false);
          setCurrentStep(1);
        }}
        onViewExpeditions={() => {
          setShowPaymentRedirect(false);
          window.location.href = '/';
        }}
        onGoToDashboard={() => {
          setShowPaymentRedirect(false);
          window.location.href = '/';
        }}
      />

      {/* Modal d'erreur de configuration */}
      <ErrorModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        title={errorModalData.title}
        message={errorModalData.message}
        type={errorModalData.type}
      />

      </div>
    </div>
  );
};

export default Expedier; 